// First: what older phones lack (structuredClone, globalThis) before any module uses it.
import './polyfills.js';
import {bindHubMedia} from './hub-media.js';
import { SurfacePreviewController } from './surface-preview.js';
import { templateSwitchFor } from './template-switch.js';
import { carryAnswers, matchOptions, rekeyByOption } from './carry-answers.js';
import { Api } from './api.js';
import { CanvasEditor } from './canvas-edit.js';
import { interruptedPick } from './photo-intake.js';
import {
  CHOICE_TEXTS,
  MAP_TEXTS,
  OptionForm,
  SONG_TEXTS,
  songLabel,
  TEXT_FIELD_TEXTS,
  UPLOAD_TEXTS,
  type PhotoState,
} from './form.js';
import { createGuide, resolveGuideSettings } from './guide.js';
import type { GuideApi, GuideStorage } from './guide-types.js';
import { createFocusSpot } from './focus-spot.js';
import type { FocusSpot } from './focus-spot.js';
import { Preview, applyValues, uploadTargetsOf } from './preview.js';
import { CartPrep, type CartPrepDesign } from './cart-prep.js';
import { addToCart, announceAdded, formPairs, newBatchId, setHidden, showAddedSheet, type CartAddConfig } from './cart-add.js';
import { FINISH_TEXTS, createFinishPanel, type FinishPanel, type PieceRun } from './finish.js';
import { createVariantPanel, type VariantPanel } from './variant-step.js';
import { backdropValues, mockupZoneOf } from './design-backdrop.js';
import { mountPageOptions, pageOptionsAvailable } from './page-options.js';
import { addLibraryEntry, readLibrary, removeLibraryEntry, writeLibrary, LIBRARY_MAX_THUMB } from './library.js';
import type { DesignerProductBackdrop } from './designer-types.js';
import { previewFrame } from './preview-layout.js';
import { Cutout, largest } from './cutout.js';
import type { HeadLanding } from './head-reveal.js';
import { pickFace, cropForFace } from './facepick.js';
import { FLOOR_PPI, PHOTO_TEXTS, headPrintPpi } from './photoquality.js';
import { STYLES } from './styles.js';
import {
  bottomBarOffset,
  canHover,
  createLayer,
  fixedRoot,
  isInAppBrowser,
  observeSize,
  revealFocused,
  showToast,
  stickyIsBroken,
  stickyTopOffset,
  type LayerHandle,
  type ToastHandle,
} from './layers.js';
import { ICONS, iconEl } from './icons.js';
import { hasSound } from './audio-control.js';
import { clock } from './youtube-clip.js';
import { dealStep, priceTotals, readPagePrice, readQuantity, renderPriceSummary } from './price-summary.js';
import { youtubeName } from './cart-words.js';
import { AiPhotoDesigns, aiPhotoKey, type AiPhotoContext, type AiPhotoState } from './ai-photo-design.js';
import { restoredSoundOption } from './restore-sounds.js';
import { aiStyleFields, aiStyleOf, aiStyleSpec, fillAiStylePrompt, cleanAiStyleWords, sceneWords, PLACEMENT_KEY, PHOTO_COLLAGE_OPTION_TYPE, collageRulesOf, fromAttributeOf, coverPhotoFrame, cropForHead, describeDesign, formatDate, resolveHeadFit, isDesignAreaAnswer, photoWindow, type HeadFit, type HeadMeasure, type ImageCrop, type ImageElement } from '@pz/scene';
import { collageSlotAtPoint } from './collage/host.js';
import { COLLAGE_TEXTS, collageSheetLabel } from './collage/texts.js';
import type { AudioSelection, OptionDef, ProductDefinition, WidgetConfig, Values } from './types.js';
import { savedWizardSteps, wizardStepsKey, type WizardStep } from './wizard-steps.js';

/**
 * The storefront widget.
 *
 * Boots on a WooCommerce product page, draws the personalization form and a live
 * preview, and — when the shopper adds to cart — commits the personalization and
 * hands the resulting id to WooCommerce through hidden inputs.
 *
 * Two presentations, chosen per product in the studio:
 *   inline — form and preview live directly on the product page;
 *   wizard — a button opens a step-by-step popup, one question at a time, which
 *            suits products with many options and keeps the page itself calm.
 *
 * Two rules shape everything here. The shopper must never be stuck: if the designer
 * cannot load, the add-to-cart button is released rather than left disabled forever.
 * And the preview must not lie: it draws with the same engine that produces the
 * print file wherever the device can run it.
 */

/**
 * A fresh key for one form session.
 *
 * `crypto.randomUUID` is only defined in a secure context, and shops do exist on
 * plain HTTP; a random fallback keeps the retry protection working there rather
 * than throwing on the way to the cart.
 */
/** What a commit answers: the personalization id, its price and extras. */
type CommitOutcome = Awaited<ReturnType<Api['commit']>>;

function newCommitKey(): string {
  const uuid = globalThis.crypto?.randomUUID;
  if (typeof uuid === 'function') return globalThis.crypto.randomUUID();
  return `pz-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * What the browser remembers about the shopper guide — only whether the shopper
 * hid the bear.
 *
 * Both ends swallow: reading `localStorage` throws outright in a private window
 * on older Safari and wherever the shop is embedded in a third-party frame with
 * storage blocked. A guide is not worth an exception on the way to the cart, so
 * a browser that will not remember simply forgets the choice after this visit.
 */
function guideStorage(): GuideStorage {
  return {
    get(key: string): string | null {
      try {
        return window.localStorage?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    set(key: string, value: string): void {
      try {
        window.localStorage?.setItem(key, value);
      } catch {
        /* the choice holds for this visit only */
      }
    },
  };
}

/**
 * The guide, wrapped so nothing it does can cost a sale.
 *
 * Every call into it is a courtesy — a sentence in a bubble, a spotlight, a bear
 * walking to the field the shopper is in. If one throws (a node left behind by a
 * product swap, a layout read on a detached canvas), the shopper must still be
 * able to finish and buy; so the failure is logged and the widget carries on as
 * if the guide had said nothing.
 */
function guarded(guide: GuideApi): GuideApi {
  const run = (name: string, call: () => void): void => {
    try {
      call();
    } catch (err) {
      console.warn(`[personalizy] guide.${name} failed`, err);
    }
  };
  return {
    mount: () => run('mount', () => guide.mount()),
    destroy: () => run('destroy', () => guide.destroy()),
    get enabled(): boolean {
      try {
        return guide.enabled;
      } catch {
        return false;
      }
    },
    focusOption: (id) => run('focusOption', () => guide.focusOption(id)),
    valuesChanged: (values) => run('valuesChanged', () => guide.valuesChanged(values)),
    photoState: (id, state) => run('photoState', () => guide.photoState(id, state)),
    step: (info) => run('step', () => guide.step(info)),
    missing: (ids) => run('missing', () => guide.missing(ids)),
    done: () => run('done', () => guide.done()),
    relayout: () => run('relayout', () => guide.relayout()),
  };
}

/** Questions answered with the shopper's own picture — the ones framed on the preview. */
const PHOTO_OPTION_TYPES = new Set(['image_upload', 'vector_upload', 'ai_image']);

/**
 * The new flow keeps the date, the promise, the quantity and the cart in the
 * personalizer; the page's own copies stay in the form (they are what is
 * posted) but out of sight.
 */
function hidePageCartChrome(form: HTMLFormElement | null, button: HTMLElement | null): void {
  if (typeof document === 'undefined') return;
  document.body?.classList.add('pz-flow');
  form?.classList.add('pz-flow-form');
  button?.classList.add('pz-flow-hidden');
  if (document.getElementById('pz-flow-style')) return;
  const style = document.createElement('style');
  style.id = 'pz-flow-style';
  style.textContent =
    // WooCommerce's own "Pogledaj korpu" link lands after the hidden button on
    // `added_to_cart`; the widget's own sheet says that.
    '.pz-flow-form .quantity,.pz-flow-hidden,.pz-flow-form .added_to_cart,' +
    'body.pz-flow .personalizy-delivery,body.pz-flow .wc-eta-rs,body.pz-flow .wc-eta-rs-timeline,' +
    'body.pz-flow .wc-eta-rs-content[data-wapf="direct"],' +
    '.pz-flow-variants table.variations,.pz-flow-variants .reset_variations,' +
    '.pz-flow-variants .single_variation_wrap .woocommerce-variation{display:none !important}';
  document.head?.appendChild(style);
}

/** A run of different pieces survives a reload for this product, in this tab (sessionStorage). */
function pieceRunKey(productId: number | string): string {
  return `pz-pieces:${productId}`;
}

function readPieceRun(productId: number | string): PieceRun | undefined {
  try {
    const raw = window.sessionStorage?.getItem(pieceRunKey(productId));
    const run = raw ? (JSON.parse(raw) as Partial<PieceRun>) : undefined;
    const total = Number(run?.total);
    const done = Number(run?.done);
    return Number.isInteger(total) && Number.isInteger(done) && total >= 2 && total <= 50 && done >= 1 && done < total ? { total, done } : undefined;
  } catch {
    return undefined;
  }
}

function writePieceRun(productId: number | string, run: PieceRun | undefined): void {
  try {
    if (run && run.done >= 1) window.sessionStorage?.setItem(pieceRunKey(productId), JSON.stringify(run));
    else window.sessionStorage?.removeItem(pieceRunKey(productId));
  } catch {
    /* private mode: the run just does not survive a reload */
  }
}

/** WooCommerce's quantity input set the way its own scripts and ours read it. */
function setQuantityInput(form: HTMLFormElement | null, n: number): void {
  const input = form?.querySelector<HTMLInputElement>('input[name="quantity"]');
  if (!input) return;
  input.value = String(n);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

/** The hidden inputs that carry one committed design into the add-to-cart POST. */
const PERSONALIZATION_INPUTS = ['personalizy_personalization_id', 'personalizy_price_delta', 'personalizy_extra_products', 'personalizy_properties'];

const TEXTS = {
  loading: 'Učitavanje…',
  failed: 'Personalizacija trenutno nije dostupna.',
  fillIn: 'Popuni obavezna polja: ',
  addToCart: 'Dodaj u korpu',
  addPiece: (k: number, n: number): string => `Dodaj komad ${k} od ${n}`,
  pieceAdded: (k: number, n: number): string => `Komad ${k} od ${n} je u korpi. Sada komad ${k + 1}: promeni sliku ili tekst, pa „Dodaj komad ${k + 1} od ${n}“.`,
  pieceHint: 'svaki komad drugačiji',
  variationNeeded: (what: string): string => (what ? `Izaberi „${what}“ pa dodaj u korpu.` : 'Izaberi opciju proizvoda pa dodaj u korpu.'),
  variationUnavailable: 'Ova opcija proizvoda trenutno nije dostupna. Izaberi drugu.',
  peekTitle: 'Tvoj dizajn',
  peekHint: 'Proveri ga još jednom pre korpe',
  peekOpen: 'Uvećaj',
  peekClose: 'Smanji',
  fillThis: 'Popuni ovo polje da nastaviš.',
  stillSending: 'Slike se još šalju — slobodno nastavi, sačekaćemo ih pre dodavanja u korpu.',
  saving: 'Čuvanje…',
  /** Shown only when the click itself has work to do and it takes a moment. */
  adding: 'Dodaje se u korpu…',
  offline: 'Nema veze sa internetom, proizvod nije dodat. Tvoj dizajn je sačuvan — proveri vezu i pokušaj ponovo.',
  previewApprox: 'Pregled dizajna — približno',
  previewExact: 'Pregled dizajna',
  personalize: 'Personalizuj',
  edit: 'Izmeni personalizaciju',
  /** The focus chip's leading word: "Uređuješ: <label>". */
  editing: 'Uređuješ',
  /** The same chip on a question nobody has answered yet: "Dodaj: <label>". */
  add: 'Dodaj',
  step: (n: number, total: number) => `Korak ${n} od ${total}`,
  stepsLabel: 'Koraci',
  stepDone: 'gotovo',
  stepMissing: 'nedostaje odgovor',
  back: 'Nazad',
  next: 'Dalje',
  done: 'Sačuvaj',
  totalLabel: 'Personalizacija',
  grandTotal: 'Ukupno',
  summaryTitle: 'Tvoja personalizacija',
  testSaved: 'Probna personalizacija je sačuvana — fajlovi se pripremaju.',
  /** The heading over the inline form. */
  formTitle: 'Personalizuj proizvod',
  /** The launch button once some, but not all, required answers exist. */
  resume: 'Nastavi personalizaciju',
  /** The progress meter once every required answer is in. */
  ready: 'Spremno',
  livePreview: 'Pregled uživo',
  /** Said once the popup's last step is saved. */
  saved: 'Personalizacija je sačuvana.',
  startOver: 'Počni ispočetka',
  /** Editing a cart line: the button, the note at the top, the way back. */
  saveEdit: 'Sačuvaj izmene',
  editNote: 'Menjaš personalizaciju iz korpe.',
  editHint: 'Kad završiš, klikni „Sačuvaj izmene” — proizvod u korpi će biti ažuriran.',
  backToCart: 'Nazad u korpu',
  unchanged: 'Nema izmena — vraćamo te u korpu.',
  /** The optional proof line, on a product that shows it (`display.proofCheck`). Never blocks the cart. */
  proofLabel: 'Proverio/la sam tekst, slike i pesmu — ovako želim da bude odštampano.',
  requiredProgress: (done: number, total: number) => `${done}/${total} obavezno`,
  missingLeft: (n: number) => `Nedostaje ${n} ${plural(n, 'obavezno polje', 'obavezna polja', 'obaveznih polja')}`,
  stepCount: (n: number) => `${n} ${plural(n, 'korak', 'koraka', 'koraka')}`,
};

/** Serbian plural: 1 / 2–4 / 5+ (with 11–14 taking the "many" form). */
function plural(n: number, one: string, few: string, many: string): string {
  const tens = n % 100;
  const units = n % 10;
  if (units === 1 && tens !== 11) return one;
  if (units >= 2 && units <= 4 && (tens < 12 || tens > 14)) return few;
  return many;
}

/**
 * Overrides the widget's words with the product's own (Settings → Connect
 * product → Labels). Only string texts can be replaced; the step counter
 * keeps its shape. An empty override means "keep the built-in".
 *
 * A bare key (`personalize`) is one of the widget's own texts; a prefixed one
 * reaches a control's words: `song.*`, `map.*`, `upload.*`, `choice.*`,
 * `text.*` (e.g. `upload.imageButton`, `map.cancel`, `song.noSound`). The designer's
 * `designer.*` and `designArea.*` keys are read where those controls are built.
 */
const LABEL_GROUPS: Record<string, Record<string, unknown>> = {
  song: SONG_TEXTS as unknown as Record<string, unknown>,
  map: MAP_TEXTS as unknown as Record<string, unknown>,
  upload: UPLOAD_TEXTS as unknown as Record<string, unknown>,
  choice: CHOICE_TEXTS as unknown as Record<string, unknown>,
  text: TEXT_FIELD_TEXTS as unknown as Record<string, unknown>,
};
/** The built-in words, so a product without an override gets them back after a swap. */
const LABEL_DEFAULTS: Record<string, Record<string, unknown>> = Object.fromEntries(
  Object.entries({ '': TEXTS as unknown as Record<string, unknown>, ...LABEL_GROUPS })
    .map(([group, texts]) => [group, { ...texts }]),
);

function applyLabels(labels: Record<string, string> | undefined): void {
  // Start from the built-ins every time: a variation that swaps to another
  // product definition must not keep the previous one's wording.
  for (const [group, defaults] of Object.entries(LABEL_DEFAULTS)) {
    Object.assign(group ? LABEL_GROUPS[group]! : (TEXTS as unknown as Record<string, unknown>), defaults);
  }
  for (const [key, value] of Object.entries(labels ?? {})) {
    if (typeof value !== 'string' || !value.trim()) continue;
    const dot = key.indexOf('.');
    const texts = dot > 0 ? LABEL_GROUPS[key.slice(0, dot)] : (TEXTS as unknown as Record<string, unknown>);
    const field = dot > 0 ? key.slice(dot + 1) : key;
    if (texts && typeof texts[field] === 'string') texts[field] = value.trim().slice(0, 200);
  }
}

/** What the stepper calls a step: its own title, else the questions it asks. */
function stepName(step: WizardStep): string {
  const title = step.title?.trim();
  if (title) return title;
  return step.options.map((o) => o.label.trim()).filter(Boolean).join(' · ');
}

class Widget {
  private readonly api: Api;
  private readonly root: ShadowRoot;
  private form: OptionForm | undefined;
  private stopHubMedia?: () => void;
  private surfacePreview?: SurfacePreviewController;
  private preview: Preview | undefined;
  private cutout: Cutout | undefined;
  /** Upload id -> the untouched photo, before any cut-out replaced it. */
  private readonly originalPhotos = new Map<string, string>();
  /**
   * Each uploaded photo's OWN pixel size. The page draws a smaller preview
   * copy of a big photo; print-quality sums (a Mini Me head's ppi) must use the
   * original the press will get, or every big photo would read as "soft".
   */
  private readonly originalSizes = new Map<string, { w: number; h: number }>();
  /**
   * Asset id -> URL for everything a design area put on the product: the
   * shopper's own uploads, their cut-outs, and the artwork they picked out of
   * the catalogue.
   *
   * Kept separately from `originalPhotos` because it is a different question —
   * that map answers "what did this photo look like before we touched it", for
   * the face picker and "keep my background". This one answers "where are the
   * pixels", which is what a draft, a share link and a product swap all need to
   * be able to redraw a design that was made in an earlier visit.
   */
  private readonly designAssets = new Map<string, string>();
  /**
   * Asset id -> the 1024 px draw copy of every photo a collage holds: what the
   * preview renders, what a draft and a share link carry, what a product swap
   * registers again. Never cut out, styled or framed on the preview.
   */
  private readonly collageAssets = new Map<string, string>();
  /** The collage board is open: the page's own preview is not drawn meanwhile (phone memory). */
  private previewPaused = false;
  private editor: CanvasEditor | undefined;
  /** The teddy bear that explains the questions; absent when the shop wants none. */
  private guide: GuideApi | undefined;
  /** The quiet "what am I editing right now" highlight; on regardless of the guide. */
  private focusSpot: FocusSpot | undefined;
  /** Whether the opening "start here" suggestion has been made yet. */
  private suggestedStart = false;
  private product: ProductDefinition | undefined;
  /**
   * Each sound question's picks. A product may ask two (a design's own and a
   * Sound add-on), and a question can be hidden by the variation ("Bez zvuka"
   * after the shopper had already picked a song): only the sounds of questions
   * on screen are part of the order — see `visibleAudios`.
   */
  private readonly audiosByOption = new Map<string, AudioSelection[]>();

  /** The sounds the order carries: every visible sound question's, in form order. */
  private visibleAudios(): AudioSelection[] {
    const product = this.product;
    if (!product) return [];
    const visible = new Set((this.form?.visibleOptions() ?? product.options).map((o) => o.id));
    const out: AudioSelection[] = [];
    for (const option of product.options) {
      if (!visible.has(option.id)) continue;
      out.push(...(this.audiosByOption.get(option.id) ?? []));
    }
    // Picks reported without their question (an older form) always count.
    out.push(...(this.audiosByOption.get('') ?? []));
    return out;
  }
  private committing = false;
  private committedId: string | undefined;
  /**
   * This form session's commit key.
   *
   * A double click, a flaky connection or a retry after a dropped response all
   * send the same request; with the key they replay the first personalization
   * instead of minting a second one behind the cart line. It is renewed once the
   * design has actually reached the cart, so deliberately adding a changed
   * design afterwards is a new order rather than a conflict.
   */
  private commitKey = newCommitKey();
  private cartPreviewUpload: { signature: string; id: string } | undefined;
  private commitSignature: string | undefined;
  /**
   * The finished design, saved ahead of the click (cart-prep.ts): add-to-cart
   * then only writes its id into the form.
   */
  private readonly cartPrep = new CartPrep<CommitOutcome>({
    current: () => this.designToSave(),
    // Long enough that a shopper nudging a photo about saves once, when they
    // stop, rather than once per nudge (each save is a picture upload).
    delayMs: 2500,
    setTimer: (run, ms) => window.setTimeout(run, ms),
    clearTimer: (handle) => window.clearTimeout(handle as number),
  });
  /** Photo questions whose picture is still being cut out: not final yet. */
  private readonly photoWork = new Set<string>();
  /**
   * AI design layers by photo question: the photo, the layer, and the words its
   * picture was made with (a name on the hat). New words make a new picture.
   */
  private readonly styleRuns = new Map<string, { assetId: string; url: string; target: string; words: string }>();
  private styleTimer: number | undefined;
  private readonly styledPhotos = new AiPhotoDesigns({
    request: (context, cacheOnly) => this.api.stylize(context.templateId, context.target, context.assetId, context.texts, cacheOnly),
    changed: (context, state, cacheOnly) => this.stylePhotoState(context, state, cacheOnly),
  });
  /**
   * The design saved ahead of the click and not bought (yet). The next save
   * replaces it on the server, so a shopper who keeps changing their design
   * leaves one saved design behind, not one per change.
   */
  private heldId: string | undefined;
  /** A batch whose answer never came (no connection): a retry of the same design reuses it, so it can never go in twice. */
  private retryBatch: { batch: string; signature: string } | undefined;
  private readonly status: HTMLParagraphElement;
  private cartValidationError = false;
  private readonly canvas: HTMLCanvasElement;
  private readonly badge: HTMLSpanElement;
  private readonly body: HTMLDivElement;
  private readonly previewBox: HTMLDivElement;
  /** The caption bar under the artwork, and the slot its action buttons go in. */
  private readonly previewCap: HTMLDivElement;
  private readonly previewCapActions: HTMLSpanElement;
  private totalLine: HTMLDivElement | undefined;
  /** The popup's "Završi" step: the same price box, above its add button. */
  private stepPriceBox: HTMLDivElement | undefined;

  // Wizard state
  private overlay: HTMLDivElement | undefined;
  /** "Personalizacija je sačuvana." with its "Dodaj u korpu", while on screen. */
  private savedToast: ToastHandle | undefined;
  private stepIndex = 0;
  private launchArea: HTMLDivElement | undefined;
  /** True until the first answer when the shop chose not to draw the preview on load. */
  private deferPreview = false;
  /** Photo answers already seen, so "open for framing after upload" fires once per upload. */
  private readonly seenPhotos = new Set<string>();
  private zoomBound = false;

  constructor(
    private readonly config: WidgetConfig,
    private readonly host: HTMLElement,
  ) {
    this.api = new Api(config.apiUrl, config.externalOrderToken, config.externalRevisionToken);
    this.root = host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = STYLES;
    // A shop's own rules, applied after ours so they win — filled in build().
    this.customStyle = document.createElement('style');
    this.customStyle.className = 'pz-custom-css';

    this.body = document.createElement('div');
    this.body.className = 'pz-wrap';

    // The preview card: the artwork, then a caption bar under it. Nothing sits
    // on the picture itself — the state ("Pregled dizajna", approximate or
    // not) and the actions (full size, fold away) live in the caption, the way
    // a gallery captions a view. The one exception is the hover hint at the
    // top-right on a mouse, which is how every product gallery says "click to
    // enlarge"; it is invisible until the pointer is over the picture.
    this.previewBox = document.createElement('div');
    this.previewBox.className = 'pz-preview';
    this.canvas = document.createElement('canvas');
    this.badge = document.createElement('span');
    this.badge.className = 'pz-badge';
    this.badge.textContent = TEXTS.previewApprox;
    const hint = document.createElement('button');
    hint.type = 'button';
    hint.className = 'pz-preview-hint';
    hint.tabIndex = -1;
    hint.setAttribute('aria-hidden', 'true');
    hint.innerHTML = `${ICONS.expand}<span>Puna veličina</span>`;
    hint.addEventListener('click', (event) => {
      event.stopPropagation();
      this.openLightboxFromPage();
    });
    const cap = document.createElement('div');
    cap.className = 'pz-preview-cap';
    const capText = document.createElement('span');
    capText.className = 'pz-preview-cap-text';
    capText.appendChild(this.badge);
    this.previewCapActions = document.createElement('span');
    this.previewCapActions.className = 'pz-preview-cap-actions';
    cap.append(capText, this.previewCapActions);
    this.previewCap = cap;
    this.previewBox.append(this.canvas, hint, cap);
    // A click on the picture itself opens it full size — a tap, not the end of
    // a drag: framing a photo or panning a map moves the pointer, and that
    // must never pop the lightbox when the finger lifts.
    let pressed: { x: number; y: number } | null = null;
    this.canvas.addEventListener('pointerdown', (event) => {
      pressed = { x: event.clientX, y: event.clientY };
    });
    this.canvas.addEventListener('click', (event) => {
      const start = pressed;
      pressed = null;
      if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 6) return;
      // A collage on screen: a tap on one of its slots opens "Raspored slika"
      // on that slot.
      if (this.collageTap(event)) return;
      if (this.previewStyle !== 'side' || this.previewBox.parentElement !== this.body) return;
      this.openLightboxFromPage();
    });
    // The mockup strip is built once the product is known (see build()).

    const right = document.createElement('div');
    right.className = 'pz-panel';
    this.status = document.createElement('p');
    this.status.className = 'pz-status';
    this.status.hidden = true;
    // A skeleton of the form rather than a spinner: the page keeps the shape it
    // is about to have, so nothing below it jumps when the questions arrive.
    const loading = document.createElement('div');
    loading.className = 'pz-loading';
    loading.setAttribute('role', 'status');
    const loadingText = document.createElement('span');
    loadingText.className = 'pz-sr';
    loadingText.textContent = TEXTS.loading;
    loading.appendChild(loadingText);
    for (const kind of ['title', 'label', 'field', 'label', 'field', 'button']) {
      const bone = document.createElement('i');
      bone.className = `pz-bone pz-bone--${kind}`;
      bone.setAttribute('aria-hidden', 'true');
      loading.appendChild(bone);
    }
    right.append(this.status, loading);

    this.body.append(right);

    // The design lives full-screen behind a tap, not permanently on the page:
    // a small thumbnail opens this lightbox wherever the shopper is.
    const lightboxInner = document.createElement('div');
    lightboxInner.className = 'pz-lightbox-inner';
    const lightboxClose = document.createElement('button');
    lightboxClose.type = 'button';
    lightboxClose.className = 'pz-modal-close pz-lightbox-close';
    lightboxClose.innerHTML = ICONS.close;
    lightboxClose.setAttribute('aria-label', 'Zatvori pregled');
    lightboxClose.addEventListener('click', () => this.closeLightbox());
    const lightboxDone = document.createElement('button');
    lightboxDone.type = 'button';
    lightboxDone.className = 'pz-btn pz-lightbox-done';
    lightboxDone.append(iconEl('check'), document.createTextNode('Gotovo'));
    lightboxDone.addEventListener('click', () => this.closeLightbox());
    // Shown when a photo is on the design: most shoppers never guess the
    // picture itself is draggable unless told, and this is the moment to tell them.
    this.lightboxHint = document.createElement('p');
    this.lightboxHint.className = 'pz-lightbox-hint';
    this.lightboxHint.innerHTML =
      '\ud83d\udc46 <b>Prevuci sliku</b> da je pomeri\u0161 \u00b7 <b>u\u0161tipni</b> ili <b>+/\u2212</b> za zum';
    this.lightboxHint.hidden = true;
    const heading = document.createElement('div');
    heading.className = 'pz-lightbox-head';
    const title = document.createElement('strong');
    title.textContent = 'Tvoj dizajn';
    heading.append(title, lightboxClose);
    lightboxInner.append(heading, this.previewBox, this.lightboxHint, lightboxDone);
    this.lightboxInner = lightboxInner;

    this.root.append(style, this.customStyle, this.body);
    if (this.config.operatorMode) {
      // Who fills this line: the Hub team, never the shopper (no cart in this mode).
      const tag = document.createElement('div');
      tag.className = 'pz-operator-tag';
      tag.setAttribute('role', 'note');
      tag.textContent = `Popunjava: ${this.config.operatorMode.label} · BearBox tim`;
      tag.style.cssText = 'margin:0 0 12px;padding:8px 12px;border-radius:8px;background:#ede7f6;color:#4a2f8a;font:600 13px/1.4 system-ui,sans-serif';
      this.body.prepend(tag);
    }

    // The overlay is a top-layer <dialog> where the browser has one: a theme
    // that transforms the product column would otherwise clip a fixed overlay
    // into a corner of that column. createLayer also owns the scroll lock,
    // Escape, the backdrop tap and returning focus to whatever opened it.
    this.lightboxLayer = createLayer({
      host: this.root,
      className: 'pz-lightbox',
      content: lightboxInner,
      label: 'Pregled dizajna',
      onClose: () => {
        this.stopLightboxWatch?.();
        this.stopLightboxWatch = undefined;
        // Escape and the backdrop close it too, not only the buttons.
        this.guide?.relayout();
        // Framing done: this is the design the shopper settled on.
        this.cartPrep.schedule(600);
      },
    });
    this.lightbox = this.lightboxLayer.element as HTMLDivElement;

    // The lightbox is sized from the design's aspect and the VISIBLE viewport,
    // which changes on rotation and when the keyboard opens — so it is
    // recomputed while open rather than once at open time.
    const resize = (): void => {
      if (this.lightboxLayer.isOpen) this.sizeLightbox();
    };
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    window.visualViewport?.addEventListener('resize', resize);
  }

  private customStyle!: HTMLStyleElement;
  private lightbox!: HTMLDivElement;
  private lightboxLayer!: LayerHandle;
  private stopLightboxWatch: (() => void) | undefined;
  private wizardLayer: LayerHandle | undefined;
  /** The "Čuvanje…" toast, dismissed the moment the status line clears. */
  private savingToast: ToastHandle | undefined;
  private stopPreviewWatch: (() => void) | undefined;
  private previewFab: HTMLButtonElement | undefined;
  private stopFabWatch: (() => void) | undefined;
  /** Bound once; a product swap must not stack a second set of listeners. */
  private listenersBound = false;
  /** The variation on the page, kept across product swaps. */
  private variation: { id: number | null; attributes: Record<string, string> } = {
    id: null,
    attributes: {},
  };
  private lightboxInner!: HTMLDivElement;
  private lightboxHint!: HTMLParagraphElement;
  /** Live thumbnails of the design, refreshed after every paint. */
  private readonly thumbs = new Set<HTMLCanvasElement>();

  /** A small live copy of the design canvas; stays fresh via blitThumbs. */
  private makeThumb(sizePx = 72): HTMLCanvasElement {
    const thumb = document.createElement('canvas');
    thumb.className = 'pz-thumb';
    this.thumbs.add(thumb);
    this.blitThumb(thumb, sizePx);
    return thumb;
  }

  private blitThumb(thumb: HTMLCanvasElement, sizePx = 72): void {
    const source = this.canvas;
    if (!source.width || !source.height) return;
    const scale = (sizePx * 2) / Math.max(source.width, source.height);
    thumb.width = Math.max(2, Math.round(source.width * scale));
    thumb.height = Math.max(2, Math.round(source.height * scale));
    thumb.style.width = `${Math.round(thumb.width / 2)}px`;
    thumb.style.height = `${Math.round(thumb.height / 2)}px`;
    thumb.getContext('2d')?.drawImage(source, 0, 0, thumb.width, thumb.height);
  }

  private blitThumbs(): void {
    for (const thumb of this.thumbs) {
      if (!thumb.isConnected) {
        this.thumbs.delete(thumb);
        continue;
      }
      this.blitThumb(thumb);
    }
  }

  /**
   * The frame takes the design's exact aspect at the largest size the screen
   * allows. Sized from the VISIBLE viewport (`--pz-vvw/--pz-vvh`, which the
   * layer keeps current) and the scene — never from the canvas, whose displayed
   * size follows the previous render and would feed back.
   */
  private sizeLightbox(): void {
    const side = this.previewSide();
    if (!side || side.width <= 0 || side.height <= 0) return;
    const vw = this.layerPx('--pz-vvw', window.innerWidth);
    const vh = this.layerPx('--pz-vvh', window.innerHeight);
    const stripHeight = this.previewStrip ? 72 : 0;
    const frame = previewFrame({ w: vw - 24, h: vh }, side, 156 + stripHeight + (this.lightboxHint.hidden ? 0 : 48));
    this.lightboxInner.style.width = Math.max(248, frame.width) + 'px';
    this.previewBox.style.setProperty('--pz-frame-width', frame.width + 'px');
    this.previewBox.style.setProperty('--pz-frame-height', frame.height + 'px');
  }

  /** One of the layer's viewport custom properties, in pixels. */
  private layerPx(name: string, fallback: number): number {
    const raw = this.lightbox?.style.getPropertyValue(name) ?? '';
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  private openLightbox(): void {
    // While the wizard holds the canvas in its own pane, there is nothing to
    // show here — and stealing it would blank the pane.
    if (this.previewBox.parentElement !== this.lightboxInner) return;
    // The adjust hint appears exactly when there is a photo to adjust.
    this.lightboxHint.hidden = !this.previewBox.querySelector('.pz-edit');
    this.canvas.style.transform = '';
    this.canvas.style.transformOrigin = '';
    this.sizeLightbox();
    this.lightboxLayer.open();
    this.sizeLightbox();
    // A dialog in the top layer has no size until the browser has laid it out.
    this.afterLayout(() => {
      this.sizeLightbox();
      this.redraw(this.form?.current ?? {}, true);
      // The canvas has moved to the middle of the screen; the bear re-anchors.
      this.guide?.relayout();
    });
    this.stopLightboxWatch?.();
    this.stopLightboxWatch = observeSize(this.lightbox, () => {
      this.sizeLightbox();
      this.redraw(this.form?.current ?? {}, true);
    });
  }

  private closeLightbox(): void {
    this.lightboxLayer.close();
    // The design is back in its small box on the page, and so is the bear.
    this.guide?.relayout();
  }

  /**
   * Runs once the browser has actually laid the new overlay out.
   *
   * Two frames, because the first only guarantees the style has been applied;
   * a timer backs it up because a hidden tab never paints and would otherwise
   * leave the canvas at its old size when the shopper comes back.
   */
  private afterLayout(run: () => void): void {
    let done = false;
    const once = (): void => {
      if (done) return;
      done = true;
      run();
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(once));
    }
    window.setTimeout(once, 250);
  }

  /** Redraws whenever the box the canvas lives in changes size, wherever it is. */
  private watchPreviewBox(): void {
    this.stopPreviewWatch?.();
    const box = this.previewBox.parentElement ?? this.previewBox;
    this.stopPreviewWatch = observeSize(box, () => {
      if (this.form) this.redraw(this.form.current, true);
    });
  }

  /**
   * The measurements the stylesheet cannot take for itself: how much of the
   * top of the viewport a theme's fixed header owns, how much of the bottom a
   * sticky add-to-cart bar owns, whether `position: sticky` works here at all,
   * and the in-app browser's own invisible toolbar.
   */
  /**
   * A full row of its own. Themes lay `form.cart` out as a non-wrapping flex
   * row (quantity | button) or a grid; our host can claim a 100% basis, but
   * only the parent can be told to wrap, so the one inline property it needs
   * is written here. Nothing else on the parent is touched.
   */
  private claimFullWidth(): void {
    const parent = this.host.parentElement;
    if (!parent) return;
    const style = getComputedStyle(parent);
    if (/flex/.test(style.display) && style.flexWrap === 'nowrap' && !parent.style.flexWrap) {
      const row = /^(row|row-reverse)$/.test(style.flexDirection);
      if (row) parent.style.flexWrap = 'wrap';
    }
  }

  private refreshViewportVars(): void {
    this.claimFullWidth();
    const top = stickyTopOffset() + 8;
    const bottom = bottomBarOffset();
    this.host.style.setProperty('--pz-sticky-top', `${top}px`);
    this.host.style.setProperty('--pz-bottom-bar', `${bottom}px`);
    // The floating thumbnail may live outside the widget (see fixedRoot), so it
    // carries the measurement itself rather than inheriting it.
    this.previewFab?.style.setProperty('--pz-bottom-bar', `${bottom}px`);
    this.previewFab?.style.setProperty('--pz-sticky-top', `${stickyTopOffset()}px`);
    // Instagram and friends draw their own bar over the page and report no
    // safe-area inset for it, so the footer has to reserve the room itself.
    this.host.style.setProperty('--pz-inapp-bottom', isInAppBrowser() ? '44px' : '0px');
    // An `overflow: hidden` summary column (Storefront, Astra, Flatsome) makes
    // `position: sticky` silently do nothing; a preview that pretends to be
    // sticky and is not just scrolls away, so it is turned off and a floating
    // thumbnail takes over.
    const broken = stickyIsBroken(this.host);
    this.body.classList.toggle('pz-no-sticky', broken);
    if (broken && this.previewStyle === 'side' && !this.studioHosted) {
      this.ensureFab();
      this.syncFab();
    } else {
      this.removeFab();
    }
  }

  /**
   * Opens the full-size view from the page itself (the floating thumbnail, the
   * preview's own corner button): the canvas moves into the lightbox first,
   * because the lightbox only shows what is inside it.
   */
  private openLightboxFromPage(): void {
    if (this.previewBox.parentElement !== this.lightboxInner) {
      this.lightboxInner.insertBefore(
        this.previewBox,
        this.lightboxInner.querySelector('.pz-lightbox-done'),
      );
    }
    this.openLightbox();
  }

  /**
   * The corner button on the page's own preview: "see it full size". Only on
   * the page — inside the popup and the lightbox the picture is already as big
   * as it gets, and the CSS hides it there.
   */
  private makePreviewOpen(): HTMLButtonElement {
    const open = document.createElement('button');
    open.type = 'button';
    open.className = 'pz-preview-open';
    open.setAttribute('aria-label', 'Pogledaj dizajn u punoj veličini');
    const word = document.createElement('span');
    word.className = 'pz-btn-label';
    word.textContent = 'Puna veličina';
    open.append(iconEl('expand'), word);
    open.addEventListener('click', (event) => {
      event.stopPropagation();
      this.openLightboxFromPage();
    });
    return open;
  }

  /**
   * A floating thumbnail, shown once the preview itself has scrolled away on a
   * page where it cannot be sticky. Tapping it opens the full-size design.
   */
  private ensureFab(): void {
    if (this.previewFab) return;
    // A slim bar docked under the shop's header, not a bubble in a corner:
    // the live thumbnail, "Tvoj dizajn", and "Pogledaj" — the shape of a
    // theme's own sticky product bar, which is what it stands in for.
    const fab = document.createElement('button');
    fab.type = 'button';
    fab.className = 'pz-preview-fab';
    fab.setAttribute('aria-label', 'Pogledaj dizajn u punoj veličini');
    fab.hidden = true;
    const pic = document.createElement('span');
    pic.className = 'pz-preview-fab-pic';
    pic.appendChild(this.makeThumb(44));
    const text = document.createElement('span');
    text.className = 'pz-preview-fab-text';
    text.innerHTML = '<strong>Tvoj dizajn</strong><small>Ažurira se dok kucaš</small>';
    const action = document.createElement('span');
    action.className = 'pz-preview-fab-ic';
    action.append(document.createTextNode('Pogledaj'), iconEl('expand'));
    fab.append(pic, text, action);
    fab.addEventListener('click', () => this.openLightboxFromPage());
    // A transformed ancestor would pin this to the bottom of the product
    // column instead of the screen; fixedRoot portals it out when that is so.
    fixedRoot(this.root).appendChild(fab);
    this.previewFab = fab;
    // Only worth showing once the real preview is off screen.
    if (typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver(() => this.syncFab(), { threshold: 0 });
      observer.observe(this.previewBox);
      this.stopFabWatch = () => observer.disconnect();
    }
    this.syncFab();
  }

  /**
   * Shows the floating thumbnail exactly when the preview itself is not on
   * screen. Measured rather than only observed: the observer is the fast path,
   * but a browser that lacks it — or a tab that was in the background while the
   * shopper scrolled — must still end up with the right answer.
   */
  private syncFab(): void {
    const fab = this.previewFab;
    if (!fab) return;
    const rect = this.previewBox.getBoundingClientRect();
    const visible = rect.bottom > 0 && rect.top < (window.innerHeight || 0) && rect.height > 0;
    fab.hidden = visible || this.lightboxLayer.isOpen || this.wizardOpen;
  }

  private removeFab(): void {
    this.stopFabWatch?.();
    this.stopFabWatch = undefined;
    this.previewFab?.remove();
    this.previewFab = undefined;
  }

  /**
   * A tap on the preview (not a drag) while a photo collage is on screen: on
   * one of its slots it opens "Raspored slika" focused on that slot; anywhere
   * else in the wizard (where a tap has no other job) at the whole sheet.
   * False when the tap is not the collage's, so the page does what it always did.
   */
  private collageTap(event: MouseEvent): boolean {
    const form = this.form;
    if (!form || this.surfacePreview?.active) return false;
    const shown = form.collagesShown();
    if (shown.length === 0) return false;
    const scene = this.preview?.paintedScene;
    const rect = this.canvas.getBoundingClientRect();
    if (scene && rect.width > 0 && scene.width > 0) {
      const k = rect.width / scene.width;
      const hit = collageSlotAtPoint(scene, (event.clientX - rect.left) / k, (event.clientY - rect.top) / k, (id) => {
        const owner = form.collageOf(id);
        return owner !== undefined && shown.includes(owner);
      });
      if (hit) {
        void form.openCollageBoard(form.collageOf(hit)!, hit);
        return true;
      }
    }
    if (!this.wizardOpen) return false;
    void form.openCollageBoard(shown[0]!);
    return true;
  }

  /**
   * "Uredi raspored" in the preview's caption while a collage question is on
   * screen. In the popup the caption is hidden to save height, so the preview
   * wears the button as a chip instead (`.has-collage`).
   */
  private syncCollageCap(): void {
    const shown = this.form?.collagesShown() ?? [];
    let button = this.previewCapActions.querySelector<HTMLButtonElement>('.pz-preview-collage');
    this.previewBox.classList.toggle('has-collage', shown.length > 0);
    if (shown.length === 0) {
      if (button) button.hidden = true;
      return;
    }
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'pz-preview-collage';
      const word = document.createElement('span');
      word.className = 'pz-btn-label';
      word.textContent = COLLAGE_TEXTS.openBoard;
      button.append(iconEl('pencil'), word);
      button.setAttribute('aria-label', COLLAGE_TEXTS.openBoard);
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const optionId = button!.getAttribute('data-option');
        if (optionId) void this.form?.openCollageBoard(optionId);
      });
      this.previewCapActions.insertBefore(button, this.previewCapActions.firstChild);
    }
    button.hidden = false;
    button.setAttribute('data-option', shown[0]!);
  }

  /** The caption's "Sakrij / Prikaži": folds the preview to a strip on a narrow page. */
  private makePreviewToggle(): HTMLButtonElement {
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'pz-preview-toggle';
    toggle.setAttribute('aria-expanded', 'true');
    const word = document.createElement('span');
    word.className = 'pz-btn-label';
    word.textContent = 'Sakrij';
    toggle.append(word, iconEl('chevronDown'));
    toggle.addEventListener('click', () => {
      const collapsed = this.previewBox.classList.toggle('is-collapsed');
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      word.textContent = collapsed ? 'Prikaži' : 'Sakrij';
      if (this.form) this.afterLayout(() => this.redraw(this.form!.current, true));
    });
    return toggle;
  }

  /** The ids of the required questions still unanswered, in form order. */
  private missingIds(): string[] {
    const form = this.form;
    if (!form) return [];
    return form
      .visibleOptions()
      .filter((option) => form.needsAnswer(option) && !form.isAnswered(option.id))
      .map((option) => option.id);
  }

  /**
   * What to say about the questions still holding things up: a collage's own
   * words when one of them is a collage ("Sačekaj da se sve slike pošalju.",
   * "Nedostaju još 3 slike. …"), otherwise `fallback`.
   */
  private missingText(ids: string[], fallback: string): string {
    for (const id of ids) {
      const own = this.form?.missingMessage(id);
      if (own) return own;
    }
    return fallback;
  }

  /**
   * Marks the unanswered rows and takes the shopper to the first of them.
   *
   * A message at the top of a long form is a message nobody reads: the field
   * that is actually blocking the sale has to be the thing on screen, with the
   * caret in it. `markMissing`/`firstMissingRow` belong to the form; they are
   * reached defensively so an older form build degrades to just the message.
   */
  private focusFirstMissing(ids: string[]): boolean {
    const form = this.form as unknown as
      | {
          markMissing?: (ids: string[]) => void;
          firstMissingRow?: () => HTMLElement | null;
          focusRow?: (row: HTMLElement) => void;
        }
      | undefined;
    try {
      form?.markMissing?.(ids);
    } catch {
      /* the message alone still tells the shopper what is wrong */
    }
    // The bear points at the same field the caret is about to land in.
    this.guide?.missing(ids);
    let row: HTMLElement | null = null;
    try {
      row = form?.firstMissingRow?.() ?? null;
    } catch {
      row = null;
    }
    if (!row) return false;
    // The form knows how to bring one of its own rows forward — a row inside a
    // folded accordion or another section has to be opened first.
    if (typeof form?.focusRow === 'function') {
      try {
        form.focusRow(row);
        return true;
      } catch {
        /* fall through to the plain scroll below */
      }
    }
    try {
      row.scrollIntoView({ block: 'center' });
    } catch {
      row.scrollIntoView();
    }
    const field = row.querySelector<HTMLElement>(
      'input:not([type="hidden"]), textarea, select, button',
    );
    try {
      field?.focus({ preventScroll: true });
    } catch {
      field?.focus();
    }
    return true;
  }

  /**
   * The compact "Pogledaj dizajn" trigger: one button that opens the lightbox.
   *
   * It has to read as a button at a glance, not as a caption beside a picture:
   * a small live thumbnail wearing a magnifier badge (the gallery convention
   * for "tap to enlarge"), the two lines of copy, and a filled accent pill \u2014
   * "Uve\u0107aj" \u2014 that is visibly pressable even on a touch screen, where there
   * is no hover state to discover. The whole card is the hit area.
   */
  private makeDesignCard(): HTMLButtonElement {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'pz-peek';
    card.setAttribute('aria-haspopup', 'dialog');
    card.setAttribute('aria-label', 'Pogledaj dizajn u punoj veli\u010dini');
    const pic = document.createElement('span');
    pic.className = 'pz-peek-pic';
    pic.append(this.makeThumb(48), iconEl('zoomIn', 'pz-peek-badge'));
    const copy = document.createElement('span');
    copy.className = 'pz-peek-copy';
    copy.innerHTML = '<strong>Pogledaj dizajn</strong><span>Uvek a\u017euran sa tvojim unosima</span>';
    const cta = document.createElement('span');
    cta.className = 'pz-peek-cta';
    cta.append(document.createTextNode('Uve\u0107aj'), iconEl('expand'));
    card.append(pic, copy, cta);
    card.addEventListener('click', () => {
      // "Show the preview button only when all required options are filled".
      if (this.product?.theme?.previewButtonRequiredOnly === 'true') {
        const missing = this.form?.missingRequired() ?? [];
        if (missing.length > 0) {
          this.showError(this.missingText(this.missingIds(), (this.product?.theme?.requiredMessage ?? TEXTS.fillIn) + missing.join(', ')));
          return;
        }
      }
      this.openLightbox();
    });
    return card;
  }

  /**
   * The shop-wide behaviours from Customily's Preview Behavior and Styles
   * tabs that are not plain CSS variables.
   */
  private applyBehaviourTheme(theme: Record<string, string | undefined>): void {
    this.deferPreview = theme.loadPreviewByDefault === 'false' && !this.studioHosted;
    this.previewBox.classList.toggle('is-sticky', theme.stickyPreview === 'true');
    this.previewBox.classList.toggle('pz-zoom-hover', theme.zoomStyle === 'hover');
    this.previewBox.classList.toggle('pz-zoom-click', theme.zoomStyle === 'click');
    this.body.classList.toggle('pz-thumbs-left', theme.galleryThumbs === 'left');
    this.body.classList.toggle('pz-thumbs-right', theme.galleryThumbs === 'right');
    if (!this.zoomBound) {
      this.zoomBound = true;
      // Hover zoom: the canvas magnifies around the cursor; leaving resets it.
      // Never bound on a touch device: a drag across the photo would fire
      // pointermove, leave the canvas stuck at scale(2), and there is no
      // "pointerleave" a finger can produce to undo it. Tap-to-open still works.
      if (canHover()) {
      this.previewBox.addEventListener('pointermove', (e) => {
        if (this.lightboxLayer.isOpen || !this.previewBox.classList.contains('pz-zoom-hover')) return;
        const rect = this.canvas.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        this.canvas.style.transformOrigin = `${x}% ${y}%`;
        this.canvas.style.transform = 'scale(2)';
      });
      this.previewBox.addEventListener('pointerleave', () => {
        this.canvas.style.transform = '';
        this.canvas.style.transformOrigin = '';
      });
      }
      // Click zoom: the page-side preview opens full size, like the card does.
      this.previewBox.addEventListener('click', () => {
        if (this.previewBox.classList.contains('pz-zoom-click') && this.previewBox.parentElement !== this.lightboxInner) {
          this.openLightbox();
        }
      });
    }
    // Where the form sits relative to the shop's cart form — Customily's
    // "Options position". `inside`/`auto` leave the connector's placement.
    const position = theme.optionsPosition;
    if ((position === 'before' || position === 'after') && !this.studioHosted) {
      const cart = document.querySelector<HTMLFormElement>('form.cart');
      if (cart && cart.parentElement && !cart.contains(this.host)) {
        if (position === 'before') cart.parentElement.insertBefore(this.host, cart);
        else cart.insertAdjacentElement('afterend', this.host);
      }
    }
  }

  /** "Auto zoom on upload": a freshly uploaded photo opens for framing at once. */
  private maybeOpenAfterUpload(values: Values): void {
    if (this.product?.theme?.autoZoomOnUpload !== 'true' || !this.product) return;
    for (const option of this.product.options) {
      if (option.type !== 'image_upload') continue;
      const value = values[option.id];
      if (!value) continue;
      // An AI design opens the lightbox itself, once the picture is made.
      if (this.styleTargetFor(option.id)) continue;
      const key = `${option.id}:${JSON.stringify(value).slice(0, 120)}`;
      if (this.seenPhotos.has(key)) continue;
      this.seenPhotos.add(key);
      // The first sighting after a restore is not an upload.
      if (this.seenPhotos.size > 1 || this.form?.isAnswered(option.id)) {
        if (this.previewBox.parentElement === this.lightboxInner) this.openLightbox();
      }
    }
  }

  /** Whether the artwork rides in a lightbox (compact) or fills half the page. */
  private get previewStyle(): 'side' | 'compact' {
    if (this.mode === 'wizard') return 'compact';
    const own = this.product?.display?.previewStyle;
    if (own === 'side') return 'side';
    if (own === 'compact') return 'compact';
    return this.themePreviewStyle() === 'page' ? 'side' : 'compact';
  }

  /**
   * Which mockup the preview shows, when the template has several — the
   * shopper switches with the thumbnails under the picture. Null means the
   * first (or only) one.
   */
  private previewSideId: string | null = null;
  /**
   * A template with no mockup and several print sheets (a box printed on two
   * A3 sheets): the sheet the preview shows, by its place in `product.sides`
   * (side ids are not guaranteed unique). Null means the first.
   */
  private previewSheet: number | null = null;
  private previewStrip: HTMLDivElement | undefined;

  /**
   * The scene the preview draws: the chosen mockup (or print sheet), else the
   * first non-print side. `shown: false` ignores a print sheet the shopper
   * switched to — the cart picture is always the first one.
   */
  private previewSide(shown = true): ProductDefinition['sides'][number] | undefined {
    const product = this.product;
    if (!product) return undefined;
    return (
      (shown && this.previewSheet !== null ? product.sides[this.previewSheet] : undefined) ??
      (this.previewSideId ? product.sides.find((s) => s.id === this.previewSideId) : undefined) ??
      product.sides.find((s) => s.target !== 'print') ??
      product.sides[0]
    );
  }

  /**
   * Thumbnails for switching between mockups. Each shows its product photo —
   * a static picture, so choosing between "front" and "in the box" costs no
   * render — and the live canvas follows the pick.
   */
  private buildPreviewStrip(product: ProductDefinition): HTMLDivElement | undefined {
    const mockups = product.sides.filter((s) => s.target === 'preview');
    if (mockups.length === 0) return this.buildSheetStrip(product);
    if (mockups.length < 2) return undefined;
    const strip = document.createElement('div');
    strip.className = 'pz-previews';
    const urlOf = (assetId: string | undefined): string | undefined =>
      assetId ? product.assets.find((a) => a.id === assetId)?.url : undefined;
    for (const [i, side] of mockups.entries()) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pz-previews-item';
      button.title = side.name ?? `Prikaz ${i + 1}`;
      const photo = urlOf(side.background?.assetId);
      if (photo) {
        const img = document.createElement('img');
        img.src = photo;
        img.alt = '';
        button.appendChild(img);
      } else {
        button.textContent = String(i + 1);
      }
      button.addEventListener('click', () => {
        this.previewSideId = side.id;
        for (const el of strip.querySelectorAll('.pz-previews-item')) {
          el.classList.toggle('is-active', el === button);
        }
        this.redraw(this.form?.current ?? {}, true);
      });
      if (i === 0) button.classList.add('is-active');
      strip.appendChild(button);
    }
    return strip;
  }

  /**
   * No mockup and a photo collage over several print sheets (two A3 sheets): one
   * button per sheet ("List 1", "List 2") under the picture, so the shopper
   * sees every sheet — and a tap on a collage slot works on whichever one is
   * shown (`collageTap` hit-tests the painted scene).
   */
  private buildSheetStrip(product: ProductDefinition): HTMLDivElement | undefined {
    const sheets = product.sides.map((side, index) => ({ side, index })).filter(({ side }) => side.target !== 'preview');
    // Only for a photo collage spread over several sheets: other templates
    // with a back side keep today's single picture.
    const slots = new Set(
      product.options.filter((o) => o.type === PHOTO_COLLAGE_OPTION_TYPE).flatMap((o) => collageRulesOf(o.config).targetElementIds),
    );
    const holding = sheets.filter(({ side }) => (side.elements ?? []).some((el) => slots.has(el.id)));
    if (sheets.length < 2 || holding.length < 2) return undefined;
    const strip = document.createElement('div');
    strip.className = 'pz-previews is-sheets';
    strip.setAttribute('role', 'group');
    strip.setAttribute('aria-label', COLLAGE_TEXTS.sheets);
    // What the preview shows before any pick (`previewSide`'s own default).
    const first = product.sides.find((s) => s.target !== 'print') ?? product.sides[0];
    for (const [i, { side, index }] of sheets.entries()) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pz-previews-item is-sheet';
      const label = collageSheetLabel(side.name, i);
      button.textContent = label;
      button.title = side.name?.trim() || label;
      button.setAttribute('aria-pressed', side === first ? 'true' : 'false');
      button.addEventListener('click', () => {
        this.previewSheet = index;
        for (const el of strip.querySelectorAll('.pz-previews-item')) {
          el.classList.toggle('is-active', el === button);
          el.setAttribute('aria-pressed', el === button ? 'true' : 'false');
        }
        this.redraw(this.form?.current ?? {}, true);
      });
      if (side === first) button.classList.add('is-active');
      strip.appendChild(button);
    }
    return strip;
  }

  /** Set when the page was opened from the cart's "Izmeni" link. */
  private editContext:
    | { restored: Awaited<ReturnType<Api['loadPersonalization']>>; cartKey: string | null }
    | undefined;

  async start(): Promise<void> {
    this.disableCart(true);
    // However this goes, the button is released within the configured wait, so a
    // slow API can never silently prevent a sale.
    const release = window.setTimeout(() => this.disableCart(false), this.config.maxWaitMs);

    try {
      // Studio's order editor: the order's own release and its current state.
      const order = this.config.orderEdit
        ? await this.api.loadOrderEdit(this.config.orderEdit.personalizationId)
        : undefined;
      // The colour already picked on the page goes with the first request: a
      // product whose colours each have a template is served the right one at
      // once, not its main template first and the colour's a moment later (two
      // definitions and two sets of pictures on a phone's first visit).
      const pageVariation = order || this.config.preview ? undefined : readPageVariation();
      const product = order
        ? order.definition
        : this.config.preview
          ? await this.api.loadPreview(this.config.preview)
          : await this.api.loadProduct(this.config.productId, {
              templateId: this.config.templateId,
              optionSetId: this.config.optionSetId,
              ...(pageVariation && Object.keys(pageVariation.attributes).length > 0 ? { variation: pageVariation } : {}),
            });
      this.product = product;

      // A variation already picked when the page loaded (a shared link to an
      // A3 poster): Woo fires its event on load, but only after this script
      // may have started — read the form's current state as well. An order is
      // the variation it was bought as.
      this.variation = order?.variation ?? (this.config.externalOrderToken?product.externalVariation:undefined) ?? readPageVariation();

      if (order) {
        this.editContext = { restored: order.restore, cartKey: null };
        this.orderRevision = order.revision;
      }

      // "Izmeni" from the cart: load the committed answers before building, so
      // the form comes up already filled instead of starting over.
      const params = new URLSearchParams(window.location.search);
      const editId = order ? null : params.get('pz_edit');
      if (editId && /^[0-9a-f-]{36}$/i.test(editId)) {
        try {
          this.editContext = {
            restored: await this.api.loadPersonalization(editId),
            cartKey: params.get('pz_key'),
          };
        } catch {
          // A stale link still shows a fresh, working form.
        }
      }

      // The product's own template — what a variation without a rule returns to.
      // Served on a colour's template, the definition still names it.
      this.baseTemplateId = product.baseTemplateId ?? product.templateId;
      this.baseOptionSetId = product.baseOptionSetId ?? product.optionSetId ?? '';
      this.build(product);
      // The variation already on the page: its conditions, and the questions it answers.
      this.form?.setVariation(this.variation);
      this.disableCart(false);
      // A variation already on the page may name another template.
      if (product.variantRules?.length && this.variantRuleFor()) {
        await this.applyVariation(this.variation);
      }
    } catch (err) {
      // The skeleton promised a form that is not coming; the message replaces it.
      this.body.querySelector('.pz-loading')?.remove();
      this.showError(err instanceof Error ? err.message : TEXTS.failed);
      // Leaving the product unsellable would be worse than losing personalization.
      this.disableCart(false);
    } finally {
      window.clearTimeout(release);
    }
  }

  /** Cut-out slots whose old render is hidden while the shopper drags them. */
  private draggingElements = new Set<string>();

  /** Resolved once per preview paint, for code that must wait for the render. */
  private drawWaiters: Array<() => void> = [];
  /** The last crop head placement wrote, per option — a crop still equal to it was not touched by hand. */
  private autoHeadCrops = new Map<string, string>();
  /** Natural sizes of cut-out pictures, by URL. */
  private pictureSizes = new Map<string, Promise<{ w: number; h: number } | undefined>>();

  /**
   * The Mini Me head slot this option's photo lands on: the print side's image
   * layer with a face cut-out, unless the layer or the shop turned placement
   * off.
   */
  private headSlotFor(optionId: string): { element: ImageElement; side: ProductDefinition['sides'][number] } | undefined {
    const product = this.product;
    if (!product || product.theme?.headPlacement === 'false') return undefined;
    const elementIds = new Set(
      product.bindings.filter((b) => b.optionId === optionId && b.property === 'image').map((b) => b.elementId),
    );
    const sides = [...product.sides].sort((a, b) => Number(b.target === 'print') - Number(a.target === 'print'));
    for (const side of sides) {
      for (const element of side.elements ?? []) {
        if (!elementIds.has(element.id) || element.type !== 'image') continue;
        if (!element.effects?.some((e) => e.kind === 'faceCutout')) continue;
        if (element.effects.some((e) => e.kind === 'aiStyle' || e.kind === 'blurBackground')) continue;
        if (element.headFit?.off) return undefined;
        return { element, side };
      }
    }
    return undefined;
  }

  /**
   * A cut-out's framing as a free crop. Outside a cut-out there is only
   * transparency, so the window may leave the picture — and a free crop is
   * the one a `contain` slot honours; the plain cover crop is ignored there
   * and the whole photo-sized cut-out is shrunk into the slot instead.
   */
  private cutCrop(optionId: string, crop: ImageCrop): ImageCrop {
    const product = this.product;
    if (!product) return crop;
    const elementIds = new Set(
      product.bindings.filter((b) => b.optionId === optionId && b.property === 'image').map((b) => b.elementId),
    );
    for (const side of product.sides) {
      for (const element of side.elements ?? []) {
        if (!elementIds.has(element.id) || element.type !== 'image') continue;
        const effects = element.effects ?? [];
        if (effects.some((e) => e.kind === 'blurBackground' || e.kind === 'aiStyle')) return crop;
        if (effects.some((e) => e.kind === 'faceCutout' || e.kind === 'removeBackground')) return { ...crop, free: true };
      }
    }
    return crop;
  }

  /** The shop's default head placement, from its storefront settings. */
  private shopHeadFit(): HeadFit {
    const theme = this.product?.theme ?? {};
    const size = Number(theme.headSize);
    const align = theme.headAlign;
    const trim = theme.headTrim === undefined || theme.headTrim === '' ? NaN : Number(theme.headTrim);
    const fill = theme.headFill;
    return {
      ...(fill === 'height' || fill === 'face' ? { fill } : {}),
      ...(Number.isFinite(size) && size >= 40 && size <= 160 ? { size: size / 100 } : {}),
      ...(align === 'top' || align === 'center' || align === 'bottom' ? { align } : {}),
      ...(Number.isFinite(trim) && trim >= 0 && trim <= 100 ? { trim: trim / 100 } : {}),
    };
  }

  private pictureSize(url: string): Promise<{ w: number; h: number } | undefined> {
    let known = this.pictureSizes.get(url);
    if (!known) {
      known = new Promise((resolve) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image.naturalWidth ? { w: image.naturalWidth, h: image.naturalHeight } : undefined);
        image.onerror = () => resolve(undefined);
        image.src = url;
      });
      this.pictureSizes.set(url, known);
    }
    return known;
  }

  /** Resolves after the next preview paint, or after `timeout` ms regardless. */
  private nextDraw(timeout = 2500): Promise<void> {
    return new Promise((resolve) => {
      const timer = window.setTimeout(resolve, timeout);
      this.drawWaiters.push(() => {
        window.clearTimeout(timer);
        resolve();
      });
    });
  }

  /**
   * Places a measured cut-out head on its Mini Me body.
   *
   * The cut-out is the size of the photo, transparent but for the head; the
   * server measured the head (hair top to chin) and the renderer measures the
   * slot's visible window (its mask minus the body drawn over it). The head is
   * fitted into that window by the layer's own placement, then the shop's,
   * then the built-in one — a `free` crop, so it can be smaller than the photo
   * or sit near an edge the photo cut it at. The shopper's own framing is never
   * overridden unless `force` (they picked a face, which is their hand too).
   *
   * Resolves with where the picture sits on screen once the preview has been
   * repainted, for the landing animation.
   */
  private async placeHead(
    optionId: string,
    assetId: string,
    head: HeadMeasure,
    heads: HeadMeasure[],
    force = false,
  ): Promise<HeadLanding | undefined> {
    const product = this.product;
    const form = this.form;
    const preview = this.preview;
    if (!product || !form || !preview) return headDebug('place: no product/form/preview');
    const current = form.current[optionId];
    const answer = typeof current === 'string' ? { assetId: current } : (current as { assetId?: string; crop?: ImageCrop } | undefined);
    if (!answer?.assetId || answer.assetId !== assetId) return headDebug('place: a different photo by now', answer?.assetId, assetId);
    const touched = answer.crop && JSON.stringify(answer.crop) !== this.autoHeadCrops.get(optionId);
    if (touched && !force) return this.landingFor(optionId, assetId);

    const slot = this.headSlotFor(optionId);
    let crop: ImageCrop | undefined;
    const url = preview.assetUrl(assetId);
    const picture = url ? await this.pictureSize(url) : undefined;
    if (slot && picture) {
      const own = slot.element.headFit;
      let area = own?.area;
      // Box height (the default) fits the head to the whole layer box; only
      // face fill needs the slot's visible window.
      if (!area && resolveHeadFit(own, this.shopHeadFit()).fill === 'face') {
        // The window is measured on the design as it stands, so a body
        // variant the shopper picked (male/female, a uniform) is the one the
        // head is fitted to.
        const scene = applyValues(slot.side, product.bindings, form.current, this.valueLabels(),
          product.libraries ?? {}, uploadTargetsOf(product.options));
        area = await preview.headWindow(scene, slot.element.id);
      }
      const fit: HeadFit = { ...this.shopHeadFit(), ...own, ...(area ? { area } : {}) };
      // No measured window (the renderer did not arrive in time on a slow
      // phone): the slot's outline is unknown, so the face is not grown into
      // it on a guess — the whole head fits the slot, as it always did —
      // unless the layer or the shop asked for a trim explicitly.
      if (!area && own?.trim === undefined && this.shopHeadFit().trim === undefined) fit.trim = 0;
      crop = cropForHead(head, picture, { w: slot.element.transform.w, h: slot.element.transform.h }, fit, heads);
    } else if (head.face) {
      // Placement is off (or the picture would not load): the older framing.
      crop = this.cutCrop(optionId, cropForFace(head.face));
    }
    headDebug('place: crop', crop, 'slot', slot?.element.id, 'picture', picture);
    if (!crop) return headDebug('place: no crop');
    // A small head enlarged into the slot prints soft even when the photo as
    // a whole is big enough — say so, in the one calm sentence, on the card.
    if (slot && picture && crop.free) {
      // Scene pixels per picture pixel, as the renderer draws this crop.
      const scale = coverPhotoFrame(picture, { w: slot.element.transform.w, h: slot.element.transform.h }, crop).scale;
      // The head's height in the ORIGINAL's pixels (the page may be drawing a
      // smaller preview copy), against its drawn height in scene pixels.
      const drawnPx = head.box.h * picture.h * scale;
      const own = this.originalSizes.get(assetId);
      const headPx = head.box.h * (own?.h ?? picture.h);
      const ppi = headPrintPpi(headPx, drawnPx, slot.side.dpi || 300);
      if (ppi < FLOOR_PPI) {
        this.setPhotoState(optionId, { phase: 'ready', url: preview.assetUrl(assetId), advice: PHOTO_TEXTS.warn, assetId });
      }
    }
    // Still the same photo, and still not framed by hand while we measured?
    const now = form.current[optionId];
    const nowAnswer = typeof now === 'string' ? { assetId: now } : (now as { assetId?: string; crop?: ImageCrop } | undefined);
    if (nowAnswer?.assetId !== assetId) return headDebug('place: photo changed while measuring', nowAnswer?.assetId, assetId);
    if (!force && nowAnswer.crop && JSON.stringify(nowAnswer.crop) !== this.autoHeadCrops.get(optionId)) {
      return this.landingFor(optionId, assetId);
    }
    this.autoHeadCrops.set(optionId, JSON.stringify(crop));
    const drawn = this.nextDraw();
    form.setPhotoFraming(optionId, { assetId, crop });
    await drawn;
    return this.landingFor(optionId, assetId);
  }

  /** Where the option's picture sits on screen in the painted preview. */
  private async landingFor(optionId: string, assetId: string): Promise<HeadLanding | undefined> {
    const product = this.product;
    const preview = this.preview;
    const scene = preview?.paintedScene;
    if (!product || !preview || !scene) return headDebug('landing: nothing painted yet');
    const elementIds = new Set(
      product.bindings.filter((b) => b.optionId === optionId && b.property === 'image').map((b) => b.elementId),
    );
    const element = scene.elements.find((el) => elementIds.has(el.id) && el.type === 'image' && el.visible !== false) as
      | ImageElement
      | undefined;
    const url = preview.assetUrl(assetId);
    if (!element?.transform || !url) return headDebug('landing: slot not in the painted scene', !!element, !!url);
    const picture = await this.pictureSize(url);
    // A popup opened with the upload may not have laid the canvas out yet
    // (and a background tab pauses the frames that size it): wait briefly.
    let rect = this.canvas.getBoundingClientRect();
    for (let waited = 0; !rect.width && waited < 2000; waited += 100) {
      await new Promise((resolve) => window.setTimeout(resolve, 100));
      rect = this.canvas.getBoundingClientRect();
    }
    if (!picture || !rect.width || !scene.width) return headDebug('landing: no size', picture, rect.width, scene.width);
    const k0 = rect.width / scene.width;
    const box = element.transform;
    const slot = {
      left: rect.left + (box.cx - box.w / 2) * k0,
      top: rect.top + (box.cy - box.h / 2) * k0,
      width: box.w * k0,
      height: box.h * k0,
    };
    const crop = element.crop;
    let image: HeadLanding['image'];
    if (crop?.rotation) {
      // A straightened picture is drawn by the rotated cover frame: the crop
      // centre sits on the slot's centre at the frame's own scale.
      const frame = coverPhotoFrame({ w: picture.w, h: picture.h }, { w: box.w, h: box.h }, crop);
      const k = frame.scale * k0;
      image = {
        left: slot.left + slot.width / 2 - frame.crop.cx * picture.w * k,
        top: slot.top + slot.height / 2 - frame.crop.cy * picture.h * k,
        width: picture.w * k,
        height: picture.h * k,
      };
    } else {
      const win = photoWindow(picture.w, picture.h, box.w, box.h, crop);
      const k = slot.width / win.w;
      image = { left: slot.left - win.x * k, top: slot.top - win.y * k, width: picture.w * k, height: picture.h * k };
    }
    return {
      image,
      rotation: crop?.rotation ?? 0,
      pivot: { x: slot.left + slot.width / 2, y: slot.top + slot.height / 2 },
      slot,
      ...(crop?.free && crop.keep ? { keep: crop.keep } : {}),
    };
  }

  /**
   * The AI-styled layer this option's photo lands on, when there is one.
   *
   * Read from the template the same way the cut-out reads its flag: the studio
   * already put the prompt on the layer, and a second switch would drift.
   */
  private styleTargetFor(optionId: string): string | undefined {
    const product = this.product;
    if (!product) return undefined;
    const elementIds = new Set(
      product.bindings.filter((b) => b.optionId === optionId).map((b) => b.elementId),
    );
    for (const side of product.sides) {
      for (const element of side.elements ?? []) {
        if (!elementIds.has(element.id) || element.type !== 'image') continue;
        if (element.effects?.some((e) => e.kind === 'aiStyle')) return element.id;
      }
    }
    return undefined;
  }

  /**
   * The starting framing the author set for this option's slot, if any —
   * Customily's "initial size & position" for uploads. Read off the print
   * side's element, where the sync keeps the authored value.
   */
  private initialCropFor(optionId: string): { cx: number; cy: number; zoom: number; rotation?: number } | undefined {
    const product = this.product;
    if (!product) return undefined;
    const elementIds = new Set(
      product.bindings.filter((b) => b.optionId === optionId && b.property === 'image').map((b) => b.elementId),
    );
    for (const side of product.sides) {
      for (const element of side.elements ?? []) {
        if (!elementIds.has(element.id) || element.type !== 'image') continue;
        if (element.initialCrop) return element.initialCrop;
      }
    }
    return undefined;
  }

  /** True when Studio is driving this widget rather than a product page. */
  private get previewing(): boolean {
    return Boolean(this.config.preview);
  }

  /** True in Studio's order editor: the widget runs over an order, with no cart. */
  private get orderEditing(): boolean {
    return Boolean(this.config.orderEdit);
  }

  /**
   * Hosted by Studio — its preview or its order editor — rather than a shop
   * page: no WooCommerce around it (no page price, no cart form, no theme
   * placement), no drafts, no proof box.
   */
  private get studioHosted(): boolean {
    return this.previewing || this.orderEditing;
  }

  /**
   * Editing a line of the shopper's own cart (the connector checked it): the
   * page says so, the cart button reads "Sačuvaj izmene", and an edit that
   * changed nothing simply goes back to the cart.
   */
  private get editingCart(): boolean {
    return Boolean(this.config.editing && this.editContext?.cartKey && !this.orderEditing);
  }

  /** The restored design, to tell an unchanged edit from one to save. */
  private editBaseline: string | undefined;

  /**
   * What makes a cart line's design, for telling an unchanged edit: the
   * answers, the variation and the sounds as chosen (orderSignature) — not a
   * length a player learns while it loads.
   */
  private editSignature(): string {
    return JSON.stringify([this.orderSignature(), this.form?.currentVariation ?? null]);
  }

  /** Nothing about the cart line changed: not the design, the quantity or the variation. */
  private editUnchanged(): boolean {
    const editing = this.config.editing;
    if (!editing || this.editBaseline === undefined) return false;
    if (this.editSignature() !== this.editBaseline) return false;
    if (readQuantity() !== editing.quantity) return false;
    return (this.variation.id ?? 0) === (editing.variationId ?? 0) || !editing.variationId;
  }

  /**
   * The restored design as the page settles. WooCommerce picks the line's
   * variation, and the restored sounds finish loading, a moment after the
   * build — later on a slow phone. Until the shopper first touches the page,
   * whatever it settles to is the design they already had.
   */
  private followEditBaseline(): void {
    this.editBaseline = this.editSignature();
    let following = true;
    const stop = (): void => {
      if (!following) return;
      // One last reading as the hand lands (before the touch changes anything):
      // the 250 ms steps could leave out the restored sounds, which arrive a
      // moment after the build — a phone scrolled at once then saved every
      // edit, changed or not, as a new design.
      this.editBaseline = this.editSignature();
      following = false;
      window.clearInterval(timer);
      document.removeEventListener('pointerdown', stop, true);
      document.removeEventListener('keydown', stop, true);
    };
    const timer = window.setInterval(() => {
      if (following) this.editBaseline = this.editSignature();
    }, 250);
    document.addEventListener('pointerdown', stop, true);
    document.addEventListener('keydown', stop, true);
    window.setTimeout(stop, 20000);
  }

  /** The note at the top of an edit: what is going on, and the way back. */
  private showEditNote(): void {
    this.body.querySelector('.pz-edit-note')?.remove();
    const note = document.createElement('div');
    note.className = 'pz-edit-note';
    note.setAttribute('role', 'note');
    const text = document.createElement('div');
    text.className = 'pz-edit-note-text';
    const title = document.createElement('strong');
    title.textContent = TEXTS.editNote;
    const hint = document.createElement('span');
    hint.textContent = TEXTS.editHint;
    text.append(title, hint);
    note.append(iconEl('pencil'), text);
    if (this.config.cartUrl) {
      const back = document.createElement('a');
      back.className = 'pz-edit-note-back';
      back.href = this.config.cartUrl;
      back.textContent = TEXTS.backToCart;
      note.append(back);
    }
    this.body.insertBefore(note, this.body.firstChild);
  }

  /** The order's revision the editor opened, sent back with the answers. */
  private orderRevision = 0;
  /** The order's answers as they opened, to tell a changed form from an untouched one. */
  private orderBaseline: string | undefined;

  /**
   * The product's own presentation wins; otherwise the shop-wide preview
   * style for this device decides (Customily's "Preview style", desktop and
   * mobile set separately): `modal` is the popup wizard, `page` and `button`
   * keep the form on the page.
   */
  private get mode(): 'inline' | 'wizard' {
    const own = this.product?.display?.mode;
    if (own === 'wizard') return 'wizard';
    if (own === 'inline') return 'inline';
    return this.themePreviewStyle() === 'modal' ? 'wizard' : 'inline';
  }

  /** The shop-wide preview style for the current device, if one is set. */
  private themePreviewStyle(): 'page' | 'modal' | 'button' | undefined {
    const theme = this.product?.theme ?? {};
    const mobile = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 768px)').matches;
    const raw = mobile ? theme.previewStyleMobile ?? theme.previewStyleDesktop : theme.previewStyleDesktop;
    return raw === 'page' || raw === 'modal' || raw === 'button' ? raw : undefined;
  }

  /**
   * Which rule the page's variation satisfies, if any — the widget decides
   * locally so an unrelated variation change never refetches the product.
   */
  private variantRuleFor(): { templateId: string; optionSetId?: string | null } | undefined {
    const rules = this.product?.variantRules ?? [];
    if (rules.length === 0) return undefined;
    const bare = (key: string): string => key.replace(/^attribute_/, '').replace(/^pa_/, '').toLowerCase();
    const picked = new Map<string, string>();
    for (const [key, value] of Object.entries(this.variation.attributes)) {
      picked.set(bare(key), String(value).toLowerCase());
    }
    return rules.find((rule) => picked.get(bare(rule.attribute)) === String(rule.value).toLowerCase());
  }

  /**
   * The variation changed. Options conditioned on it follow immediately; when
   * a rule names another template for it, the whole designer is rebuilt on
   * that template — Customily's "change template by variant".
   */
  private async applyVariation(detail: { id?: number | null; attributes?: Record<string, string> }): Promise<void> {
    if (this.config.externalOrderToken) return; // A native order's purchased variation is fixed by its saved selection.
    // A design committed for one variation was validated and priced for it. On
    // a theme that adds to cart without reloading, the next click must commit
    // again rather than reuse that id for another variation's line.
    if ((detail.id ?? null) !== this.variation.id) {
      this.committedId = undefined;
      this.writeHiddenInput('personalizy_personalization_id', '');
    }
    this.variation = { id: detail.id ?? null, attributes: detail.attributes ?? {} };
    this.form?.setVariation(this.variation);
    this.cartPrep.schedule();
    // The promise is per variation (its own stock and processing).
    this.finishPanel?.refresh();
    void this.loadFaceEta();
    if (this.mode === 'wizard') this.renderLaunchArea();
    // The variation's price: Woo prints it into the form in the same event,
    // so it is read a beat later.
    this.updateTotals();
    window.setTimeout(() => this.updateTotals(), 80);
    await this.followVariation();
  }

  /** Rebuilds the designer on the template the page's variation calls for, when it is another one. */
  private async followVariation(): Promise<void> {
    const product = this.product;
    if (!product || this.studioHosted || this.swapping) return;
    const rule = this.variantRuleFor();
    const wantedTemplate = rule?.templateId ?? this.baseTemplateId;
    const wantedSet = rule?.optionSetId ?? (rule ? product.optionSetId : this.baseOptionSetId);
    if (wantedTemplate === product.templateId && (wantedSet ?? '') === (product.optionSetId ?? '')) return;

    this.swapping = true;
    this.disableCart(true);
    try {
      const next = await this.api.loadProduct(this.config.productId, {
        templateId: this.config.templateId,
        optionSetId: this.config.optionSetId,
        variation: this.variation,
      });
      this.swapProduct(next);
    } catch {
      // The current designer keeps working; the rule simply did not apply.
    } finally {
      this.swapping = false;
      this.disableCart(false);
    }
  }

  private swapping = false;
  private baseTemplateId = '';
  private baseOptionSetId = '';

  /**
   * "Change Template": a choice may carry another template of the shop's
   * (Studio → Option sets → the choice's "loads:" select). Picking it swaps
   * the whole designer to that template, keeping the answers both share;
   * picking a choice without one returns to the template the variation calls
   * for — its rule's, or the product's own (template-switch.ts).
   */
  private maybeChangeTemplate(values: Values): void {
    const product = this.product;
    // An order keeps the template it was bought on; so does Studio's preview.
    if (!product || this.swapping || this.studioHosted) return;
    let wanted: string | undefined;
    for (const option of product.options) {
      const answer = values[option.id];
      if (answer === undefined || answer === null || answer === '') continue;
      const chosen = option.values.find((v) => v.id === answer || v.value === answer || v.label === answer);
      const templateId = chosen?.meta?.templateId;
      if (typeof templateId === 'string' && templateId) wanted = templateId;
    }
    if (!this.baseTemplateId) this.baseTemplateId = product.templateId;
    const next = templateSwitchFor({
      choiceTemplateId: wanted,
      ruleTemplateId: this.variantRuleFor()?.templateId,
      baseTemplateId: this.baseTemplateId,
      currentTemplateId: product.templateId,
    });
    if (next.kind === 'stay') return;
    // No choice names a template: the variation decides, exactly as a variation change does.
    if (next.kind === 'variation') {
      void this.followVariation();
      return;
    }
    this.swapping = true;
    void this.api
      .loadProduct(this.config.productId, {
        switchTemplateId: next.templateId,
        optionSetId: product.optionSetId,
        variation: this.variation,
      })
      .then((next) => this.swapProduct(next))
      .catch(() => undefined)
      .finally(() => {
        this.swapping = false;
      });
  }

  /**
   * Saves the product's current design to "Moji dizajni": a share snapshot on
   * the server (the same one a share link opens), and an entry in this
   * browser's list with a small thumbnail of the product preview.
   */
  private async saveToLibrary(name: string): Promise<void> {
    const product = this.product;
    if (!product) throw new Error('Sačekajte da se proizvod učita.');
    const link = await this.shareDesign();
    const id = new URL(link).searchParams.get('pz_share') ?? '';
    let thumb: string | undefined;
    try {
      const side = this.previewSide();
      if (side && this.preview && this.form) {
        const scene = applyValues(side, product.bindings, this.form.current, this.valueLabels(),
          product.libraries ?? {}, uploadTargetsOf(product.options));
        thumb = await thumbnailOf(await this.preview.snapshot(scene), 160);
      }
    } catch {
      thumb = undefined; // a design without a picture is still a saved design
    }
    const list = addLibraryEntry(readLibrary(safeStorage()), {
      id,
      name,
      url: link,
      productId: String(product.productId),
      ...(product.name ? { productName: product.name } : {}),
      at: Date.now(),
      ...(thumb ? { thumb } : {}),
    });
    if (!writeLibrary(safeStorage(), list)) throw new Error('Pregledač ne dozvoljava čuvanje spiska dizajna.');
  }

  /** Object URLs of the last two designer backdrops; older ones are released. */
  private backdropUrls: string[] = [];

  /**
   * The product picture a design question is edited on. Rendered from the
   * mockup the shopper is looking at (or the first that holds a flat copy of
   * the zone) by the same renderer as the preview, at the preview's size.
   */
  private async designBackdrop(optionId: string, values: Values): Promise<DesignerProductBackdrop | undefined> {
    const product = this.product;
    const preview = this.preview;
    if (!product || !preview) return undefined;
    const found = mockupZoneOf(product, optionId, this.previewSideId);
    if (!found) return undefined;
    const scene = applyValues(
      found.side,
      product.bindings,
      backdropValues(values, optionId),
      this.valueLabels(),
      product.libraries ?? {},
      uploadTargetsOf(product.options),
    );
    const blob = await preview.snapshot(scene);
    const url = URL.createObjectURL(blob);
    // The designer may still be showing the previous picture while this one
    // decodes, so one generation is kept alive behind the current one.
    this.backdropUrls.push(url);
    while (this.backdropUrls.length > 2) URL.revokeObjectURL(this.backdropUrls.shift()!);
    return { url, zone: found.fraction, ...(found.side.name ? { name: found.side.name } : {}) };
  }

  /** Replaces the designer with another template's, keeping shared answers. */
  private swapProduct(product: ProductDefinition): void {
    // A designer open over the old form keeps its design and reopens on the
    // new one: a colour that swaps the template must not strand the shopper in
    // an editor writing into a form that no longer exists.
    const reopen = this.form?.closeDesignerKeeping();
    const kept = this.form?.current ?? {};
    // A collage's photos go with it: the landed ones in its answer (their
    // thumbnails and file fingerprints with them), and the ones still on
    // their way are stopped here and sent again by the new form — the old
    // form's uploads would otherwise land in a form nobody sees.
    const collageMedia = this.form?.collageDraft();
    const collageUploads = this.form?.releaseCollageUploads();
    const previousCutout = this.cutout;
    // The questions the old form asked: the new form's matching ones take their answers.
    const previousOptions = this.product?.options ?? [];
    // The popup's own variation step can be what swapped the template (each
    // pillow size its own): it comes back at the step the shopper was on.
    const openStep = this.wizardOpen ? this.stepIndex : undefined;
    // Everything built for the old product goes: the wizard popup, the edit
    // overlays, the mockup strip. The preview box and canvas are reused.
    if (this.wizardLayer) {
      this.wizardLayer.destroy();
      this.wizardLayer = undefined;
      this.overlay = undefined;
    }
    // The guide belongs to the form it was explaining; the next product builds
    // its own, with that product's own settings and words.
    this.guide?.destroy();
    this.guide = undefined;
    this.editor?.clear();
    this.previewStrip?.remove();
    this.previewStrip = undefined;
    this.previewSideId = null;
    this.previewSheet = null;
    this.valueLabelCache = undefined;
    this.boundSnapshot = undefined;
    this.committedId = undefined;
    this.cartPrep.reset();
    this.heldId = undefined;
    this.photoWork.clear();
    window.clearTimeout(this.styleTimer);
    this.styleRuns.clear();
    this.styledPhotos.reset();
    this.audiosByOption.clear();
    this.lightboxLayer.close();
    if (this.previewBox.parentElement !== this.lightboxInner) {
      this.lightboxInner.insertBefore(this.previewBox, this.lightboxInner.querySelector('.pz-lightbox-done'));
    }
    this.product = product;
    this.build(product);
    // The photos already uploaded stay drawable: the new preview has to learn
    // their pixels (cut-out first, else the original) all over again — and so
    // does everything a design area put on the product, since an answer that
    // carries over to the other template must not carry over as a hole.
    for (const [assetId, original] of this.originalPhotos) {
      this.preview?.registerAsset(assetId, previousCutout?.resultFor(assetId) ?? original);
    }
    for (const [assetId, url] of this.designAssets) this.preview?.registerAsset(assetId, url);
    for (const [assetId, url] of this.collageAssets) this.preview?.registerAsset(assetId, url);
    // Answers carry to the same question on the new form: the same id, or the
    // same type and label (each colour of a product its own option set) — the
    // photos keep their slots and the texts their words (carry-answers.ts).
    const optionMap = matchOptions(previousOptions, product.options);
    const shared = carryAnswers(previousOptions, product.options, kept);
    this.form?.restoreCollageDraft(rekeyByOption(collageMedia, optionMap));
    if (Object.keys(shared).length > 0) this.form?.applyInitial(shared);
    this.form?.adoptCollageUploads(
      new Map(Object.entries(rekeyByOption(Object.fromEntries(collageUploads ?? []), optionMap))),
    );
    this.form?.setVariation(this.variation);
    this.restoreStylePhotos();
    if (this.mode === 'wizard') this.renderLaunchArea();
    if (openStep !== undefined) {
      this.openWizard();
      this.showStep(openStep);
    }
    if (reopen) this.form?.openDesigner(reopen);
  }

  /**
   * What is happening to one photo — cutting out, ready, failed — told to
   * everyone who shows it.
   *
   * The upload card shows it as a spinner over the thumbnail; the guide says it
   * in words ("Sređujem sliku…") and then points at the picture on the design,
   * which is the moment a shopper needs to be told they can drag it into place.
   */
  /**
   * The words an AI layer's prompt reads (`{{el:id}}`), from the design as it
   * stands now: each named text layer's resolved words, and which are empty.
   */
  private styleWords(target: string): { texts: Record<string, string>; missing: string[] } {
    const product = this.product;
    let prompt = '';
    for (const side of product?.sides ?? []) {
      for (const element of side.elements ?? []) if (element.id === target) prompt = aiStyleOf(element)?.prompt ?? prompt;
    }
    const fields = aiStyleFields(prompt);
    if (!product || !fields.length) return { texts: {}, missing: [] };
    const words = sceneWords(product.sides.map((side) => applyValues(side, product.bindings, this.form?.current ?? {},
      this.valueLabels(), product.libraries ?? {}, uploadTargetsOf(product.options))));
    const texts: Record<string, string> = {};
    const missing: string[] = [];
    for (const id of fields) {
      texts[id] = cleanAiStyleWords(words.get(id));
      if (!texts[id]) missing.push(id);
    }
    return { texts, missing };
  }

  /**
   * Has the AI paint this photo for its layer — with the shopper's words in
   * the prompt when the design carries them. Until those are typed the photo
   * waits as it is; the shopper is told what the picture still needs.
   */
  private runStyle(optionId: string, target: string, assetId: string, url: string, cacheOnly = false): void {
    const product = this.product;
    if (!product) return;
    const { texts, missing } = this.styleWords(target);
    this.styledPhotos.forget(optionId);
    this.styleRuns.set(optionId, { assetId, url, target, words: JSON.stringify(texts) });
    if (missing.length) {
      this.setPhotoState(optionId, { phase: 'failed', assetId, message: 'Upiši ime i ostala polja za AI dizajn.' });
      this.photoWork.add(optionId);
      return;
    }
    const bound = new Set(product.bindings.filter(b => b.optionId === optionId && b.property === 'image').map(b => b.elementId));
    const side = product.sides.find(s => s.target === 'print' && s.elements.some(e => bound.has(e.id) && aiStyleOf(e)))
      ?? product.sides.find(s => s.elements.some(e => e.id === target));
    const element = side?.elements.find(e => bound.has(e.id) && aiStyleOf(e));
    const effect = aiStyleOf(element);
    if (!side || !element || !effect) return;
    const spec = aiStyleSpec(effect, fillAiStylePrompt(effect.prompt, id => texts[id]));
    void this.styledPhotos.run({
      optionId, target, assetId, originalUrl: url, templateId: product.templateId, texts,
      key: aiPhotoKey(assetId, spec, element.transform.w, element.transform.h, side.dpi),
    }, cacheOnly);
  }

  private stylePhotoState(context: AiPhotoContext, state: AiPhotoState, cacheOnly: boolean): void {
    const { optionId, assetId, originalUrl, target } = context;
    if (state.phase === 'ready') {
      this.preview?.registerAsset(assetId, state.url);
      this.setPhotoState(optionId, { phase: 'ready', url: state.url, assetId });
      this.saveDraftNow();
      if (!cacheOnly && this.previewStyle === 'compact' && !this.wizardOpen) this.openLightbox();
    } else if (state.phase === 'working') {
      this.setPhotoState(optionId, { phase: 'cutting', assetId,
        message: cacheOnly ? 'Vraćam gotov dizajn…' : 'AI pravi dizajn… (do jednog minuta)' });
    } else {
      this.setPhotoState(optionId, { phase: 'failed', assetId, message: state.message,
        action: { label: 'Napravi dizajn', run: () => this.runStyle(optionId, target, assetId, originalUrl) } });
      // Failed AI is not a ready original photo. Keep checkout/order collection blocked.
      this.photoWork.add(optionId);
    }
    if (this.form) this.redraw(this.form.current, true);
  }

  /** Restoring or changing size is a cache read, never permission to spend. */
  private restoreStylePhotos(): void {
    for (const option of this.product?.options ?? []) {
      const target = this.styleTargetFor(option.id);
      if (!target) continue;
      const value = this.form?.current[option.id];
      const assetId = typeof value === 'string' ? value : value && typeof value === 'object' && 'assetId' in value ? String(value.assetId) : undefined;
      if (!assetId) continue;
      const url = this.originalPhotos.get(assetId) ?? this.preview?.assetUrl(assetId);
      if (url) this.runStyle(option.id, target, assetId, url, true);
    }
  }

  /**
   * Words an AI design carries were changed: the card waits at once (no cart
   * with the old name), and the picture is made only once every word is in
   * and the shopper has stopped typing — one picture per finished answer,
   * never one per pause in the middle of a name.
   */
  private restyleSoon(): void {
    if (!this.styleRuns.size) return;
    let changed = false;
    for (const [optionId, run] of this.styleRuns) {
      if (!this.form?.isAnswered(optionId)) {
        this.styleRuns.delete(optionId);
        this.styledPhotos.forget(optionId);
        continue;
      }
      const { texts, missing } = this.styleWords(run.target);
      if (JSON.stringify(texts) === run.words) continue;
      changed = true;
      this.styledPhotos.forget(optionId);
      if (!missing.length) this.setPhotoState(optionId, { phase: 'cutting', message: 'AI čeka da završiš unos…', assetId: run.assetId });
    }
    if (!changed) return;
    window.clearTimeout(this.styleTimer);
    const fire = (): void => {
      // Still in a text box: the answer is not finished yet.
      if (this.typingInForm()) {
        this.styleTimer = window.setTimeout(fire, 1000);
        return;
      }
      for (const [optionId, run] of this.styleRuns) {
        if (JSON.stringify(this.styleWords(run.target).texts) !== run.words) this.runStyle(optionId, run.target, run.assetId, run.url);
      }
    };
    this.styleTimer = window.setTimeout(fire, 2000);
  }

  /** Whether the shopper's cursor is in one of the form's text boxes. */
  private typingInForm(): boolean {
    const active = this.root.activeElement as HTMLInputElement | null;
    return !!active && (active.tagName === 'TEXTAREA' || (active.tagName === 'INPUT' && !['file', 'checkbox', 'radio', 'button'].includes(active.type)));
  }

  private setPhotoState(optionId: string, state: PhotoState): void {
    // The card turns down news about a photo it no longer shows (removed or
    // replaced while the cut-out ran); the guide must not announce it either.
    if (this.form && !this.form.setPhotoState(optionId, state)) return;
    this.guide?.photoState(optionId, { phase: state.phase, message: state.message });
    if (state.phase === 'cutting') this.photoWork.add(optionId);
    else this.photoWork.delete(optionId);
    this.cartPrep.schedule();
  }

  private build(product: ProductDefinition): void {
    // The shop-wide look, as custom properties on the host: an inline property
    // beats the stylesheet's :host defaults, and a product's own CSS beats both.
    const theme = product.theme ?? {};
    const vars: Array<[string, string | undefined]> = [
      ['--pz-accent', theme.accentColor],
      ['--pz-btn-text', theme.buttonTextColor],
      ['--pz-btn-radius', theme.buttonRadius],
      ['--pz-swatch-radius', theme.swatchShape === 'square' ? '8px' : theme.swatchShape === 'round' ? '50%' : undefined],
      ['--pz-swatch-size', theme.swatchSize],
      // Picture choices: tile size and the backdrops behind transparent art.
      ['--pz-tile-size', theme.choiceTileSize],
      ['--pz-tile-light', theme.choiceBackdropLight],
      ['--pz-tile-dark', theme.choiceBackdropDark],
      ['--pz-font-size', theme.fontSize],
      ['--pz-font', theme.fontFamily],
      ['--pz-selection', theme.selectionColor],
      ['--pz-required', theme.requiredColor],
      // Each button's own look, when the shop set one (Customily's Styles tab).
      ...(['personalize', 'share', 'preview'] as const).flatMap((b): Array<[string, string | undefined]> => [
        [`--pz-${b}-bg`, theme[`${b}Bg`]],
        [`--pz-${b}-text`, theme[`${b}Text`]],
        [`--pz-${b}-border`, theme[`${b}Border`]],
        [`--pz-${b}-border-width`, theme[`${b}BorderWidth`]],
        [`--pz-${b}-radius`, theme[`${b}Radius`]],
        [`--pz-${b}-font-size`, theme[`${b}FontSize`]],
      ]),
    ];
    for (const [name, value] of vars) {
      if (value) this.host.style.setProperty(name, value);
      else this.host.style.removeProperty(name);
    }
    this.applyBehaviourTheme(theme);
    // The shop's own styles, if the product carries any.
    this.customStyle.textContent =
      typeof product.display?.customCss === 'string' ? product.display.customCss : '';
    // The shop's own words, if the product carries any.
    applyLabels(product.display?.labels);

    this.surfacePreview?.dispose();
    this.surfacePreview = undefined;
    this.preview = new Preview({
      canvas: this.canvas,
      product,
      onRendererChange: (kind) => {
        this.badge.textContent = kind === 'skia' ? TEXTS.previewExact : TEXTS.previewApprox;
      },
      onDrawn: () => {
        for (const resolve of this.drawWaiters.splice(0)) resolve();
        // A photo just let go keeps its live stand-in until this paint shows it.
        this.editor?.painted(this.preview?.paintedScene);
        this.blitThumbs();
        this.guide?.relayout();
        this.focusSpot?.relayout();
        // The product is on screen for the first time, and it is blank where
        // the shopper's own words and pictures will go: say where to start.
        // The wizard does its own, per step.
        if (!this.suggestedStart && !this.wizardOpen) {
          this.suggestedStart = true;
          this.suggestNextQuestion();
        }
      },
    });

    if (product.surfacePreview?.modelUrl) this.surfacePreview = new SurfacePreviewController({
      config: product.surfacePreview, labels: product.display?.labels, host: this.previewBox, actions: this.previewCapActions,
      flatCanvas: this.canvas, preview: this.preview, rendererUrl: rendererUrl(),
      bundleUrl: rendererUrl().replace('personalizy-render.js', 'personalizy-3d.js'),
      scenes: () => new Map(product.sides.filter(s => s.target === 'print').map(s => [s.id, applyValues(s, product.bindings, this.form?.current ?? {}, this.valueLabels(), product.libraries ?? {}, uploadTargetsOf(product.options))])),
      onToggle: () => this.redraw(this.form?.current ?? {}, true),
    });

    this.cutout = new Cutout({
      api: this.api,
      product,
      // The same asset id now resolves to the cut-out pixels; the scene keeps
      // referring to the upload, so nothing about the commit changes.
      onCut: (assetId, url) => this.preview?.registerAsset(assetId, url),
      onState: (optionId, state) => this.setPhotoState(optionId, state),
      onAutoFrame: (optionId, assetId, face) => {
        // Framing lands only when the shopper has not framed the photo
        // themselves — the detector suggests, the hand decides.
        this.form?.setPhotoFramingIfUnset(optionId, { assetId, crop: this.cutCrop(optionId, cropForFace(face)) });
      },
      // Mini Me: the measured head is fitted into the slot's visible window.
      onHeads: (optionId, assetId, head, heads) => this.placeHead(optionId, assetId, head, heads),
      onPickHead: (optionId, assetId, heads) => {
        const url = this.originalPhotos.get(assetId);
        // Heads the detector matched to a face are offered by that face;
        // the rest by their own silhouette.
        const boxes = heads.map((h) => ({ ...(h.face ?? h.box), score: 1 }));
        if (!url) {
          void this.placeHead(optionId, assetId, largest(heads), heads);
          return;
        }
        void pickFace({ host: this.root as unknown as HTMLElement, imageUrl: url, faces: boxes }).then((face) => {
          const index = face ? boxes.indexOf(face as (typeof boxes)[number]) : -1;
          const chosen = index >= 0 ? heads[index]! : face
            ? heads.reduce((best, h) => (dist(h, face) < dist(best, face) ? h : best), heads[0]!)
            : largest(heads);
          // Picking IS the shopper's hand: it replaces the automatic placement.
          void this.placeHead(optionId, assetId, chosen, heads, true);
        });
      },
      originalUrl: (assetId) => this.originalPhotos.get(assetId),
      animation: () => {
        const value = this.product?.theme?.cutoutAnimation;
        return value === 'simple' || value === 'off' ? value : 'full';
      },
      onFaces: (optionId, assetId, faces) => {
        // The picker shows the ORIGINAL photo: the cut-out has had its
        // background removed and, in a group shot, possibly some of its people
        // with it, so it is the wrong thing to point at.
        const url = this.originalPhotos.get(assetId);
        if (!url) return;
        void pickFace({ host: this.root as unknown as HTMLElement, imageUrl: url, faces }).then(
          (face) => {
            if (!face) return; // backed out: leave the photo framed as it was
            // Framing, not a new answer: the filter and the "keep the original"
            // choice belong to this same photo and must survive the crop.
            this.form?.setPhotoFraming(optionId, { assetId, crop: this.cutCrop(optionId, cropForFace(face)) });
          },
        );
      },
      stage: () => this.previewBox,
    });

    // Restored uploads must resolve before the form builds its controls.
    for (const asset of this.editContext?.restored.assets ?? []) {
      this.preview.registerAsset(asset.id, asset.url);
      // A design restored from the cart refers to these by id; remembering them
      // is what lets the shopper share the design they came back to edit.
      this.designAssets.set(asset.id, asset.url);
    }

    // Several mockups: thumbnails under the picture switch between them.
    this.previewStrip = this.buildPreviewStrip(product);
    // Under the artwork, above the caption.
    if (this.previewStrip) this.previewBox.insertBefore(this.previewStrip, this.previewCap);

    this.form = new OptionForm({
      product,
      api: this.api,
      onDesignShare: this.previewing || this.editContext ? undefined : () => this.shareDesign(),
      formatPrice: (amount) => this.formatPrice(amount),
      onChange: (values) => {
        this.clearResolvedCartValidation();
        this.deferPreview = false;
        // A changed answer changes the design the shopper confirmed.
        this.resetProof();
        this.maybeOpenAfterUpload(values);
        this.redraw(values);
        // A ticked box may have just added a question to this screen.
        this.followSteps();
        this.settleFootError();
        // The answer may have moved a layer; realign the highlight to it.
        this.focusSpot?.relayout();
        this.syncCollageCap();
        // An answer landed. If the shopper is still typing somewhere the
        // highlight stays where it is; if they picked a swatch and let go, it
        // moves on to whatever they have not answered yet.
        this.suggestNextQuestion(this.stepOptions(), true);
        // Track the changed question now; onDrawn aligns its outline after paint.
        this.guide?.valuesChanged(values);
        this.saveDraft(values);
        this.maybeChangeTemplate(values);
        // A name the AI design carries was retyped: the picture follows.
        this.restyleSoon();
        // A photo removed while it was being cut out has nothing left to wait for.
        for (const optionId of this.photoWork) if (!this.form?.isAnswered(optionId)) this.photoWork.delete(optionId);
        this.cartPrep.schedule();
      },
      onAudioChange: (audios, optionId) => {
        this.audiosByOption.set(optionId ?? '', audios);
        this.clearResolvedCartValidation();
        this.cartPrep.schedule();
        if (this.orderEditing) this.reportPreviewState();
        // The sound is part of what the shopper confirmed.
        this.resetProof();
        // Restored sounds arrive a beat after build; the launch summary follows.
        if (this.mode === 'wizard' && !this.wizardOpen) {
          this.renderLaunchArea();
        }
      },
      onAsset: (id, url, optionId, original) => {
        if (original) this.originalSizes.set(id, original);
        this.preview?.registerAsset(id, url);
        // A fresh photo is selected on the preview, its tools showing, so the
        // shopper sees at once that it can be moved, zoomed and turned.
        if (optionId) this.editor?.select(optionId);
        // Kept because `registerAsset` will shortly re-point this id at the
        // cut-out, and the face picker needs the picture the faces were found in.
        this.originalPhotos.set(id, url);
        // The author's starting framing lands first; a detected face (below)
        // or the shopper's own drag then beats it, by the same "only if unset"
        // rule — so it never overrides a choice the shopper already made.
        if (optionId && !this.headSlotFor(optionId)) {
          // A Mini Me head slot places the measured head instead; a starting
          // crop there would only be replaced a moment later.
          const start = this.initialCropFor(optionId);
          if (start) this.form?.setPhotoFramingIfUnset(optionId, { assetId: id, crop: { ...start } });
        }
        // A fresh photo wants framing. In the wizard the live pane is already
        // on screen; on a compact inline page the lightbox opens instead, so
        // the shopper drags the photo into place without hunting for it. It
        // opens BEFORE the cut starts, so the AI cut-out plays where the
        // shopper is looking instead of in a hidden box on the page.
        // Not for an AI design: the raw photo is not what they framed or buy,
        // so the lightbox opens when the design comes back (see runStyle).
        if (this.previewStyle === 'compact' && !this.wizardOpen && !(optionId && this.styleTargetFor(optionId))) {
          this.openLightbox();
        }
        // A photo destined for a layer that removes its background is cut out
        // now, while the shopper is looking at it — not silently at print time.
        // A layer with an AI style is styled instead; the two never combine.
        if (optionId) {
          const styleTarget = this.styleTargetFor(optionId);
          if (styleTarget) {
            this.runStyle(optionId, styleTarget, id, url);
          } else {
            void this.cutout?.run(optionId, id).then(() => {
              // Force: the values did not change, only the pixels behind an id.
              if (this.form) this.redraw(this.form.current, true);
            });
          }
        }
      },
      // A picture (or its cut-out) that landed inside the designer. Only the
      // preview has to learn about it: the designer does its own framing and
      // its own background removal, so none of the photo-question pipeline —
      // resolution check, automatic cut-out, the lightbox opening — applies.
      onDesignAsset: (id, url) => {
        this.preview?.registerAsset(id, url);
        this.designAssets.set(id, url);
        if (this.form) this.redraw(this.form.current, true);
      },
      // A collage photo landed: the preview learns its draw copy — nothing
      // else. No cut-out, no style, no lightbox, no editor selection: those
      // belong to single photo questions (and some of them are paid).
      onCollageAsset: (_optionId, media) => {
        this.preview?.registerAsset(media.assetId, media.drawUrl);
        this.collageAssets.set(media.assetId, media.drawUrl);
      },
      collage: {
        render: (scene, opts) => {
          const preview = this.preview as (Preview & { renderView?: Preview['renderView'] }) | undefined;
          return typeof preview?.renderView === 'function'
            ? preview.renderView(scene, { ...opts, waiting: () => this.rendererLoads > 0 })
            : Promise.resolve(undefined);
        },
        onBoard: (_optionId, open) => {
          // The board renders the sheet itself; two renders of an A3 at once
          // is what runs a phone out of memory.
          this.previewPaused = open;
          if (!open && this.form) {
            this.redraw(this.form.current, true);
            this.syncCollageCap();
            this.cartPrep.schedule(600);
          }
        },
      },
      // The designer draws with the print renderer; a device the widget skipped
      // the upgrade for gets it when (and only when) a designer is opened.
      loadRenderer: () => this.upgradeRenderer(),
      // Zazzle designs on the product: the mockup, with every other answer
      // applied and this zone left empty, goes behind the designer's zone.
      designBackdrop: (optionId, values) => this.designBackdrop(optionId, values),
      pageOptions: this.studioHosted
        ? undefined
        : { available: () => pageOptionsAvailable(), mount: (host) => mountPageOptions(host) },
      // "Moji dizajni": a named share snapshot, listed in this browser.
      library: this.previewing || this.editContext
        ? undefined
        : {
          save: (name) => this.saveToLibrary(name),
          list: () => readLibrary(safeStorage()),
          remove: (id) => void writeLibrary(safeStorage(), removeLibraryEntry(readLibrary(safeStorage()), id)),
          suggestName: () => `${document.title.split(/\s[|–-]\s/)[0]?.trim() || 'Dizajn'} — ${new Date().toLocaleDateString('sr-RS')}`.slice(0, 80),
        },
      previewing: this.studioHosted,
      initialAudios: this.editContext?.restored.audios,
      initialAudiosOption: this.editContext
        ? restoredSoundOption(product.options, this.editContext.restored.values ?? {})
        : undefined,
      // A reload keeps the sounds too, not only the words that named them.
      draftAudios: this.editContext || this.sharedIdFromPage() ? undefined : this.draftAudios(product),
      resolveAssetUrl: (id) => this.preview?.assetUrl(id),
      // Before a picker opens and the moment a photo lands: the phone may
      // close this page while its camera app is in front.
      flushDraft: () => this.saveDraftNow(),
    });
    this.stopHubMedia?.();
    const hub=this.config.operatorMode;
    if(hub?.hubOrigin&&hub.lineId&&window.parent!==window)this.stopHubMedia=bindHubMedia({origin:hub.hubOrigin,lineId:hub.lineId,targets:()=>this.form?.hubMediaTargets()??[],importFile:async(id,file)=>{if(!this.form)throw new Error('Obrazac nije spreman.');await this.form.importHubMedia(id,file);}});
    if (this.editContext) {
      this.form.restoreCollageThumbs(this.editContext.restored.collageThumbs, this.editContext.restored.collageFramers);
      this.form.applyInitial(this.editContext.restored.values);
      this.restoreStylePhotos();
    } else {
      // A share link beats the browser's own draft; a dead link falls back to it.
      const sharedId = this.sharedIdFromPage();
      if (sharedId) {
        void this.openShared(product, sharedId).then((opened) => {
          if (!opened) this.restoreDraft(product);
        });
      } else {
        this.restoreDraft(product);
      }
      this.recoverPictures(product);
    }

    // Uploaded photos are edited on the preview itself: drag to move, pinch or
    // scroll to zoom — the framing feeds straight back into the form.
    this.editor = new CanvasEditor({
      box: this.previewBox,
      canvas: this.canvas,
      resolveUrl: (assetId) => this.preview?.assetUrl(assetId),
      // The tool bar names each photo when it holds the tools of several.
      labelFor: (optionId) => product.options.find((o) => o.id === optionId)?.label ?? '',
      // Only the shopper's own pictures are framed; a picked pin or clipart is not.
      isPhoto: (optionId) => PHOTO_OPTION_TYPES.has(product.options.find((o) => o.id === optionId)?.type ?? ''),
      // The layers over a photo (a frame, a caption, a Mini Me collar) stay on
      // top of it while it is dragged, drawn by the print renderer.
      renderLayers: (scene) => this.preview?.renderLayers(scene) ?? Promise.resolve(undefined),
      // Merged, not replaced: a photo filter chosen for this same slot must
      // survive the shopper dragging the picture into place.
      onCrop: (optionId, value) => this.form?.setPhotoFraming(optionId, value),
      // A cut-out head being dragged: the render stops drawing its old copy
      // (the stand-in shows the moving one), and gets it back on release.
      onDragging: (optionId, dragging) => {
        const ids = product.bindings
          .filter((b) => b.optionId === optionId && b.property === 'image')
          .map((b) => b.elementId);
        for (const id of ids) {
          if (dragging) this.draggingElements.add(id);
          else this.draggingElements.delete(id);
        }
        this.redraw(this.form?.current ?? {}, true);
      },
      // Where a movable layer was dragged — one map for the whole design,
      // stored beside the answers and applied by both renderers.
      onPlace: (placements) =>
        this.form?.setValue(PLACEMENT_KEY, Object.keys(placements).length ? placements : undefined),
      // Maps are answered in the field's dialog, as Customily does it. The
      // on-preview pan/zoom is a per-option extra the template author asks for.
      allowMapPan: (optionId) =>
        product.options.find((option) => option.id === optionId)?.config?.previewPan === true,
    });

    if (this.previewStyle === 'side') {
      this.body.classList.remove('pz-wrap--single');
      this.body.insertBefore(this.previewBox, this.body.firstChild);
      // On a narrow page the preview is pinned above the form; the shopper has
      // to be able to fold it away to see more than two questions at a time.
      if (!this.previewCapActions.querySelector('.pz-preview-open')) {
        this.previewCapActions.appendChild(this.makePreviewOpen());
      }
      if (!this.previewCapActions.querySelector('.pz-preview-toggle')) {
        this.previewCapActions.appendChild(this.makePreviewToggle());
      }
    } else {
      this.body.classList.add('pz-wrap--single');
      if (this.previewBox.parentElement === this.body) {
        this.lightboxInner.insertBefore(this.previewBox, this.lightboxInner.querySelector('.pz-lightbox-done'));
      }
    }

    if (this.mode === 'wizard') this.buildWizardLayout();
    else this.buildInlineLayout();

    // Last, because the bear stands beside a canvas and explains rows that both
    // have to exist first. In the wizard the form is still inside a modal that
    // has not been built yet; the guide re-checks on `relayout()`, which the
    // popup's own open and close call.
    this.startGuide(product);

    this.redraw(this.form.current, true);
    // "Uredi raspored" on the preview, when the inline form shows a collage.
    this.syncCollageCap();

    if (this.editingCart) {
      this.showEditNote();
      this.followEditBaseline();
    }

    if (this.listenersBound) return;
    this.listenersBound = true;
    if (this.orderEditing) this.listenForOrderSave();
    else this.hookCart();

    // The preview canvas is sized from its container, which changes on window
    // resize, on rotation, when a theme tab or accordion opens around us, when
    // the product gallery finishes loading, and when the wizard moves the
    // canvas into the popup. A window listener sees only the first of those.
    let resizeTimer: number | undefined;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        this.refreshViewportVars();
        this.redraw(this.form!.current, true);
      }, 150);
    });
    this.watchPreviewBox();
    this.refreshViewportVars();
    // A theme's fixed header and sticky cart bar move as the page scrolls.
    let scrollTimer: number | undefined;
    window.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => this.refreshViewportVars(), 200);
      },
      { passive: true },
    );

    // The connector bridges WooCommerce's variation events ("Sa zvukom" picked)
    // to this event; variation-conditioned options show and hide with it, and a
    // variation with its own template swaps the designer.
    document.addEventListener('personalizy:variation', (event) => {
      const detail = (event as CustomEvent).detail as
        | { id?: number | null; attributes?: Record<string, string> }
        | undefined;
      void this.applyVariation(detail ?? {});
    });
    // Two of the same design cost twice as much: the total follows Woo's
    // quantity field.
    const onQuantity = (event: Event): void => {
      const target = event.target as HTMLElement | null;
      if (target?.matches?.('form.cart input[name="quantity"], form.cart input.qty')) this.updateTotals();
    };
    document.addEventListener('input', onQuantity);
    document.addEventListener('change', onQuantity);
    // Themes' own −/+ buttons (Flatsome's ux-quantity) set the value and tell
    // only jQuery, so a click on them is read a beat later, and jQuery's own
    // change is heard where jQuery is on the page.
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('form.cart .plus, form.cart .minus, form.cart .ux-quantity__button, form.cart .quantity button')) {
        window.setTimeout(() => this.updateTotals(), 60);
      }
    });
    const jq = (window as unknown as { jQuery?: (target: unknown) => { on: (events: string, selector: string, fn: () => void) => void } }).jQuery;
    try {
      jq?.(document).on('change input', 'form.cart input.qty, form.cart input[name="quantity"]', () => this.updateTotals());
    } catch {
      // No jQuery events to hear: the native ones above still count.
    }

    // The heavier print renderer arrives once the shopper starts. A device
    // that reports very little memory keeps the light 2D preview instead —
    // the WASM engine's heap alone would push such a phone into swapping.
    if (!lowMemoryDevice()) this.whenShopperStarts(() => void this.upgradeRenderer());
  }

  /**
   * Runs `start` when the shopper starts personalizing — the first tap, key
   * or focus inside the form — or once the page has been open a while and
   * the browser is idle.
   *
   * The print renderer is about 3 MB (CanvasKit's WASM) and was fetched on
   * every product page view as soon as the form appeared: a visitor who only
   * looked at the product downloaded it, and it competed with the page's own
   * pictures while the page loaded. Until it arrives the light 2D preview
   * draws ("Pregled dizajna — približno"), as it always has meanwhile.
   */
  private whenShopperStarts(start: () => void): void {
    const kinds = ['pointerdown', 'touchstart', 'keydown', 'focusin'] as const;
    let done = false;
    let timer = 0;
    const go = (): void => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      for (const kind of kinds) this.host.removeEventListener(kind, go, true);
      start();
    };
    for (const kind of kinds) this.host.addEventListener(kind, go, { capture: true, passive: true });
    const idle = (window as { requestIdleCallback?: (fn: () => void, options?: { timeout: number }) => number }).requestIdleCallback;
    timer = window.setTimeout(() => (idle ? idle(go, { timeout: 4000 }) : go()), RENDERER_IDLE_DELAY_MS);
  }

  /**
   * Points the highlight at the question to answer next.
   *
   * The product opens blank — a layer a question drives shows nothing until it
   * is answered — so something has to say where to start, or the shopper is
   * looking at an empty poster with no idea what it wants from them. The chip
   * reads "Dodaj: <label>" and the layers that question drives are outlined,
   * exactly as if they had clicked into it. Nothing is focused: a keyboard
   * unfolding over the preview on a phone would undo the whole point.
   *
   * Required questions first, in the form's own order, then the first
   * unanswered one of any kind; with everything answered the suggestion is
   * withdrawn. `pool` narrows it to one wizard step's questions.
   */
  private suggestNextQuestion(pool?: OptionDef[], onlyWhenIdle = false): void {
    try {
      const form = this.form;
      const spot = this.focusSpot;
      if (!form || !spot) return;
      const candidates = (pool ?? form.visibleOptions()).filter(
        (option) => option.type !== 'divider' && !form.hasShopperAnswer(option.id),
      );
      const next = candidates.find((option) => option.required) ?? candidates[0];
      spot.suggest(next?.id ?? null, { onlyWhenIdle });
    } catch (err) {
      console.warn('[personalizy] could not suggest the next question', err);
    }
  }

  /**
   * The exact renderer before a picture that is kept (the cart's), waiting at
   * most `ms`: a shopper who went straight to the cart may have beaten the
   * download. The flat preview draws it after that, as before.
   */
  private async exactRenderer(ms: number): Promise<void> {
    if (!this.preview || this.preview.kind === 'skia' || lowMemoryDevice()) return;
    let timer = 0;
    await Promise.race([
      this.upgradeRenderer(),
      new Promise<void>((resolve) => (timer = window.setTimeout(resolve, ms))),
    ]);
    window.clearTimeout(timer);
  }

  /** Loads the print renderer once; a swapped-in preview reuses the loaded bundle. */
  private async upgradeRenderer(): Promise<void> {
    this.rendererLoads++;
    try {
      await this.preview?.upgrade(rendererUrl());
    } finally {
      this.rendererLoads--;
    }
  }

  /**
   * Renderer loads under way. The collage board waits for one that is on its
   * way; on a phone the page chose not to load it for (2 GB of memory) — or
   * once a load failed — it draws its stand-ins at once instead of showing
   * "Pripremam raspored…" for seconds on every open.
   */
  private rendererLoads = 0;

  /**
   * Puts the shopper guide on the page, when the shop, the product and the
   * shopper all want it there.
   *
   * Off unless somebody asked for it: the connector can forbid it shop-wide, the
   * studio turns it on per product (or the Storefront look turns it on for every
   * product), the preview frame overrides both, and the shopper's own "hide"
   * beats everything — `resolveGuideSettings` holds that order.
   *
   * The whole thing is wrapped. The guide is company, not the product: if it
   * cannot be built — an engine without `ResizeObserver`, a shadow root a page
   * builder has already moved — the shopper personalizes and buys exactly as
   * they would have, minus a bear.
   */
  private startGuide(product: ProductDefinition): void {
    const form = this.form;
    if (!form) return;

    // The highlight is company for every shopper, not only those the shop
    // gave a bear to — it starts here too, and unconditionally, so it belongs
    // to this product's own form and canvas rather than the previous one's.
    this.focusSpot?.destroy();
    this.focusSpot = undefined;
    try {
      this.focusSpot = createFocusSpot({
        form: form.element,
        previewBox: this.previewBox,
        canvas: this.canvas,
        scene: () => this.preview?.paintedScene,
        values: () => this.form?.current ?? {},
        product,
        // Full-screen inspection shows the design without any highlight.
        canvasInLightbox: () =>
          this.previewBox.parentElement === this.lightboxInner && this.lightboxLayer.isOpen,
        texts: { editing: TEXTS.editing, add: TEXTS.add },
      });
    } catch (err) {
      console.warn('[personalizy] focus highlight could not start', err);
      this.focusSpot = undefined;
    }

    try {
      const storage = guideStorage();
      const settings = resolveGuideSettings({ config: this.config, product, storage });
      // `enabled` is what the shop decided. A shopper who hid the bear keeps the
      // small chip that brings it back, and the guide renders that itself.
      if (!settings.enabled) return;
      const guide = guarded(
        createGuide({
          host: this.root,
          product,
          settings,
          form: form.element,
          canvas: this.canvas,
          previewBox: this.previewBox,
          scene: () => this.preview?.paintedScene,
          values: () => this.form?.current ?? {},
          rowFor: (id) => this.form?.rowFor(id) ?? null,
          isAnswered: (id) => this.form?.isAnswered(id) ?? false,
          formatPrice: (amount) => this.formatPrice(amount),
          // Full-screen inspection shows the design without guidance outlines.
          canvasInLightbox: () =>
            this.previewBox.parentElement === this.lightboxInner && this.lightboxLayer.isOpen,
          storage,
        }),
      );
      this.guide = guide;
      guide.mount();
    } catch (err) {
      console.warn('[personalizy] guide could not start', err);
      this.guide = undefined;
    }
  }

  // ── Drafts ──────────────────────────────────────────────────────────────────
  //
  // Answers survive a reload and a return visit — Customily's "save
  // personalization data". Kept in the browser, per product and template, for a
  // week; uploads are server assets already, so the draft only needs their ids
  // and where their pixels live. Never on a preview (Studio tests start clean)
  // and never over an edit from the cart, which carries its own answers.

  private draftTimer: number | undefined;

  private draftKey(product: ProductDefinition): string {
    return `pz-draft:${product.productId}:${product.templateId}`;
  }

  // ── Sharing ─────────────────────────────────────────────────────────────────

  /**
   * "Podeli dizajn": the answers and the photos' addresses go to the server
   * under a fresh id, and this page's own URL carries the id back. Whoever
   * opens the link finds the design as it was left, free to change or buy it.
   */
  private async shareDesign(): Promise<string> {
    const product = this.product, form = this.form;
    if (!product || !form) throw new Error('Sačekajte da se proizvod učita.');
    const { id } = await this.api.share({ productId: product.productId, templateId: product.templateId, optionSetId: product.optionSetId, values: form.current, assets: this.answerAssets() });
    const url = new URL(window.location.href);
    url.searchParams.set('pz_share', id); url.hash = '';
    return url.toString();
  }

  private makeShareRow(): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'pz-share';
    if (this.previewing || this.editContext) {
      row.hidden = true;
      return row;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pz-btn-ghost pz-share-btn';
    button.append(iconEl('link'), document.createTextNode('Podeli dizajn'));
    const note = document.createElement('span');
    note.className = 'pz-hint';
    row.append(button, note);
    button.addEventListener('click', () => {
      void (async () => {
        const product = this.product;
        const form = this.form;
        if (!product || !form) return;
        button.disabled = true;
        note.textContent = 'Pravim link…';
        try {
          const link = await this.shareDesign();

          const input = document.createElement('input');
          input.className = 'pz-input';
          input.readOnly = true;
          input.value = link;
          input.addEventListener('focus', () => input.select());
          const copy = document.createElement('button');
          copy.type = 'button';
          copy.className = 'pz-btn-ghost';
          copy.textContent = 'Kopiraj';
          copy.addEventListener('click', () => {
            void navigator.clipboard?.writeText(link).then(
              () => {
                note.textContent = 'Link je kopiran.';
              },
              () => {
                input.select();
                note.textContent = 'Označi link i kopiraj ga.';
              },
            );
          });
          row.textContent = "";
          row.append(input, copy);
          if (typeof navigator.share === 'function') {
            const send = document.createElement('button');
            send.type = 'button';
            send.className = 'pz-btn-ghost';
            send.textContent = 'Pošalji';
            send.addEventListener('click', () => {
              void navigator.share({ url: link, title: document.title }).catch(() => undefined);
            });
            row.appendChild(send);
          }
          note.textContent = 'Ko otvori link vidi ovaj dizajn i može da ga izmeni ili kupi.';
          row.appendChild(note);
        } catch (err) {
          note.textContent = err instanceof Error ? err.message : 'Deljenje nije uspelo.';
          button.disabled = false;
        }
      })();
    });
    return row;
  }

  /** The id from a share link on this page, if any. */
  private sharedIdFromPage(): string | null {
    try {
      const id = new URL(window.location.href).searchParams.get('pz_share');
      return id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ? id : null;
    } catch {
      return null;
    }
  }

  /** Restores a shared design into the form; false when the link is dead or for another product. */
  private async openShared(product: ProductDefinition, id: string): Promise<boolean> {
    try {
      const shared = await this.api.shared(id);
      if (shared.templateId !== product.templateId) return false;
      for (const [assetId, url] of Object.entries(shared.assets ?? {})) {
        if (typeof url === 'string' && /^https?:/.test(url)) {
          this.preview?.registerAsset(assetId, url);
          this.originalPhotos.set(assetId, url);
        }
      }
      this.form?.applyInitial(shared.values);
      this.restoreStylePhotos();
      this.offerStartOver('Otvoren je podeljeni dizajn. Izmeni šta želiš ili počni ispočetka.');
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Every picture the current answers point at, as a draft or a share link has
   * to carry it: asset id -> where its pixels are.
   *
   * Photo questions contribute the cut-out when there is one (that is what the
   * design shows) and the original otherwise; a design area contributes its
   * uploads, their cut-outs and the artwork picked from the catalogue. Without
   * the second half, a shopper who returned to a saved design would find the
   * layers they had drawn replaced by holes.
   */
  private answerAssets(): Record<string, string> {
    const assets: Record<string, string> = {};
    for (const [id, url] of this.originalPhotos) assets[id] = this.styledPhotos.resultFor(id) ?? this.cutout?.resultFor(id) ?? url;
    for (const [id, url] of this.designAssets) assets[id] = url;
    for (const [id, url] of this.collageAssets) assets[id] = url;
    return assets;
  }

  private saveDraft(values: Values): void {
    const product = this.product;
    if (!product || this.previewing || this.editContext) return;
    window.clearTimeout(this.draftTimer);
    this.draftTimer = window.setTimeout(() => this.writeDraft(product, values), 400);
  }

  /**
   * Saves the answers now rather than after the usual pause — before a file
   * picker or the camera opens, when a photo lands, and when the page is
   * hidden: on a phone short of memory the browser may close the page in any
   * of those moments, and a reload must find everything the shopper did.
   */
  private saveDraftNow(): void {
    const product = this.product;
    if (!product || this.previewing || this.editContext || !this.form) return;
    window.clearTimeout(this.draftTimer);
    this.writeDraft(product, this.form.current);
  }

  private writeDraft(product: ProductDefinition, values: Values): void {
    // "Počni ispočetka" is reloading the page: the page leaving must not save it back.
    if (this.startingOver) return;
    // The design is in the cart: the draft has done its job. Without this the
    // page leaving after add-to-cart (pagehide) wrote it straight back, and
    // the next visit reopened a design that was already bought. A change
    // after the add clears `committedId` and drafts resume.
    if (this.committedId) return;
    try {
      const assets = this.answerAssets();
      const answered = Object.values(values).some((v) => v !== undefined && v !== null && v !== '');
      if (!answered) {
        localStorage.removeItem(this.draftKey(product));
        return;
      }
      // The sounds, per question, without the local playback addresses: those
      // die with the page, and the stored files are what a reload plays.
      const audios: Record<string, AudioSelection[]> = {};
      for (const [optionId, list] of this.audiosByOption) {
        const kept = list.filter((sel) => sel.storageKey || sel.url || sel.title);
        if (optionId && kept.length) audios[optionId] = kept.map((sel) => ({ ...sel, previewUrl: undefined }));
      }
      // A collage's tray thumbnails, framer copies and file fingerprints (a
      // resumed or re-picked photo is never sent twice).
      const collage = this.form?.collageDraft() ?? {};
      localStorage.setItem(
        this.draftKey(product),
        JSON.stringify({ at: Date.now(), values, assets, audios, ...(Object.keys(collage).length ? { collage } : {}) }),
      );
    } catch {
      // Storage may be full or blocked; the draft is a convenience only.
    }
  }

  /**
   * After a reload: finishes the photo uploads the reload cut off (the picked
   * file was kept in IndexedDB before any processing), and says so plainly
   * when the reload came while a picker or the phone's camera was open — the
   * one case where the picture never reached the page at all.
   */
  private recoverPictures(product: ProductDefinition): void {
    if (this.studioHosted) return;
    const form = this.form;
    if (!form) return;
    const interrupted = interruptedPick(`${product.productId}:${product.templateId}`);
    // After the draft has put the other answers back.
    window.setTimeout(() => {
      void form.resumePending().catch(() => undefined);
      if (interrupted) form.noteInterruptedPick(interrupted.optionId);
    }, 50);
    if (!this.draftGuard) {
      this.draftGuard = true;
      // A page hidden (the camera app came to the front, the shopper switched
      // apps) may never be shown again: save what there is.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.saveDraftNow();
      });
      window.addEventListener('pagehide', () => this.saveDraftNow());
    }
  }

  private draftGuard = false;

  private clearDraft(): void {
    if (!this.product) return;
    // A save still waiting on its pause would write it right back.
    window.clearTimeout(this.draftTimer);
    try {
      localStorage.removeItem(this.draftKey(this.product));
    } catch {
      /* nothing to clear */
    }
  }

  /** The sounds saved in this browser's draft, per sound question, when it is still fresh. */
  private draftAudios(product: ProductDefinition): Record<string, AudioSelection[]> | undefined {
    if (this.previewing) return undefined;
    try {
      const draft = JSON.parse(localStorage.getItem(this.draftKey(product)) ?? 'null') as {
        at?: number;
        audios?: Record<string, AudioSelection[]>;
      } | null;
      if (!draft?.audios || !draft.at || Date.now() - draft.at > 7 * 86_400_000) return undefined;
      return draft.audios;
    } catch {
      return undefined;
    }
  }

  private restoreDraft(product: ProductDefinition): void {
    if (this.previewing) return;
    let draft: { at?: number; values?: Values; assets?: Record<string, string>; collage?: unknown } | null = null;
    try {
      draft = JSON.parse(localStorage.getItem(this.draftKey(product)) ?? 'null');
    } catch {
      return;
    }
    if (!draft?.values || !draft.at || Date.now() - draft.at > 7 * 86_400_000) return;
    for (const [id, url] of Object.entries(draft.assets ?? {})) {
      if (typeof url === 'string' && /^https?:/.test(url)) {
        this.preview?.registerAsset(id, url);
        this.originalPhotos.set(id, url);
      }
    }
    // Before the answers, so the collage's tray shows its thumbnails at once.
    this.form?.restoreCollageDraft(draft.collage);
    this.form?.applyInitial(draft.values);
    this.restoreStylePhotos();
    this.offerStartOver('Nastavljeno od prethodnog puta. Promeni šta želiš ili počni ispočetka.');
  }

  /**
   * Says the form opened with earlier answers, with a "Počni ispočetka"
   * button beside it. It used to say "refresh the page for a fresh start" —
   * but a refresh restores the same draft, so there was no way to start over.
   */
  private offerStartOver(message: string): void {
    this.status.textContent = message;
    this.status.className = 'pz-status is-info';
    this.status.hidden = false;
    const again = document.createElement('button');
    again.type = 'button';
    again.className = 'pz-status-action';
    again.textContent = TEXTS.startOver;
    again.addEventListener('click', () => this.startOver());
    this.status.append(' ', again);
    showToast(this.root, { message, kind: 'info', timeoutMs: 12_000, action: { label: TEXTS.startOver, onClick: () => this.startOver() } });
  }

  /**
   * Drops this browser's draft for the product (and a shared design's link)
   * and opens the page fresh. Photos still on their way are left behind with
   * the page; nothing is written back while it goes.
   */
  private startOver(): void {
    this.startingOver = true;
    this.clearDraft();
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('pz_share');
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  }

  private startingOver = false;

  private buildInlineLayout(): void {
    const right = this.body.lastElementChild as HTMLElement;
    right.innerHTML = '';
    right.className = 'pz-panel';
    right.appendChild(this.makeFormHead());
    right.appendChild(this.status);
    // Sections fold or page only on the product page; the popup wizard has its own steps.
    this.form!.groupSections();
    if (this.finishFlow()) {
      const pieceBanner = document.createElement('p');
      pieceBanner.className = 'pz-piece-banner';
      pieceBanner.hidden = true;
      right.appendChild(pieceBanner);
      window.setTimeout(() => this.renderPieceBanner(), 0);
    }
    // The variation as the form's first section, before any question.
    const variant = this.variant();
    if (variant?.active) {
      const section = document.createElement('section');
      section.className = 'pz-variant-section';
      const heading = document.createElement('h3');
      heading.className = 'pz-finish-title';
      heading.textContent = variant.title();
      section.append(heading, variant.element);
      right.appendChild(section);
    }
    right.appendChild(this.form!.element);
    if (this.previewStyle === 'compact') right.appendChild(this.makeDesignCard());
    // "Završi" as the form's last section: the date, the promise, how many.
    const finish = this.finish();
    if (finish) {
      const section = document.createElement('section');
      section.className = 'pz-finish-section';
      const heading = document.createElement('h3');
      heading.className = 'pz-finish-title';
      heading.textContent = FINISH_TEXTS.title;
      section.append(heading, finish.element);
      right.appendChild(section);
      finish.refresh();
    }
    // The price box: the product's price, each paid extra, the total.
    this.totalLine = document.createElement('div');
    this.totalLine.className = 'pz-pricebox';
    this.totalLine.hidden = true;
    right.appendChild(this.totalLine);
    // The shop's own button is hidden in this flow; this one stands in for it.
    if (finish) right.appendChild(this.makeFinishButton('pz-finish-add'));
    const proof = this.makeProofBox();
    if (proof) right.appendChild(proof);
    right.appendChild(this.makeShareRow());
    this.updateProgress();
    this.updateTotals();
  }

  // ── Proof ("I checked it") ────────────────────────────────────────────────
  //
  // A shop that wants it (Studio → the product's display → "Show a confirm
  // checkbox") gets one optional checkbox above the cart: the shopper may
  // confirm the text, photos and song are what they want printed. It unticks
  // whenever the design changes afterwards, the order records a tick for staff,
  // and it NEVER stops "Dodaj u korpu".

  private proofInput: HTMLInputElement | undefined;

  private proofWanted(): boolean {
    return this.product?.display?.proofCheck === true && !this.studioHosted;
  }

  private makeProofBox(): HTMLLabelElement | undefined {
    if (!this.proofWanted()) return undefined;
    const box = document.createElement('label');
    box.className = 'pz-proof';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'pz-proof-input';
    input.checked = this.proofInput?.checked ?? false;
    // The tick is part of the order (`values.__proof`): what was saved ahead
    // of the click has to be saved again with it.
    input.addEventListener('change', () => this.cartPrep.schedule(300));
    const text = document.createElement('span');
    text.textContent = TEXTS.proofLabel;
    box.append(input, text);
    this.proofInput = input;
    return box;
  }

  /** A change to the design after it was confirmed needs confirming again. */
  private resetProof(): void {
    if (this.proofInput?.checked) this.proofInput.checked = false;
  }

  /**
   * The inline form's heading: what this block is, and how far along the
   * shopper is. The meter counts required questions only — an optional
   * dedication must never make a finished design look unfinished.
   */
  private makeFormHead(): HTMLDivElement {
    const head = document.createElement('div');
    head.className = 'pz-head';
    const title = document.createElement('div');
    title.className = 'pz-head-title';
    title.append(iconEl('sparkle'), document.createTextNode(TEXTS.formTitle));
    head.append(title, this.makeMeter());
    return head;
  }

  /** A small "2/3 obavezno" meter, kept current by updateProgress(). */
  private makeMeter(): HTMLSpanElement {
    const meter = document.createElement('span');
    meter.className = 'pz-meter';
    meter.setAttribute('aria-live', 'polite');
    const bar = document.createElement('span');
    bar.className = 'pz-meter-bar';
    bar.setAttribute('aria-hidden', 'true');
    const fill = document.createElement('span');
    fill.className = 'pz-meter-fill';
    bar.appendChild(fill);
    const text = document.createElement('span');
    text.className = 'pz-meter-text';
    meter.append(bar, text);
    this.meters.add(meter);
    return meter;
  }

  private readonly meters = new Set<HTMLSpanElement>();

  /** Required questions on screen, and how many of them are answered. */
  private requiredProgress(): { done: number; total: number } {
    const form = this.form;
    if (!form) return { done: 0, total: 0 };
    const required = form.visibleOptions().filter((o) => o.required && o.type !== 'divider');
    return { done: required.filter((o) => form.isAnswered(o.id)).length, total: required.length };
  }

  private updateProgress(): void {
    const { done, total } = this.requiredProgress();
    for (const meter of this.meters) {
      if (!meter.isConnected) {
        this.meters.delete(meter);
        continue;
      }
      meter.hidden = total === 0;
      const complete = total > 0 && done >= total;
      meter.classList.toggle('is-done', complete);
      const fill = meter.querySelector<HTMLElement>('.pz-meter-fill');
      if (fill) fill.style.width = `${total > 0 ? Math.round((done / total) * 100) : 0}%`;
      const text = meter.querySelector<HTMLElement>('.pz-meter-text');
      if (text) {
        text.textContent = '';
        if (complete) text.append(iconEl('check'), document.createTextNode(TEXTS.ready));
        else text.textContent = TEXTS.requiredProgress(done, total);
      }
    }
  }

  // ── Wizard presentation ─────────────────────────────────────────────────────

  private buildWizardLayout(): void {
    const right = this.body.lastElementChild as HTMLElement;
    right.innerHTML = '';
    right.className = 'pz-panel';
    right.appendChild(this.status);

    this.launchArea = document.createElement('div');
    this.launchArea.className = 'pz-launch';
    right.appendChild(this.launchArea);
    // The flow's page face (the delivery line, the shop's own controls out of sight) from the start, not only once the popup has opened.
    this.variant();
    this.finish();
    // Under the design: what it all costs, right above the shop's button.
    this.totalLine = document.createElement('div');
    this.totalLine.className = 'pz-pricebox';
    this.totalLine.hidden = true;
    right.appendChild(this.totalLine);
    // Outside the launch area, which is redrawn on every answer: the box keeps
    // its own state and sits right above the shop's add-to-cart button.
    const proof = this.makeProofBox();
    if (proof) right.appendChild(proof);
    this.renderLaunchArea();
    this.updateTotals();
  }

  /** The calm face on the product page: a button, then a summary once done. */
  private renderLaunchArea(): void {
    const area = this.launchArea;
    if (!area || !this.form) return;
    area.innerHTML = '';

    // Only what the shopper did themselves counts: a preselected swatch must
    // not turn a fresh product page into "Izmeni personalizaciju".
    const answered = this.form.visibleOptions().filter((o) => this.form!.hasShopperAnswer(o.id));
    const { done, total: required } = this.requiredProgress();
    const missing = Math.max(0, required - done);
    const started = answered.length > 0;
    if (started) {
      const summary = document.createElement('div');
      summary.className = 'pz-summary';
      summary.classList.toggle('is-incomplete', missing > 0);
      const top = document.createElement('div');
      top.className = 'pz-summary-top';
      const title = document.createElement('strong');
      title.textContent = TEXTS.summaryTitle;
      const state = document.createElement('span');
      state.className = `pz-summary-state ${missing > 0 ? 'is-warn' : 'is-ok'}`;
      if (missing > 0) state.textContent = TEXTS.missingLeft(missing);
      else state.append(iconEl('check'), document.createTextNode(TEXTS.ready));
      top.append(title, state);
      summary.append(top, this.makeDesignCard());
      const lines = document.createElement('dl');
      lines.className = 'pz-summary-lines';
      for (const [label, value] of Object.entries(this.describeChoices())) {
        const line = document.createElement('div');
        line.className = 'pz-summary-line';
        // A colour answer reads as the colour, not only as its code.
        const swatch = /^#[0-9a-f]{6}$/i.test(value) ? `<span class="pz-summary-swatch" style="background:${value}"></span>` : '';
        line.innerHTML = `<dt>${escapeHtml(label)}</dt><dd>${swatch}${escapeHtml(value)}</dd>`;
        lines.appendChild(line);
      }
      if (lines.childElementCount > 0) summary.appendChild(lines);
      // What it costs is in the price box under this card.
      area.appendChild(summary);
    }

    // Before anything is answered the button is the page's call to action.
    // After a finished design the shop's own add-to-cart is the next step, so
    // "Izmeni" steps back to a secondary button instead of competing with it.
    const complete = started && missing === 0;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `pz-btn pz-launch-btn${complete ? ' is-edit' : ''}`;
    button.setAttribute('aria-haspopup', 'dialog');
    // A glyph before the words, never in them: the shop may replace the label.
    const label = document.createElement('span');
    label.textContent = !started
      ? (this.product?.display?.buttonText?.trim() || TEXTS.personalize)
      : complete
        ? TEXTS.edit
        : TEXTS.resume;
    button.append(iconEl(started ? 'pencil' : 'sparkle'), label);
    if (!complete) button.append(iconEl('chevronRight', 'pz-launch-arrow'));
    // The shop's button is hidden in this flow: a finished design is added from
    // here, with what it costs right above it (not under it, out of sight).
    if (complete && this.finishPanel) {
      if (this.totalLine) area.appendChild(this.totalLine);
      area.appendChild(this.makeFinishButton('pz-face-add'));
    } else if (this.totalLine && this.totalLine.parentElement !== area.parentElement) {
      area.after(this.totalLine);
    }
    button.addEventListener('click', () => {
      this.openWizard();
      // Coming back to an unfinished design: straight to the first gap.
      if (started && missing > 0) {
        const steps = this.wizardSteps();
        const gap = steps.findIndex((step) =>
          step.options.some((o) => this.form!.needsAnswer(o) && !this.form!.isAnswered(o.id)),
        );
        if (gap > 0) this.showStep(gap);
      }
    });
    area.appendChild(button);

    // What the popup holds, before it is opened: how long it is and that the
    // product redraws as they go. Only on the fresh page — once there is a
    // summary, the summary says everything.
    if (this.pieceRun) {
      const banner = document.createElement('p');
      banner.className = 'pz-piece-banner';
      area.insertBefore(banner, area.firstChild);
      window.setTimeout(() => this.renderPieceBanner(), 0);
    }
    if (this.faceEta) {
      const promise = document.createElement('p');
      promise.className = 'pz-face-eta';
      promise.textContent = `🚛 ${this.faceEta}`;
      area.appendChild(promise);
    }
    if (!started) {
      const meta = document.createElement('div');
      meta.className = 'pz-launch-meta';
      const count = this.wizardSteps().length;
      if (count > 0) {
        const steps = document.createElement('span');
        steps.append(iconEl('steps'), document.createTextNode(TEXTS.stepCount(count)));
        meta.appendChild(steps);
      }
      const live = document.createElement('span');
      live.append(iconEl('eye'), document.createTextNode(TEXTS.livePreview));
      meta.appendChild(live);
      area.appendChild(meta);
    }
  }

  /**
   * The wizard's screens: related questions share one screen.
   *
   * The studio can define the grouping explicitly (display.steps); without it,
   * neighbouring quick fields — the song title and the artist, say — are asked
   * together, while an upload or a sound gets a screen of its own.
   */
  private wizardSteps(): WizardStep[] {
    const steps = this.questionSteps();
    if (!this.finishFlow() || steps.length === 0) return steps;
    // The variation opens the flow (everything after it follows it); "Završi" closes it.
    const variant = this.variant();
    const first: WizardStep[] = variant?.active ? [{ title: variant.title(), options: [], variant: true }] : [];
    return [...first, ...steps, { title: FINISH_TEXTS.title, options: [], finish: true }];
  }

  private questionSteps(): WizardStep[] {
    const visible = this.form?.visibleOptions() ?? [];
    const configured = this.product?.display?.steps;

    if (Array.isArray(configured) && configured.length > 0) {
      // Grouping from the saved screens, order from the option set as published.
      return savedWizardSteps(this.product?.options ?? visible, visible, configured);
    }

    // "Quick" is what can share a screen with its neighbours. Everything else —
    // an upload, a sound, a map, and a design area, which IS a screen of its own
    // (it opens full-screen over the wizard) — gets a step to itself.
    // "Quick" is the allow-list of what may share a screen with its neighbours.
    // Everything else — an upload, a sound, a map, and a design area, which is a
    // screen of its own (it opens full-screen over the wizard through the layer
    // stack) — gets a step to itself: `design_area` is deliberately absent here.
    const quick = new Set(['text', 'textarea', 'dropdown', 'swatch', 'color', 'date', 'checkbox']);
    const steps: WizardStep[] = [];
    // A divider is not a question: it opens a new screen and names it.
    let pendingTitle: string | undefined;
    for (const option of visible) {
      if (option.type === 'divider') {
        pendingTitle = option.label;
        continue;
      }
      const last = steps[steps.length - 1];
      if (
        pendingTitle === undefined &&
        last &&
        quick.has(option.type) &&
        last.options.every((o) => quick.has(o.type)) &&
        last.options.length < 3
      ) {
        last.options.push(option);
      } else {
        steps.push({ title: pendingTitle, options: [option] });
        pendingTitle = undefined;
      }
    }
    return steps;
  }

  /** True while the step-by-step popup is on screen. */
  private get wizardOpen(): boolean {
    return this.wizardLayer?.isOpen === true;
  }

  /**
   * The questions on the screen the shopper is looking at — the current step's
   * while the wizard is up, and `undefined` (meaning "the whole form") on the
   * page. What the "answer this next" highlight is allowed to point at.
   */
  private stepOptions(): OptionDef[] | undefined {
    if (!this.wizardOpen) return undefined;
    return this.wizardSteps()[this.stepIndex]?.options;
  }

  private openWizard(): void {
    if (!this.form) return;
    if (!this.wizardLayer) this.wizardLayer = this.buildWizardModal();
    // "Izmeni personalizaciju" right after "Sačuvaj": the save toast belongs to
    // the page. Carried into the popup it sat over "Dalje", and its "Dodaj u
    // korpu" would add the design being edited to the cart.
    this.savedToast?.dismiss();
    this.savedToast = undefined;
    this.stepIndex = 0;
    // The preview moves into the popup so the shopper sees the product change
    // with every answer; it moves back to the lightbox when the popup closes.
    // First in its pane: the share row, if any, sits under the picture.
    const pane = this.overlay!.querySelector('.pz-modal-preview')!;
    pane.insertBefore(this.previewBox, pane.firstChild);
    this.wizardLayer.open();
    this.showStep(0);
    // The popup has just been put in the top layer; the canvas only has its new
    // size once the browser has laid it out.
    this.afterLayout(() => {
      this.redraw(this.form!.current, true);
      // The form and the canvas are now inside the popup — so is the bear, and
      // it has to find them where they are.
      this.guide?.relayout();
    });
  }

  private closeWizard(): void {
    this.wizardLayer?.close();
  }

  /** Whatever closed the popup — a button, Escape, the backdrop — ends here. */
  private onWizardClosed(): void {
    // A song or a voice message still playing would go on behind the page with
    // nothing left on screen to stop it.
    this.form?.stopMedia();
    // The steps are done; the button is the shopper's next stop.
    this.cartPrep.schedule(600);
    this.form?.showOnly(null);
    this.editor?.setFocus(null);
    // The step's suggestion belonged to a screen that no longer exists, and the
    // summary behind it is not the place to start over.
    this.suggestedStart = true;
    try {
      this.focusSpot?.suggest(null);
    } catch {
      /* the highlight is company, never a blocker */
    }
    // The summary's "Pogledaj dizajn" needs the canvas back in the lightbox.
    this.lightboxInner.insertBefore(
      this.previewBox,
      this.lightboxInner.querySelector('.pz-lightbox-done'),
    );
    this.renderLaunchArea();
    this.syncCollageCap();
    // The canvas went back to the lightbox and the page is on top again.
    this.guide?.relayout();
  }

  private buildWizardModal(): LayerHandle {
    const modal = document.createElement('div');
    modal.className = 'pz-modal';

    const head = document.createElement('div');
    head.className = 'pz-modal-head';
    const heading = document.createElement('div');
    heading.className = 'pz-modal-heading';
    const title = document.createElement('strong');
    // Never the Studio's own record name ("Muzicki Ram TEST (46959)" reached
    // shoppers): the page already says which product this is. A product words
    // it through the `formTitle` label.
    title.textContent = this.popupTitle();
    const stepLabel = document.createElement('span');
    stepLabel.className = 'pz-step-label';
    heading.append(title, stepLabel);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'pz-modal-close';
    close.innerHTML = ICONS.close;
    close.setAttribute('aria-label', 'Zatvori');
    close.addEventListener('click', () => this.closeWizard());

    // The stepper (see renderProgress): every step by name, what is done, what
    // still needs an answer and where the shopper is. On a phone it takes the
    // heading's place in the one top row, so the picture keeps the height.
    const progress = document.createElement('div');
    progress.className = 'pz-progress pz-steps';
    progress.setAttribute('role', 'group');
    progress.setAttribute('aria-label', TEXTS.stepsLabel);
    head.append(heading, progress, close);

    const bodyEl = document.createElement('div');
    bodyEl.className = 'pz-modal-body';
    const previewPane = document.createElement('div');
    previewPane.className = 'pz-modal-preview';
    const stepPane = document.createElement('div');
    stepPane.className = 'pz-modal-step';
    const stepTitle = document.createElement('h3');
    stepTitle.className = 'pz-step-title';
    stepTitle.hidden = true;
    const pieceBanner = document.createElement('p');
    pieceBanner.className = 'pz-piece-banner';
    pieceBanner.hidden = true;
    stepPane.append(pieceBanner, stepTitle);
    const variant = this.variant();
    if (variant?.active) {
      variant.element.hidden = true;
      stepPane.appendChild(variant.element);
    }
    stepPane.appendChild(this.form!.element);
    const finish = this.finish();
    if (finish) {
      finish.element.hidden = true;
      stepPane.appendChild(finish.element);
      this.stepPriceBox = document.createElement('div');
      this.stepPriceBox.className = 'pz-pricebox pz-step-pricebox';
      this.stepPriceBox.hidden = true;
      stepPane.appendChild(this.stepPriceBox);
    }
    // Two copies of the share row: under the picture where there is room for
    // it, at the foot of the step on a phone, whose pinned picture has none.
    // The stylesheet shows exactly one.
    previewPane.appendChild(this.makeShareRow());
    // "Završi" on a phone: the picture shrinks to a strip beside this, so the
    // date, the promise, how many and the price all fit one screen; a tap
    // brings the picture back to full size (CSS shows it only there).
    const peek = document.createElement('button');
    peek.type = 'button';
    peek.className = 'pz-finish-peek';
    const peekCopy = document.createElement('span');
    peekCopy.className = 'pz-finish-peek-copy';
    peekCopy.innerHTML = `<strong>${escapeHtml(TEXTS.peekTitle)}</strong><span>${escapeHtml(TEXTS.peekHint)}</span>`;
    const peekCta = document.createElement('span');
    peekCta.className = 'pz-finish-peek-cta';
    peekCta.textContent = TEXTS.peekOpen;
    peek.append(peekCopy, peekCta);
    peek.setAttribute('aria-expanded', 'false');
    peek.addEventListener('click', () => {
      const open = !bodyEl.classList.contains('is-peek-open');
      bodyEl.classList.toggle('is-peek-open', open);
      peek.setAttribute('aria-expanded', String(open));
      peekCta.textContent = open ? TEXTS.peekClose : TEXTS.peekOpen;
      this.afterLayout(() => this.redraw(this.form!.current, true));
    });
    previewPane.appendChild(peek);
    stepPane.appendChild(this.makeShareRow());
    bodyEl.append(previewPane, stepPane);

    const foot = document.createElement('div');
    foot.className = 'pz-modal-foot';
    // The reason "Dalje" did nothing sits beside it, where the shopper is
    // already looking, and the field itself is marked and focused. The step
    // used to repeat the same sentence under the form too, which on a short
    // step put it on screen twice.
    const footError = document.createElement('p');
    footError.className = 'pz-foot-error';
    footError.hidden = true;
    foot.appendChild(footError);
    const fail = (message: string, ids: string[]): void => {
      this.showFootError(message, ids);
      this.focusFirstMissing(ids);
    };
    const clearFail = (): void => {
      footError.hidden = true;
      this.footErrorIds = [];
    };
    const backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.className = 'pz-btn-ghost pz-modal-back';
    backBtn.setAttribute('aria-label', TEXTS.back);
    const backText = document.createElement('span');
    backText.className = 'pz-btn-text';
    backText.textContent = TEXTS.back;
    backBtn.append(iconEl('chevronLeft'), backText);
    backBtn.addEventListener('click', () => this.showStep(this.stepIndex - 1));
    const total = document.createElement('span');
    total.className = 'pz-foot-total';
    total.hidden = true;
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'pz-btn pz-modal-next';
    const nextText = document.createElement('span');
    nextText.className = 'pz-btn-text';
    nextBtn.append(nextText, iconEl('chevronRight', 'pz-next-ic'), iconEl('check', 'pz-done-ic'));
    const goNext = (): void => {
      const steps = this.wizardSteps();
      const step = steps[this.stepIndex];
      if (step?.finish) {
        clearFail();
        this.finishAdd(fail);
        return;
      }
      if (step?.variant) {
        const missing = this.variantPanel?.validate(true) ?? '';
        if (missing) {
          fail(missing, []);
          return;
        }
        clearFail();
        this.showStep(this.stepIndex + 1);
        return;
      }
      // A photo collage holds "Dalje" while slots are empty, and says so in
      // its own words. Photos still on their way do not hold it — the shopper
      // can do the texts and the sound meanwhile — except on the last step,
      // which leads to the cart, and the cart waits for them.
      const unanswered = (step?.options ?? [])
        .filter((o) => this.form!.needsAnswer(o) && !this.form!.isAnswered(o.id))
        .map((o) => o.id);
      const last = this.stepIndex >= steps.length - 1;
      const blocking = last ? unanswered : unanswered.filter((id) => !this.form!.inProgress(id));
      if (blocking.length > 0) {
        fail(this.missingText(blocking, TEXTS.fillThis), blocking);
        return;
      }
      if (last) {
        // Photos from an earlier step still on their way: the cart would
        // refuse them, so the last step waits here and says why.
        const sending = this.missingIds().filter((id) => this.form!.inProgress(id));
        if (sending.length > 0) {
          this.showFootError(this.missingText(sending, TEXTS.fillThis), sending);
          return;
        }
      } else if (unanswered.length > 0) {
        this.toast(TEXTS.stillSending, 'info');
      }
      clearFail();
      if (this.stepIndex >= steps.length - 1) {
        // Said while the popup is still up, so the shopper reads it where they
        // are rather than on a page that has just changed under them.
        this.guide?.done();
        this.closeWizard();
        // Back on the page, the next step is the shop's own button — which on
        // a phone is usually a scroll away. The toast carries it.
        if (this.editingCart && this.missingIds().length === 0) {
          // "Sačuvaj izmene" in the popup IS the save: the cart line is
          // updated now, not after a second click on the page.
          this.cartButton()?.click();
          return;
        }
        if (!this.previewing && this.missingIds().length === 0) {
          const cart = this.cartButton();
          this.savedToast = showToast(this.root, {
            message: TEXTS.saved,
            kind: 'success',
            ...(cart && this.product?.theme?.addToCartInModal !== 'true'
              ? { action: { label: TEXTS.addToCart, onClick: () => cart.click() } }
              : {}),
          });
        }
      } else this.showStep(this.stepIndex + 1);
    };
    nextBtn.addEventListener('click', goNext);
    foot.append(backBtn, total, nextBtn);

    // Enter in a one-line field means "done with this answer". Left alone it
    // would submit the shop's own cart form from inside the popup, which adds
    // an unpersonalized line to the cart.
    bodyEl.addEventListener('keydown', (event) => {
      const key = (event as KeyboardEvent).key;
      if (key !== 'Enter' || (event as KeyboardEvent).isComposing) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A') return;
      if (tag === 'INPUT' && (target as HTMLInputElement).type === 'file') return;
      if (tag !== 'INPUT' && tag !== 'SELECT') return;
      event.preventDefault();
      goNext();
    });
    // A field that the on-screen keyboard would cover is scrolled clear of it.
    bodyEl.addEventListener('focusin', (event) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) revealFocused(target);
    });
    // "Insert an add to cart button to the preview modal": the popup can end in
    // the cart directly, through the shop's own add-to-cart button.
    if (this.product?.theme?.addToCartInModal === 'true' && !this.studioHosted) {
      const cartBtn = document.createElement('button');
      cartBtn.type = 'button';
      cartBtn.className = 'pz-btn pz-modal-cart';
      cartBtn.textContent = TEXTS.addToCart;
      cartBtn.addEventListener('click', () => {
        const missing = this.form?.missingRequired() ?? [];
        if (missing.length > 0) {
          fail(
            this.missingText(this.missingIds(), (this.product?.theme?.requiredMessage ?? TEXTS.fillIn) + missing.join(', ')),
            this.missingIds(),
          );
          return;
        }
        this.closeWizard();
        const submit = document.querySelector<HTMLButtonElement>(
          'form.cart button[type="submit"], form.cart .single_add_to_cart_button',
        );
        submit?.click();
      });
      foot.appendChild(cartBtn);
    }

    // A tap on a step goes there: back always, forward only past steps whose
    // required answers are in — the same rule "Dalje" applies one step at a time.
    progress.addEventListener('click', (event) => {
      const chip = (event.target as HTMLElement).closest<HTMLElement>('.pz-step-chip');
      const target = Number(chip?.getAttribute('data-step'));
      if (!chip || !Number.isInteger(target) || target === this.stepIndex) return;
      const steps = this.wizardSteps();
      if (target > this.stepIndex) {
        for (let i = this.stepIndex; i < target; i += 1) {
          const variantMissing = steps[i]?.variant ? (this.variantPanel?.validate(true) ?? '') : '';
          if (variantMissing) {
            if (i !== this.stepIndex) this.showStep(i);
            fail(variantMissing, []);
            return;
          }
          const unanswered = (steps[i]?.options ?? [])
            .filter((o) => this.form!.needsAnswer(o) && !this.form!.isAnswered(o.id) && !this.form!.inProgress(o.id))
            .map((o) => o.id);
          if (unanswered.length > 0) {
            if (i !== this.stepIndex) this.showStep(i);
            fail(this.missingText(unanswered, TEXTS.fillThis), unanswered);
            return;
          }
        }
      }
      clearFail();
      this.showStep(target);
    });

    modal.append(head, bodyEl, foot);

    // Escape and a tap on the dim close the popup; the answers stay, so
    // reopening resumes rather than restarts. Focus goes back to the button
    // that opened it, which is where a keyboard shopper left off.
    const layer = createLayer({
      host: this.root,
      className: 'pz-overlay',
      content: modal,
      label: this.popupTitle(),
      onClose: () => this.onWizardClosed(),
    });
    this.overlay = layer.element as HTMLDivElement;
    return layer;
  }

  /** The popup's heading: "Personalizuj proizvod", or the product's `formTitle` label. */
  private popupTitle(): string {
    return TEXTS.formTitle;
  }

  /**
   * The stepper: one chip per step. The current step is a pill with its name;
   * the others are numbered dots — a check once their answers are in, an
   * amber mark for one passed with a required answer still missing. The line
   * between them fills as the shopper goes. Rebuilt only when the steps
   * change; otherwise just the states move.
   */
  private renderProgress(bar: HTMLElement | null, steps: WizardStep[]): void {
    if (!bar || !this.form) return;
    const form = this.form;
    const names = steps.map((step) => stepName(step));
    const key = names.join('|');
    if (bar.getAttribute('data-key') !== key) {
      bar.setAttribute('data-key', key);
      bar.textContent = '';
      names.forEach((name, i) => {
        if (i > 0) {
          const line = document.createElement('span');
          line.className = 'pz-step-line';
          bar.appendChild(line);
        }
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'pz-step-chip';
        chip.setAttribute('data-step', String(i));
        chip.title = name;
        const dot = document.createElement('span');
        dot.className = 'pz-step-dot';
        const num = document.createElement('span');
        num.className = 'pz-step-num';
        num.textContent = String(i + 1);
        dot.append(num, iconEl('check', 'pz-step-ok'));
        const label = document.createElement('span');
        label.className = 'pz-step-name';
        label.textContent = name;
        chip.append(dot, label);
        bar.appendChild(chip);
      });
    }
    const chips = [...bar.querySelectorAll<HTMLElement>('.pz-step-chip')];
    chips.forEach((chip, i) => {
      const step = steps[i]!;
      const current = i === this.stepIndex;
      const complete = step.options.every((o) => !form.needsAnswer(o) || form.isAnswered(o.id));
      // A step ahead is only "done" once the shopper answered something there
      // themselves; an optional step nobody has opened is still to come.
      const done = !current && complete &&
        (i < this.stepIndex || step.options.some((o) => form.hasShopperAnswer(o.id)));
      const missing = !current && !complete && i < this.stepIndex;
      chip.classList.toggle('is-current', current);
      chip.classList.toggle('is-done', done);
      chip.classList.toggle('is-missing', missing);
      if (current) chip.setAttribute('aria-current', 'step');
      else chip.removeAttribute('aria-current');
      const state = done ? ` (${TEXTS.stepDone})` : missing ? ` (${TEXTS.stepMissing})` : '';
      chip.setAttribute('aria-label', `${TEXTS.step(i + 1, steps.length)}: ${names[i]}${state}`);
      const line = chip.previousElementSibling;
      line?.classList.toggle('is-done', i <= this.stepIndex);
    });
    // The current chip stays in view when there are more steps than fit.
    const currentChip = chips[this.stepIndex];
    if (currentChip && bar.scrollWidth > bar.clientWidth) {
      bar.scrollLeft = Math.max(0, currentChip.offsetLeft - (bar.clientWidth - currentChip.offsetWidth) / 2);
    }
  }

  /** The questions the footer's "Popuni ovo polje" is about — see `settleFootError`. */
  private footErrorIds: string[] = [];

  /** Says in the popup's footer why it will not go on, about these questions. */
  private showFootError(message: string, ids: string[]): void {
    const footError = this.overlay?.querySelector<HTMLElement>('.pz-foot-error');
    if (!footError) return;
    footError.textContent = message;
    footError.hidden = false;
    this.footErrorIds = [...ids];
  }

  /**
   * The footer's "Popuni ovo polje" goes as soon as nothing it was about is
   * missing any more — the field filled, or its checkbox unticked so it is no
   * longer asked — instead of lingering until the next "Dalje".
   */
  private settleFootError(): void {
    const form = this.form;
    if (!this.wizardOpen || !form || this.footErrorIds.length === 0) return;
    const asked = new Map(form.visibleOptions().map((o) => [o.id, o] as const));
    const open = this.footErrorIds.some((id) => {
      const option = asked.get(id);
      return Boolean(option && form.needsAnswer(option) && !form.isAnswered(id));
    });
    if (open) return;
    const footError = this.overlay?.querySelector<HTMLElement>('.pz-foot-error');
    if (footError) footError.hidden = true;
    this.footErrorIds = [];
  }

  /** The screens as last drawn, and the questions on the one shown — see `followSteps`. */
  private shownStepsKey = '';
  private shownStepIds: string[] = [];

  /**
   * An answer that asks one more question ("Dodaj posvetu" showing "Tekst
   * posvete") or one less changes the wizard's screens. The screen the
   * shopper is on follows at once: left as it was drawn, the new field stayed
   * out of sight until they went Back and forward again, while "Dalje" and
   * "Sačuvaj" already refused to go on without it. They stay on the screen of
   * the question they just answered, wherever the screens now fall.
   */
  private followSteps(): void {
    if (!this.wizardOpen || !this.form) return;
    const steps = this.wizardSteps();
    if (wizardStepsKey(steps) === this.shownStepsKey) return;
    const here = new Set(this.shownStepIds);
    const index = steps.findIndex((step) => step.options.some((o) => here.has(o.id)));
    this.showStep(index >= 0 ? index : this.stepIndex, false);
  }

  /**
   * Shows wizard step `index`. `arriving` is the shopper going there; false
   * redraws the screen they are already on because its questions changed —
   * no jump back to the top, no "Korak 2" from the guide, the sound playing on.
   */
  private showStep(index: number, arriving = true): void {
    const overlay = this.overlay;
    const form = this.form;
    if (!overlay || !form) return;
    const steps = this.wizardSteps();
    const previous = this.stepIndex;
    this.stepIndex = Math.min(Math.max(index, 0), steps.length - 1);
    const step = steps[this.stepIndex];
    if (!step) return;
    this.shownStepsKey = wizardStepsKey(steps);
    this.shownStepIds = step.options.map((o) => o.id);
    // The sound step's players are hidden with it: leaving it silences them.
    // Redrawing the same step (its answers changed) leaves them playing.
    if (arriving && this.stepIndex !== previous) form.stopMedia();
    form.showOnly(step.options.map((o) => o.id));
    this.editor?.setFocus(step.options.map((o) => o.id));
    const finishing = step.finish === true;
    if (this.variantPanel) {
      this.variantPanel.element.hidden = step.variant !== true;
      if (step.variant && arriving) this.variantPanel.refresh();
    }
    if (this.finishPanel) {
      this.finishPanel.element.hidden = !finishing;
      if (finishing && arriving) this.finishPanel.refresh();
    }
    if (this.stepPriceBox) this.stepPriceBox.classList.toggle('is-off', !finishing);

    // A sound step gets the whole popup: sound never changes the artwork, and
    // the cutter needs the room far more than a static picture does.
    const audioTypes = new Set(['audio_source', 'voice_record', 'video_source']);
    const audioOnly = step.options.length > 0 && step.options.every((o) => audioTypes.has(o.type));
    const body = overlay.querySelector('.pz-modal-body') as HTMLElement;
    const wasFinish = body.classList.contains('is-finish-step');
    body.classList.toggle('is-finish-step', finishing);
    if (!finishing && body.classList.contains('is-peek-open')) {
      body.classList.remove('is-peek-open');
      const peek = overlay.querySelector('.pz-finish-peek');
      peek?.setAttribute('aria-expanded', 'false');
      const cta = peek?.querySelector('.pz-finish-peek-cta');
      if (cta) cta.textContent = TEXTS.peekOpen;
    }
    // The picture changes size between a step and "Završi" (a strip on a phone).
    if (wasFinish !== finishing) this.afterLayout(() => this.redraw(this.form!.current, true));
    const wasAudio = body.classList.contains('is-audio-step');
    body.classList.toggle('is-audio-step', audioOnly);
    // Coming back from a sound step, the canvas returns at a fresh size.
    if (wasAudio && !audioOnly) {
      this.afterLayout(() => this.redraw(this.form!.current, true));
    }

    const titleEl = overlay.querySelector('.pz-step-title') as HTMLElement;
    titleEl.textContent = step.title ?? '';
    titleEl.hidden = !step.title;

    const stepLabel = overlay.querySelector('.pz-step-label') as HTMLElement;
    stepLabel.textContent = TEXTS.step(this.stepIndex + 1, steps.length);
    this.renderProgress(overlay.querySelector('.pz-progress') as HTMLElement, steps);
    const back = overlay.querySelector('.pz-modal-back') as HTMLButtonElement;
    back.disabled = this.stepIndex === 0;
    back.classList.toggle('is-first', this.stepIndex === 0);
    const last = this.stepIndex >= steps.length - 1;
    const next = overlay.querySelector('.pz-modal-next') as HTMLButtonElement;
    next.classList.toggle('is-last', last);
    body.classList.toggle('is-last-step', last);
    // One question, named by the stepper already: a phone skips its label.
    body.classList.toggle('is-single', step.options.length === 1 && !step.title);
    next.classList.toggle('is-cart', finishing);
    (next.querySelector('.pz-btn-text') as HTMLElement).textContent = last
      ? (this.editingCart ? TEXTS.saveEdit : finishing ? TEXTS.addToCart : TEXTS.done)
      : TEXTS.next;
    if (finishing) this.syncFinishLabel();
    this.renderPieceBanner();
    this.updateTotals();
    this.syncCollageCap();
    if (!arriving) return;
    // A new step starts at its top, not wherever the previous one was scrolled to.
    for (const pane of overlay.querySelectorAll<HTMLElement>('.pz-modal-body, .pz-modal-step')) {
      pane.scrollTop = 0;
    }
    // The status line lives on the page panel, not in the popup, in the stepped
    // layout; an unguarded lookup threw here and cut the rest of the step short.
    const status = overlay.querySelector<HTMLElement>('.pz-status');
    if (status) status.hidden = true;
    const footError = overlay.querySelector('.pz-foot-error') as HTMLElement | null;
    if (footError) footError.hidden = true;
    this.footErrorIds = [];
    // Where the shopper is, in the guide's own words: "Korak 2: Slika" — and
    // then what this step's question actually changes on the design.
    this.guide?.step({
      index: this.stepIndex,
      total: steps.length,
      title: step.title,
      optionIds: step.options.map((o) => o.id),
    });
    // Every step arrives with its own question still unanswered, so every step
    // points at it — on this screen only.
    this.suggestNextQuestion(step.options);
  }

  // ── Shared behaviour ────────────────────────────────────────────────────────

  /** What the scene last rendered from — changes to anything else skip the render. */
  private boundSnapshot: string | undefined;

  private redraw(values: Values, force = false): void {
    // "Load preview by default: no" — nothing is drawn until the shopper answers.
    if (this.deferPreview && !force) return;
    const product = this.product;
    if (!product || !this.preview) return;
    // A change invalidates whatever was committed before it.
    this.committedId = undefined;
    this.updateTotals();

    // Only bound answers reach the artwork. A sound choice, a checkbox with no
    // binding — rendering for those burns hundreds of milliseconds of WASM work
    // to draw an identical image, which is exactly the jank shoppers feel.
    if (this.studioHosted) this.reportPreviewState();
    // The collage board draws the sheet itself; the page catches up on close.
    if (this.previewPaused) return;

    const bound = product.bindings.map((b) => [b.optionId, values[b.optionId]]);
    // A dragged layer changes the picture without changing any answer.
    bound.push([PLACEMENT_KEY, values[PLACEMENT_KEY]]);
    const snapshot = JSON.stringify(bound);
    if (!force && snapshot === this.boundSnapshot) return;
    this.boundSnapshot = snapshot;

    if (this.surfacePreview?.active) { this.surfacePreview.update(); return; }
    const side = this.previewSide();
    if (side) {
      if (this.lightboxLayer.isOpen) this.sizeLightbox();
      const applied = applyValues(
        side,
        product.bindings,
        values,
        this.valueLabels(),
        product.libraries ?? {},
        uploadTargetsOf(product.options),
      );
      const drawn = this.draggingElements.size
        ? { ...applied, elements: applied.elements.map((el) => (this.draggingElements.has(el.id) ? { ...el, visible: false } : el)) }
        : applied;
      void this.preview
        .draw(drawn)
        .then(() => {
          // Overlay geometry depends on the drawn canvas size, so it settles after.
          this.editor?.update(applied, product.bindings, this.form?.current ?? {});
          // The photo overlays may have just appeared; the hint follows them.
          if (this.lightboxLayer.isOpen) {
            this.lightboxHint.hidden = !this.previewBox.querySelector('.pz-edit');
            this.sizeLightbox();
          }
        })
        .catch((err: unknown) => this.previewFailed(err, snapshot));
    }
  }

  /** Whether a failed preview render has been reported on this page yet. */
  private previewFailureLogged = false;

  /**
   * A render that failed — a picture that would not decode, the renderer
   * itself — must not become an uncaught page error: the form, the price and
   * add to cart keep working, and the next answer draws again. Reported once
   * per page, not once per keystroke.
   */
  private previewFailed(err: unknown, snapshot: string): void {
    // Nothing was drawn for these answers, so the same answers may try again.
    if (this.boundSnapshot === snapshot) this.boundSnapshot = undefined;
    if (this.previewFailureLogged) return;
    this.previewFailureLogged = true;
    console.warn('[personalizy] the preview could not be drawn', err);
  }

  /**
   * Option value id -> its stored text, built from the product definition.
   *
   * Layout swatches bind per choice, and the answer is the choice's text, so the
   * two have to be matched up. Cached because it cannot change while the shopper
   * is on the page.
   */
  private valueLabelCache: Record<string, string> | undefined;

  private valueLabels(): Record<string, string> {
    if (this.valueLabelCache) return this.valueLabelCache;
    const labels: Record<string, string> = {};
    for (const option of this.product?.options ?? []) {
      for (const value of option.values ?? []) labels[value.id] = value.value;
    }
    this.valueLabelCache = labels;
    return labels;
  }

  /**
   * Keeps every price on the page honest as answers change: the price box
   * (product, extras, total, the saving) and the wizard footer's total.
   */
  private updateTotals(): void {
    const lines = this.form?.priceLines() ?? [];
    const total = lines.reduce((sum, line) => sum + line.amount, 0);
    // The product's own price only on a real product page: Studio's preview
    // has no WooCommerce price to read.
    // Hub operator: no Woo page to read, the Hub sends the line's unit price and quantity.
    const operator = this.config.operatorMode;
    const page = operator?.basePrice != null ? { price: operator.basePrice, regular: operator.basePrice } : this.studioHosted ? null : readPagePrice(this.config.currency, this.variation.id);
    const quantity = operator?.quantity ?? (this.studioHosted ? 1 : readQuantity());
    // On the product page the box stands right above the quantity and the
    // shop's add-to-cart — under everything else the form holds (the gift
    // date, too) — so the last thing read before the button is the price.
    // In the "Završi" flow the shop's controls are hidden and the widget has
    // its own button: the box stays in the widget, right above that button
    // (placed there by the layouts), never under it.
    // Decided by the flow itself (known from the start), not by whether the
    // step is built yet: a box put under the button by an early call would stay.
    const inFlow = this.finishFlow() && !this.finishFailed;
    const outside = inFlow ? undefined : this.priceBoxBeforeCart();
    if (inFlow && this.priceHost) {
      this.priceHost.host.remove();
      this.priceHost = undefined;
    }
    // A pieces deal (connector 0.12) reached by this quantity: the cart applies
    // it to any add, the classic form's too, so the box shows it in every mode.
    // In a run of different pieces each is added alone, at the step the whole run reaches.
    const dealPercent = this.studioHosted || operator ? undefined : dealStep(this.config.deal?.tiers, this.pieceRun?.total ?? quantity)?.percent;
    const summary = { page, lines, quantity, format: (amount: number) => this.formatPrice(amount), ...(dealPercent ? { dealPercent } : {}) };
    if (this.totalLine) {
      if (outside) {
        this.totalLine.hidden = true;
        this.totalLine.innerHTML = '';
      } else {
        renderPriceSummary(this.totalLine, summary);
      }
    }
    if (outside) renderPriceSummary(outside, summary);
    // The popup's last step shows the same box above its "Dodaj u korpu".
    if (this.stepPriceBox) renderPriceSummary(this.stepPriceBox, summary);
    this.finishPanel?.refreshDeal();
    // The stepper's checks follow the answers, not only the step changes.
    if (this.wizardOpen) {
      this.renderProgress(this.overlay?.querySelector('.pz-progress') ?? null, this.wizardSteps());
    }
    const footTotal = this.overlay?.querySelector('.pz-foot-total') as HTMLElement | undefined;
    if (footTotal) {
      if (page) {
        // The whole price, with the regular one struck through on a sale or a deal.
        const totals = priceTotals(summary);
        const now = totals.total ?? (page.price + total) * quantity;
        const before = totals.regularTotal ?? (page.regular + total) * quantity;
        footTotal.hidden = false;
        const off = before > now + 0.004 ? Math.round(((before - now) / before) * 100) : 0;
        footTotal.innerHTML =
          `<small><span class="pz-foot-label">${escapeHtml(TEXTS.grandTotal)}</span>${before > now + 0.004 ? ` <del>${escapeHtml(this.formatPrice(before))}</del>` : ''}</small>` +
          `<b>${off >= 1 ? `<em class="pz-foot-save">−${off}%</em> ` : ''}${escapeHtml(this.formatPrice(now))}</b>`;
      } else {
        footTotal.hidden = total <= 0;
        footTotal.innerHTML =
          total > 0 ? `<small><span class="pz-foot-label">${escapeHtml(TEXTS.totalLabel)}</span></small><b>+${escapeHtml(this.formatPrice(total))}</b>` : '';
      }
    }
    this.updateProgress();
  }

  /** The price box's own host before the cart, and the box inside it. */
  private priceHost: { host: HTMLElement; box: HTMLDivElement } | undefined;

  /**
   * The price box placed just above the shop's quantity field (or its
   * add-to-cart button), in a shadow root of its own with the widget's styles
   * and the shop's colours. Undefined in Studio's preview and where the page
   * has no cart button — the box then stays under the form.
   */
  private priceBoxBeforeCart(): HTMLDivElement | undefined {
    if (this.studioHosted) return undefined;
    const button = document.querySelector<HTMLElement>(this.config.cartButton);
    const form = this.host.closest('form') ?? button?.closest('form') ?? document.querySelector('form.cart');
    const anchor = form?.querySelector<HTMLElement>('.quantity') ?? button;
    if (!anchor?.parentElement) return undefined;
    let current = this.priceHost;
    if (!current || !current.host.isConnected) {
      const host = document.createElement('div');
      host.className = 'personalizy-price';
      // A theme's flex cart row must give it a line of its own.
      host.style.cssText = 'display:block;width:100%;flex:0 0 100%;margin:0 0 14px;';
      const root = host.attachShadow({ mode: 'open' });
      const style = document.createElement('style');
      style.textContent = STYLES;
      const box = document.createElement('div');
      box.className = 'pz-pricebox';
      box.hidden = true;
      root.append(style, box);
      current = { host, box };
      this.priceHost = current;
    }
    if (current.host.nextElementSibling !== anchor) anchor.parentElement.insertBefore(current.host, anchor);
    // The shop's colours and fonts, as the widget's own host carries them.
    for (let i = 0; i < this.host.style.length; i++) {
      const name = this.host.style.item(i);
      if (name.startsWith('--pz-')) current.host.style.setProperty(name, this.host.style.getPropertyValue(name));
    }
    return current.box;
  }

  private formatPrice(amount: number): string {
    const { symbol, decimals, decimalSep, thousandSep } = this.config.currency;
    const fixed = amount.toFixed(decimals);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole!.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
    return `${grouped}${fraction ? decimalSep + fraction : ''} ${symbol}`.trim();
  }

  private cartButton(): HTMLButtonElement | null {
    return document.querySelector<HTMLButtonElement>(this.config.cartButton);
  }

  private disableCart(disabled: boolean): void {
    const button = this.cartButton();
    if (!button) return;
    button.disabled = disabled;
    button.classList.toggle('pz-disabled', disabled);
  }

  /**
   * Every message goes two places on purpose.
   *
   * The status line keeps the message next to the form, where it belongs when
   * the shopper is looking at the form. The toast is anchored to the visible
   * viewport, because the shopper may be at the bottom of a long page, inside
   * the wizard, or looking at a theme's sticky add-to-cart bar — in all of
   * which the status line is somewhere off screen.
   */
  private toast(message: string, kind: 'error' | 'info' | 'success', timeoutMs?: number): ToastHandle {
    return showToast(this.root, { message, kind, ...(timeoutMs === undefined ? {} : { timeoutMs }) });
  }

  private showError(message: string): void {
    this.cartValidationError = false;
    this.status.textContent = message;
    this.status.className = 'pz-status is-error';
    this.status.hidden = false;
    // Whatever we were saving is over, one way or another.
    this.savingToast?.dismiss();
    this.savingToast = undefined;
    this.toast(message, 'error');
  }

  private showInfo(message: string): void {
    this.status.textContent = message;
    this.status.className = 'pz-status is-info';
    this.status.hidden = false;
    // "Čuvanje…" has no end of its own: it stays until the commit resolves.
    if (message === TEXTS.saving || message === TEXTS.adding) {
      this.savingToast?.dismiss();
      this.savingToast = this.toast(message, 'info', 0);
    } else {
      this.toast(message, 'info');
    }
  }

  private clearStatus(): void {
    this.status.hidden = true;
    this.savingToast?.dismiss();
    this.savingToast = undefined;
  }

  private clearResolvedCartValidation(): void {
    if (this.cartValidationError && !(this.form?.missingRequired().length)) {
      this.cartValidationError = false;
      this.clearStatus();
    }
  }

  /**
   * Intercepts add-to-cart so the personalization is committed first.
   *
   * The form submit is paused, not cancelled: once the id is in the hidden input,
   * the same submit is replayed so WooCommerce and every other plugin on the page
   * behave exactly as they normally would.
   */
  private hookCart(): void {
    const button = this.cartButton();
    const form = button?.closest('form');
    if (!form) return;

    // A hand on its way to the button: whatever is not saved yet starts now,
    // not after the pause.
    const reaching = (): void => this.cartPrep.now();
    button!.addEventListener('pointerenter', reaching);
    button!.addEventListener('touchstart', reaching, { passive: true });
    button!.addEventListener('focus', reaching);

    // Back from the cart may restore this page from memory, mid-add as it was
    // left: the buttons work again and nothing claims to be adding.
    window.addEventListener('pageshow', (event) => {
      if (!(event as PageTransitionEvent).persisted) return;
      this.committing = false;
      button!.classList.remove('loading');
      this.disableCart(false);
      this.setFinishBusy(false);
      this.clearStatus();
    });

    form.addEventListener(
      'submit',
      (event) => {
        if (this.committing) { event.preventDefault(); return; }
        if (this.form?.collageSending()) {
          event.preventDefault();
          button!.classList.remove('loading');
          window.setTimeout(() => { if (!this.committing) button!.classList.remove('loading'); }, 0);
          this.showInfo('Sačekaj da se sve slike pošalju i sačuvaju.');
          return;
        }
        if (this.committedId) return;

        // Personalization the shop made optional: an untouched form lets the
        // plain product through, with nothing committed for the line.
        if (this.untouchedOptional()) {
          const cartAdd = this.ajaxCart();
          if (!cartAdd) return;
          event.preventDefault();
          this.committing = true;
          this.disableCart(true);
          for (const name of PERSONALIZATION_INPUTS) this.writeHiddenInput(name, '');
          this.carryProductId(form, button!);
          void this.addByAjax(form, button!, cartAdd).finally(() => { this.committing = false; this.setFinishBusy(false); });
          return;
        }

        // An edit that changed nothing has nothing to save: back to the cart,
        // exactly as the shopper left it.
        if (this.editingCart && this.config.cartUrl && this.editUnchanged()) {
          event.preventDefault();
          this.showInfo(TEXTS.unchanged);
          window.location.assign(this.config.cartUrl);
          return;
        }

        const missing = this.form?.missingRequired() ?? [];
        if (missing.length > 0) {
          event.preventDefault();
          // Themes can mark the button during click or a later submit handler.
          button!.classList.remove('loading');
          window.setTimeout(() => { if (!this.committing) button!.classList.remove('loading'); }, 0);
          if (this.mode === 'wizard') {
            // The popup is where those fields live — reopen it at the first gap.
            this.openWizard();
            const steps = this.wizardSteps();
            const firstMissing = steps.findIndex((step) =>
              step.options.some((o) => this.form!.needsAnswer(o) && !this.form!.isAnswered(o.id)),
            );
            if (firstMissing >= 0) this.showStep(firstMissing);
            // A collage still sending or with empty slots says so where the
            // shopper lands (the generic gap needs no words: its step shows it).
            const note = this.missingText(this.missingIds(), '');
            if (note) this.showFootError(note, this.missingIds());
          } else {
            this.showError(this.missingText(this.missingIds(), (this.product?.theme?.requiredMessage ?? TEXTS.fillIn) + missing.join(', ')));
            // The field, not the widget: scrolling to the top of a long form
            // leaves the shopper hunting for which question they skipped.
            if (!this.focusFirstMissing(this.missingIds())) {
              this.host.scrollIntoView({ block: 'center' });
            }
          }
          this.cartValidationError = true;
          return;
        }

        // The delivery date is checked before anything is saved: a day the shop
        // cannot deliver used to fail only after the design had been committed.
        if (this.finishPanel && this.ajaxCart() && this.finishPanel.validate()) {
          event.preventDefault();
          button!.classList.remove('loading');
          this.showFinish();
          return;
        }

        // The proof box never holds the cart up (the shop's rule: nothing
        // optional stands between a shopper and "Dodaj u korpu"). Ticked, the
        // order records the approval; unticked, the order simply goes through.

        event.preventDefault();
        this.committing = true;
        this.disableCart(true);

        // WooCommerce's add-to-cart button carries the product id in its own
        // name/value pair, which a programmatic form.submit() leaves out — the
        // POST then reaches WooCommerce and silently adds nothing. Whatever
        // button triggered this submit, its pair is preserved as a hidden input.
        const submitter = (event as SubmitEvent).submitter;
        if (submitter instanceof HTMLButtonElement && submitter.name) {
          let carrier = form.querySelector<HTMLInputElement>(
            `input[type="hidden"][name="${submitter.name}"]`,
          );
          if (!carrier) {
            carrier = document.createElement('input');
            carrier.type = 'hidden';
            carrier.name = submitter.name;
            form.appendChild(carrier);
          }
          carrier.value = submitter.value;
        }
        // Browsers without `submitter` (iOS < 15.4, Chrome < 81) and a click
        // that came from our own buttons: the simple product's id all the same.
        this.carryProductId(form, button!);

        void (async () => {
          // A click with nothing left to do says nothing. Only one that has to
          // do the work itself (the design changed a moment ago, or the saving
          // ahead failed) shows a line — and only once it takes long enough to
          // be noticed.
          const slow = window.setTimeout(() => this.showInfo(this.previewing || this.config.externalOrderToken ? TEXTS.saving : TEXTS.adding), 700);
          try {
            const designSignature = this.designSignature();
            // A save of this very design still under way is waited for, but not
            // forever: a request stuck on a bad connection is overtaken by the
            // click's own commit.
            let result = this.previewing
              ? undefined
              : await Promise.race([
                  this.cartPrep.take(designSignature),
                  new Promise<undefined>((resolve) => window.setTimeout(() => resolve(undefined), 10_000)),
                ]);
            // Changed while a save of it was finishing: do the work now instead.
            if (result && this.designSignature() !== designSignature) result = undefined;
            if (result) {
              // Saved ahead of the click; it goes into the cart now.
              this.api.release(result.id);
            } else {
              const values = structuredClone(this.form!.current);
              const variation = structuredClone(this.form!.currentVariation);
              const audios = this.decorateAudios();
              const signature = this.answerSignature();
              let cartPreviewAssetId: string | undefined;
              try {
                cartPreviewAssetId = await this.cartPicture(values, variation);
              } catch {
                // The cart picture is for show; the order never waits on it. The
                // cart falls back to the server's own preview once it is drawn.
                cartPreviewAssetId = undefined;
              }
              if (signature !== this.answerSignature()) {
                throw new Error('Personalizacija je promenjena. Klikni ponovo na dodavanje u korpu.');
              }
              if (this.commitSignature !== undefined && this.commitSignature !== signature) this.commitKey = newCommitKey();
              this.commitSignature = signature;
              result = await this.api.commit(this.commitBody(values, variation, audios, cartPreviewAssetId, this.commitKey));
              if (signature !== this.answerSignature()) {
                throw new Error('Personalizacija je promenjena. Klikni ponovo na dodavanje u korpu.');
              }
            }
            window.clearTimeout(slow);
            this.committedId = result.id;
            // What was ordered is in the cart now; the draft has done its job.
            // The AJAX add keeps it until the cart said yes: a failed add
            // (no connection, a refusal) must not lose the shopper's design.
            if (this.previewing || !this.ajaxCart()) this.clearDraft();
            this.guide?.done();
            if (this.previewing) {
              // Nothing to add to a cart — hand the result to Studio, which shows
              // the production files as they come out of the worker.
              this.showInfo(TEXTS.testSaved);
              this.post({
                type: 'pz:committed',
                id: result.id,
                priceDelta: result.priceDelta,
                playerUrl: result.playerUrl,
              });
              this.disableCart(false);
              this.committing = false;
              return;
            }
            this.writeHiddenInput('personalizy_personalization_id', result.id);
            this.writeHiddenInput('personalizy_price_delta', String(result.priceDelta));
            // Bundled products ride along for the connector to add beside this line.
            this.writeHiddenInput(
              'personalizy_extra_products',
              result.extraProducts?.length ? JSON.stringify(result.extraProducts) : '',
            );
            this.writeHiddenInput(
              'personalizy_properties',
              JSON.stringify(this.describeChoices()),
            );
            // An edit replaces its cart line instead of adding a second one.
            if (this.editContext?.cartKey && /^[a-f0-9]{32}$/i.test(this.editContext.cartKey)) {
              let carrier = form.querySelector<HTMLInputElement>(
                'input[name="personalizy_replace_cart_key"]',
              );
              if (!carrier) {
                carrier = document.createElement('input');
                carrier.type = 'hidden';
                carrier.name = 'personalizy_replace_cart_key';
                form.appendChild(carrier);
              }
              carrier.value = this.editContext.cartKey;
            }
            // This design is on its way into the cart; anything the shopper adds
            // after it is a new order, not a retry of this one.
            this.commitKey = newCommitKey();
            this.cartPrep.reset();
            // Bought: the next design starts a fresh chain, never replacing this one.
            this.heldId = undefined;
            this.clearStatus();
            const cartAdd = this.ajaxCart();
            if (cartAdd) await this.addByAjax(form, button!, cartAdd);
            else form.submit();
          } catch (err) {
            window.clearTimeout(slow);
            this.showError(err instanceof Error ? err.message : TEXTS.failed);
            button!.classList.remove('loading');
            this.disableCart(false);
          } finally {
            this.committing = false;
            this.setFinishBusy(false);
          }
        })();
      },
      // Capture, so this runs before theme scripts that also handle submit.
      true,
    );
  }

  // ── "Završi": the date, the promise, how many (finish.ts) ─────────────────

  private finishPanel: FinishPanel | undefined;
  /** "Stiže 3. ili 4. novembra ako poručiš danas", for the page face; '' until known. */
  private faceEta = '';

  /** The new product-page flow runs where the add stays on the page (connector setting). */
  private finishFlow(): boolean {
    return !!this.ajaxCart() && !this.studioHosted;
  }

  private finish(): FinishPanel | undefined {
    if (!this.finishFlow() || this.finishFailed) return undefined;
    if (!this.finishPanel) {
      const form = this.cartButton()?.closest('form') ?? document.querySelector<HTMLFormElement>('form.cart');
      try {
        this.finishPanel = createFinishPanel({
          form,
          allowPieces: !this.editingCart,
          onPieceMode: () => this.syncFinishLabel(),
          onStopRun: () => this.stopPieces(),
          ...(this.config.deal?.tiers?.length ? { deal: this.config.deal } : {}),
          deliveryConfig: (window as unknown as { personalizyDeliveryConfig?: unknown }).personalizyDeliveryConfig,
          productId: () => this.etaProductId(),
          onQuantity: () => this.updateTotals(),
        });
      } catch {
        // Without the step the page keeps its own date, quantity and button.
        this.finishFailed = true;
        return undefined;
      }
      hidePageCartChrome(form, this.cartButton());
      void this.loadFaceEta();
      // A run of different pieces left by a reload (the answers come back with the draft).
      const saved = readPieceRun(this.config.productId);
      if (saved && !this.editingCart) {
        this.pieceRun = saved;
        setQuantityInput(form, 1);
        this.finishPanel.setRun(saved);
      }
    }
    return this.finishPanel;
  }

  private variantPanel: VariantPanel | undefined;
  private variantFailed = false;
  private finishFailed = false;

  /** The variation step: only in this flow, only on a variable product with something to choose. */
  private variant(): VariantPanel | undefined {
    if (!this.finishFlow()) return undefined;
    if (!this.variantPanel) {
      if (this.variantFailed || !document.querySelector('form.variations_form')) return undefined;
      try {
        this.variantPanel = createVariantPanel({
          format: (amount) => this.formatPrice(amount),
          onPick: () => this.updateTotals(),
        });
      } catch {
        // The step is a convenience: without it the page's own swatches stay.
        this.variantFailed = true;
        return undefined;
      }
      // The page's own swatches give way to the step.
      if (this.variantPanel.active) document.querySelector('form.variations_form')?.classList.add('pz-flow-variants');
    }
    return this.variantPanel;
  }

  /** The variation picked, else the product: what the delivery promise is for. */
  private etaProductId(): number {
    const picked = Number(document.querySelector<HTMLInputElement>('form.cart input[name="variation_id"]')?.value);
    return picked > 0 ? picked : Number(this.config.productId) || 0;
  }

  private async loadFaceEta(): Promise<void> {
    if (!this.finishPanel || this.mode !== 'wizard') return;
    const line = await this.finishPanel.headline();
    if (line === this.faceEta) return;
    this.faceEta = line;
    this.renderLaunchArea();
  }

  /** "Dodaj u korpu" of this flow (page face, inline form): the shop's own button, which is hidden. */
  private makeFinishButton(className: string): HTMLButtonElement {
    const add = document.createElement('button');
    add.type = 'button';
    add.className = `pz-btn ${className}`;
    add.textContent = this.finishLabel();
    add.addEventListener('click', () => {
      if (this.committing) return;
      if (this.variationBlocked()) return;
      this.startPieces();
      this.setFinishBusy(true);
      this.cartButton()?.click();
      if (!this.committing) this.setFinishBusy(false);
    });
    return add;
  }

  /** The popup's last step: everything answered, a deliverable date, then the cart. */
  private finishAdd(fail: (message: string, ids: string[]) => void): void {
    const missing = this.missingIds();
    if (missing.length > 0) {
      const steps = this.wizardSteps();
      const gap = steps.findIndex((step) => step.options.some((o) => missing.includes(o.id)));
      if (gap >= 0) this.showStep(gap);
      fail(this.missingText(missing, TEXTS.fillThis), missing);
      return;
    }
    if (this.finishPanel?.validate()) return;
    if (this.variationBlocked()) return;
    const cart = this.cartButton();
    if (!cart || this.committing) return;
    this.startPieces();
    this.setFinishBusy(true);
    cart.click();
    if (!this.committing) this.setFinishBusy(false);
  }

  /**
   * WooCommerce holds its own button while a variable product has no complete
   * variation picked (it would only alert, and the hidden button says nothing
   * in this flow). Said here instead, and the shopper is taken to the choice
   * on the page. Until the variation is a step of its own (plan phase 3).
   */
  private variationBlocked(): boolean {
    const button = this.cartButton();
    if (!button) return false;
    const needed = button.classList.contains('wc-variation-selection-needed');
    const unavailable = button.classList.contains('wc-variation-is-unavailable');
    if (!needed && !unavailable) return false;
    // The flow asks the variation itself: back to that step, which says what is missing.
    if (this.variantPanel?.active) {
      if (this.mode === 'wizard') {
        if (!this.wizardOpen) this.openWizard();
        this.showStep(0);
      } else {
        this.variantPanel.element.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      // The popup's footer says it once; inline, the words sit under the tiles.
      const said = this.variantPanel.validate(this.mode === 'wizard');
      if (said && this.mode === 'wizard') this.showFootError(said, []);
      else if (!said) this.showError(TEXTS.variationUnavailable);
      return true;
    }
    const form = button.closest('form');
    const label = (form?.querySelector('table.variations th label, .variations label')?.textContent ?? '').replace(/[:*]/g, '').trim();
    const message = unavailable
      ? TEXTS.variationUnavailable
      : TEXTS.variationNeeded(label);
    if (this.wizardOpen) this.closeWizard();
    const picker = form?.querySelector<HTMLElement>('table.variations, .variations');
    picker?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    this.showError(message);
    return true;
  }

  /** Brings "Završi" into view: the popup's last step, or the inline section. */
  private showFinish(): void {
    if (this.mode === 'wizard') {
      if (!this.wizardOpen) this.openWizard();
      this.showStep(this.wizardSteps().length - 1);
      return;
    }
    this.finishPanel?.element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  /** The flow's add buttons say they are working, and cannot be pressed twice. */
  private setFinishBusy(busy: boolean): void {
    // Released wherever they are now: the popup may have moved on to another
    // step (a run of pieces goes back to step 1) while the add was running.
    const buttons = busy
      ? [
          ...(this.overlay ? [...this.overlay.querySelectorAll<HTMLButtonElement>('.pz-modal-next.is-cart')] : []),
          ...this.root.querySelectorAll<HTMLButtonElement>('.pz-finish-add, .pz-face-add'),
        ]
      : [
          ...(this.overlay ? [...this.overlay.querySelectorAll<HTMLButtonElement>('.pz-modal-next.is-busy')] : []),
          ...this.root.querySelectorAll<HTMLButtonElement>('.pz-finish-add, .pz-face-add, .is-busy'),
        ];
    for (const button of buttons) {
      button.disabled = busy;
      button.classList.toggle('is-busy', busy);
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
  }

  /** Where the add goes without leaving the page, or undefined for the classic form. */
  private ajaxCart(): CartAddConfig | undefined {
    const config = this.config.cartAdd;
    if (!config || !config.url || this.previewing || this.config.externalOrderToken || this.config.orderEdit) return undefined;
    return config;
  }

  /**
   * The form's own fields go to the connector's AJAX add instead of a page
   * POST. Added: the header cart is refreshed and "Dodato u korpu" opens (or
   * the cart, when the shop prefers it, and always after an edit). Refused:
   * the shop's words, the design kept. No answer at all: the classic form,
   * whose `pz_batch` keeps it from adding a second copy.
   */
  /**
   * WooCommerce reads a simple product's id from its button's own name/value
   * pair, which neither `FormData` nor `form.submit()` carries (variable
   * products have a hidden `add-to-cart` input): kept as a hidden input.
   */
  private carryProductId(form: HTMLFormElement, button: HTMLButtonElement | null): void {
    if (form.querySelector('input[name="add-to-cart"], select[name="add-to-cart"]')) return;
    const value = (button?.name === 'add-to-cart' && button.value) || (this.config.productId ? String(this.config.productId) : '');
    if (value) setHidden(form, 'add-to-cart', value);
  }

  private async addByAjax(form: HTMLFormElement, button: HTMLButtonElement, cartAdd: CartAddConfig): Promise<void> {
    const signature = this.designSignature();
    const batch = this.retryBatch?.signature === signature ? this.retryBatch.batch : newBatchId();
    this.retryBatch = undefined;
    setHidden(form, 'pz_batch', batch);
    const slow = window.setTimeout(() => this.showInfo(TEXTS.adding), 700);
    const outcome = await addToCart(cartAdd, [formPairs(form)], batch);
    window.clearTimeout(slow);
    if (outcome.kind === 'unreachable') {
      // The classic form takes it (the batch id keeps it single). A run of
      // different pieces counts this one: the page that comes back must not
      // ask for it again.
      this.clearDraft();
      const run = this.pieceRun;
      if (run && !this.editingCart) {
        run.done += 1;
        if (run.done < run.total) writePieceRun(this.config.productId, run);
        else this.stopPieces();
      }
      form.submit();
      return;
    }
    if (outcome.kind === 'offline') {
      // Nothing reached the shop, or nothing could be asked: no classic form
      // into a dead connection (an error page, and a design lost on Back).
      this.retryBatch = { batch, signature };
    } else if (outcome.kind === 'added') {
      // A run with pieces still to make keeps the draft (it is saved again below).
      const run = this.pieceRun;
      const continues = !!run && !this.editingCart && !outcome.body.replaced && run.done + 1 < run.total;
      if (!continues) this.clearDraft();
    }
    // Used, refused or not sent, the next add commits its own design.
    this.committedId = undefined;
    for (const name of PERSONALIZATION_INPUTS) this.writeHiddenInput(name, '');
    form.querySelector('input[name="personalizy_replace_cart_key"]')?.remove();
    this.clearStatus();
    button.classList.remove('loading');
    if (outcome.kind === 'refused' || outcome.kind === 'offline') {
      this.disableCart(false);
      this.showError(outcome.kind === 'offline' ? TEXTS.offline : outcome.messages.join(' '));
      return;
    }
    const body = outcome.body;
    announceAdded(body, button);
    // A run of different pieces: the next one starts from this design, in the open popup.
    if (this.pieceRun && !this.editingCart && !body.replaced) {
      const run = this.pieceRun;
      run.done += 1;
      if (run.done < run.total) {
        writePieceRun(this.config.productId, run);
        // The next piece starts from these answers: kept as the draft again, so
        // a reload in the middle of the run loses nothing.
        this.saveDraftNow();
        this.finishPanel?.setRun(run);
        this.disableCart(false);
        this.updateTotals();
        this.renderPieceBanner();
        this.syncFinishLabel();
        this.toast(TEXTS.pieceAdded(run.done, run.total), 'success', 8000);
        if (this.mode === 'wizard') {
          if (!this.wizardOpen) this.openWizard();
          this.showStep(0);
        } else {
          this.host.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
        return;
      }
      this.stopPieces();
    }
    if (body.replaced || this.editingCart || cartAdd.after === 'cart') {
      // A Back from the cart may bring this very page back from memory: its button must work.
      this.disableCart(false);
      window.location.assign(body.cartUrl || cartAdd.cartUrl);
      return;
    }
    this.disableCart(false);
    if (this.wizardOpen) this.closeWizard();
    let picture: string | undefined;
    try {
      picture = await this.addedPicture();
    } catch {
      picture = undefined;
    }
    showAddedSheet({
      host: this.root,
      body,
      ...(picture ? { picture } : {}),
      format: (amount) => this.formatPrice(amount),
      cartUrl: cartAdd.cartUrl || body.cartUrl,
      onAgain: () => this.personalizeAgain(),
    });
  }

  /** A small picture of the design just added, for "Dodato u korpu". */
  private async addedPicture(): Promise<string | undefined> {
    const product = this.product;
    const side = this.previewSide();
    if (!product || !side || !this.preview || !this.form) return undefined;
    const scene = applyValues(side, product.bindings, this.form.current, this.valueLabels(),
      product.libraries ?? {}, uploadTargetsOf(product.options));
    return thumbnailOf(await this.preview.snapshot(scene), 160);
  }

  /** The run of different pieces under way (Završi → "Svaki komad drugačiji"). */
  private pieceRun: PieceRun | undefined;

  /** "Svaki komad drugačiji" with N pieces: N single adds, starting with this design. */
  private startPieces(): void {
    if (this.pieceRun || this.editingCart || !this.finishPanel?.pieceMode()) return;
    const total = Math.min(50, readQuantity());
    if (total < 2) return;
    this.pieceRun = { total, done: 0 };
    writePieceRun(this.config.productId, this.pieceRun);
    setQuantityInput(this.cartButton()?.closest('form') ?? null, 1);
    this.finishPanel.setRun(this.pieceRun);
    this.renderPieceBanner();
    this.syncFinishLabel();
  }

  /** The run ends (all added, or "Prekini"): back to one design for any quantity. */
  private stopPieces(): void {
    this.pieceRun = undefined;
    writePieceRun(this.config.productId, undefined);
    this.finishPanel?.setRun(undefined);
    this.renderPieceBanner();
    if (this.mode === 'wizard') this.renderLaunchArea();
    this.syncFinishLabel();
    this.updateTotals();
  }

  /** "Komad 2 od 3" over the steps (popup) and over the form (inline) while a run is on. */
  private renderPieceBanner(): void {
    const run = this.pieceRun;
    const text = run ? `${FINISH_TEXTS.piece(run.done + 1, run.total)} · ${TEXTS.pieceHint}` : '';
    for (const banner of this.root.querySelectorAll<HTMLElement>('.pz-piece-banner')) {
      banner.textContent = text;
      banner.hidden = !run;
    }
  }

  /** What the flow's add button says: "Dodaj u korpu", "Sačuvaj izmene" or "Dodaj komad 2 od 3". */
  private finishLabel(): string {
    const run = this.pieceRun;
    const planned = !run && this.finishPanel?.pieceMode() ? readQuantity() : 0;
    return run
      ? TEXTS.addPiece(run.done + 1, run.total)
      : planned > 1
        ? TEXTS.addPiece(1, planned)
        : this.editingCart
          ? TEXTS.saveEdit
          : TEXTS.addToCart;
  }

  /** Every add button of the flow says the same thing. */
  private syncFinishLabel(): void {
    const label = this.finishLabel();
    const next = this.overlay?.querySelector<HTMLElement>('.pz-modal-next.is-cart .pz-btn-text');
    if (next) next.textContent = label;
    for (const button of this.root.querySelectorAll<HTMLElement>('.pz-finish-add, .pz-face-add')) button.textContent = label;
  }

  /** "Personalizuj još jedan": the same answers to start from, back at the first step. */
  private personalizeAgain(): void {
    if (this.mode === 'wizard') {
      this.openWizard();
      return;
    }
    this.host.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  /** A form the shop made optional that the shopper never touched: nothing to commit. */
  private untouchedOptional(): boolean {
    if (!this.product?.display?.optional || this.previewing) return false;
    const touched = this.form?.visibleOptions().some((o) => this.form!.isAnswered(o.id) || this.form!.needsAnswer(o) && !o.required);
    return !touched && this.visibleAudios().length === 0;
  }

  /** The answers as the order records them — what "changed while saving" compares. */
  private answerSignature(): string {
    return JSON.stringify([this.product!.templateId, this.form!.current, this.form!.currentVariation, this.decorateAudios()]);
  }

  /**
   * Everything that makes the saved order what it is: the answers, the proof
   * tick (it is stored on the order) and the pictures' pixels (a cut-out that
   * lands changes the cart picture without changing an answer).
   */
  private designSignature(): string {
    return JSON.stringify([
      this.answerSignature(),
      this.proofWanted() && this.proofInput?.checked === true,
      this.preview?.pictureVersion ?? 0,
    ]);
  }

  /** The design to save ahead of the click, or undefined while it is not finished. */
  private designToSave(): CartPrepDesign<CommitOutcome> | undefined {
    const product = this.product;
    const form = this.form;
    if (!product || !form || this.studioHosted || this.committing || this.committedId) return undefined;
    if (this.photoWork.size > 0 || form.collageSending() || form.missingRequired().length > 0) return undefined;
    // Still at it in the lightbox, the wizard or the collage board: every drag
    // would be a save (and on the board, a cart picture drawn beside the
    // board's own render). Closing any of them saves at once (their onClose
    // schedules it; the board's in `collage.onBoard`).
    if (this.lightboxLayer.isOpen || this.wizardOpen || this.previewPaused) return undefined;
    // Only a design the shopper made: a product whose questions are all
    // optional is "finished" the moment the page opens, and a visit that never
    // touches it must not leave a saved design (and a cart picture) behind.
    const made = form.visibleOptions().some((o) => form.hasShopperAnswer(o.id)) || this.visibleAudios().some(hasSound);
    if (!made) return undefined;
    // An edit from the cart opens on a design that is already bought: nothing
    // is saved again until the shopper changes something.
    if (this.editingCart && (this.editBaseline === undefined || this.editSignature() === this.editBaseline)) return undefined;
    const signature = this.designSignature();
    return { signature, run: () => this.saveAhead(signature) };
  }

  /**
   * Commits the finished design ahead of the click, held: the server renders
   * nothing and downloads no sound until it goes into a cart. The cart picture
   * comes first — a design saved without one would reach the cart as the
   * plain product photo — and a design that changed meanwhile is not saved.
   */
  private async saveAhead(signature: string): Promise<CommitOutcome> {
    const values = structuredClone(this.form!.current);
    const variation = structuredClone(this.form!.currentVariation);
    const audios = this.decorateAudios();
    const cartPreviewAssetId = await this.cartPicture(values, variation);
    if (this.designSignature() !== signature) throw new Error('changed while saving');
    const supersedes = this.heldId;
    // The server deletes it with this save; the click must not take it meanwhile.
    if (supersedes) this.cartPrep.forget(supersedes);
    const result = await this.api.commit({
      ...this.commitBody(values, variation, audios, cartPreviewAssetId, newCommitKey()),
      hold: true,
      ...(supersedes ? { supersedes } : {}),
    });
    this.heldId = result.id;
    return result;
  }

  /**
   * The cart picture of these answers, drawn and uploaded once per design:
   * the flat preview as the order will print, never with a photo missing
   * (`Preview.snapshot` fails instead). Undefined where no flat preview exists.
   */
  private async cartPicture(
    values: Values,
    variation: { id: number | null; attributes: Record<string, string> },
  ): Promise<string | undefined> {
    // The product keeps its own photo in the cart: nothing to draw or send.
    if (this.product?.display?.cartPicture === 'product') return undefined;
    const side = this.previewSide(false);
    if (this.previewing || !side || !this.preview) return undefined;
    const key = JSON.stringify([this.product!.templateId, values, variation, this.preview.pictureVersion]);
    if (this.cartPreviewUpload?.signature === key) return this.cartPreviewUpload.id;
    const scene = applyValues(side, this.product!.bindings, values, this.valueLabels(),
      this.product!.libraries ?? {}, uploadTargetsOf(this.product!.options));
    await this.exactRenderer(8000);
    const blob = await this.preview.snapshot(scene);
    const name = blob.type === 'image/jpeg' ? 'personalizy-cart-preview.jpg' : 'personalizy-cart-preview.png';
    const uploaded = await this.api.upload(blob, name, 'image');
    this.cartPreviewUpload = { signature: key, id: uploaded.id };
    return uploaded.id;
  }

  private commitBody(
    values: Values,
    variation: { id: number | null; attributes: Record<string, string> },
    audios: AudioSelection[],
    cartPreviewAssetId: string | undefined,
    idempotencyKey: string,
  ): Parameters<Api['commit']>[0] {
    return {
      cartPreviewAssetId,
      releaseId: this.product!.releaseId,
      // A preview has no product row, and 'preview' is not an id — the
      // template and option set carry the commit instead.
      productId: this.previewing ? undefined : this.product!.productId,
      source: this.previewing ? 'studio' : undefined,
      templateId: this.product!.templateId,
      optionSetId: this.product!.optionSetId,
      optionSetIds: this.product!.optionSetIds,
      values,
      audios,
      variation,
      idempotencyKey,
      ...(this.proofWanted() && this.proofInput?.checked ? { proofApproved: true } : {}),
    };
  }

  /**
   * Tells the embedding page what the shopper has done.
   *
   * Studio's preview shows the answers, the price and the missing fields beside
   * the widget, and it reads them from here rather than from its own copy of the
   * form — a second reading of the same state is a second thing to keep in step.
   */
  private reportPreviewState(): void {
    const product = this.product;
    if (!product || !this.form) return;
    const values = this.form.current;
    const answers = product.options
      .map((option) => {
        const value = values[option.id];
        if (value === undefined || value === null || value === '') return null;
        // A collage reads as its count of printed photos, "24 slike".
        if (option.type === PHOTO_COLLAGE_OPTION_TYPE) {
          const text = this.form!.collageSummary(option.id);
          return text ? { label: option.label, type: option.type, text } : null;
        }
        const text = Array.isArray(value)
          ? // Several picked choices read as their names; a fan-out upload's
            // list of asset ids reads as a count, because ids say nothing.
            option.type === 'image_upload' || option.type === 'vector_upload'
            ? `${value.filter((one) => one !== null && one !== undefined && one !== '').length} slika`
            : value
                .map((one) => option.values.find((v) => v.value === String(one))?.label ?? String(one))
                .join(', ')
          : (option.values.find((v) => v.value === String(value))?.label ?? describeAnswer(value));
        return { label: option.label, type: option.type, text };
      })
      .filter((a): a is { label: string; type: string; text: string } => a !== null);

    this.post({
      type: 'pz:state',
      values,
      answers,
      missing: this.form.missingRequired(),
      priceDelta: this.form.priceTotal(),
      audios: this.visibleAudios().filter(hasSound).length,
      ...(this.orderEditing
        ? { order: { revision: this.orderRevision, dirty: this.orderDirty(), busy: this.orderBusy() } }
        : {}),
    });
  }

  /**
   * Staff must wait before a revision is saved: a photo still being cut out,
   * or collage photos still on their way ("Nova slika", "Dodaj slike" in the
   * editor) — saved now, the revision would go without them.
   */
  private orderBusy(): boolean {
    return this.photoWork.size > 0 || (this.form?.collageSending() ?? false);
  }

  /**
   * The order's answers and sounds as they are now — what a revision saves.
   * Only what a person chooses counts: a length the form measures once a
   * restored sound has loaded is not a change. Nor is an older order's
   * "fade 0" or "100 %" coming back unset (`chosenEffects`): the same sound.
   */
  private orderSignature(): string {
    const sounds = this.decorateAudios().map((a) => [
      a.kind, a.slot ?? '', a.url ?? '', a.storageKey ?? '', a.title ?? '', a.artist ?? '',
      a.fromMs ?? null, a.toMs ?? null, a.fadeInMs || null, a.fadeOutMs || null,
      a.volumePercent === 100 ? null : (a.volumePercent ?? null), a.gapMs ?? null,
    ]);
    return JSON.stringify([this.form?.current ?? {}, sounds]);
  }

  /** Whether staff changed anything since the order opened. */
  private orderDirty(): boolean {
    if (!this.form) return false;
    // The first reading after the restore settles is the baseline.
    this.orderBaseline ??= this.orderSignature();
    return this.orderSignature() !== this.orderBaseline;
  }

  /**
   * Studio's order editor asks for the answers (`pz:order-collect`) and saves
   * them itself as the order's next revision. The widget answers with the same
   * values and sounds a checkout would commit, plus what Studio needs to decide
   * how to save: required answers still empty, photos still being processed.
   * Same origin only — Studio serves this frame itself.
   */
  private listenForOrderSave(): void {
    // Settles the "untouched" baseline once the restored sounds have arrived.
    window.setTimeout(() => {
      this.orderBaseline = undefined;
      this.orderDirty();
      this.reportPreviewState();
    }, 1200);
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== window.parent) return;
      const data = event.data as { type?: string; requestId?: string } | null;
      if (!data || data.type !== 'pz:order-collect') return;
      const form = this.form;
      if (!form) return;
      this.post({
        type: 'pz:order-state',
        requestId: data.requestId,
        revision: this.orderRevision,
        busy: this.orderBusy(),
        missing: form.missingRequired(),
        dirty: this.orderDirty(),
        values: structuredClone(form.current),
        audios: this.decorateAudios(),
      });
    });
    // Studio shows its own save bar; it hears at once that the form is ready.
    this.post({ type: 'pz:order-ready', revision: this.orderRevision });
  }

  /** Posts to the embedder. Same origin only — Studio serves the frame itself. */
  private post(message: Record<string, unknown>): void {
    if (window.parent === window) return;
    try {
      window.parent.postMessage(message, window.location.origin);
    } catch {
      // An embedder that cannot receive is not a reason to break the preview.
    }
  }

  private writeHiddenInput(name: string, value: string): void {
    const input = document.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    if (input) input.value = value;
  }

  /**
   * The song title and artist the shopper typed also name the player page.
   *
   * The audio selection itself only knows a filename or a link; the human names
   * live in whichever text options are labelled like a title and an artist. A
   * heuristic, but the labels in this catalogue are stable and a wrong guess
   * still beats a player page titled "melodija.wav".
   */
  private decorateAudios(): AudioSelection[] {
    const audios = this.visibleAudios();
    if (audios.length === 0) return [];
    const values = this.form?.current ?? {};
    // Never a Spotify song's printed lines: they name the Spotify song, and a
    // video the shopper picked in the sound step is often another song. Named
    // after them, the order (and the next "Izmeni") showed the old song's name.
    const textOf = (pattern: RegExp): string | undefined => {
      const option = this.product?.options.find(
        (o) => (o.type === 'text' || o.type === 'textarea') && pattern.test(o.label) && !this.form?.isSongLine(o.id),
      );
      const value = option ? values[option.id] : undefined;
      return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
    };
    const title = textOf(/pesm|naslov|title|song/i);
    const artist = textOf(/izvo[dđ]|artist|bend|band/i);
    // Only a linked song benefits from the typed title/artist; a recording or an
    // AI voice already carries a truthful name. A song given only by name keeps
    // that name: it is what production (or the worker) searches for. So does the
    // Spotify song ("Tvoja pesma"): its name is Spotify's, and a line retyped
    // for the print ("Naša pesma") is not what the song is called.
    return audios.map((audio, index) =>
      index === 0 && audio.kind === 'youtube' && audio.url && !audio.spotifyUri
        ? { ...audio, title: title ?? audio.title, artist: artist ?? audio.artist }
        : audio,
    );
  }

  /** Human-readable choices, shown on the cart and the order. */
  private describeChoices(): Record<string, string> {
    const out: Record<string, string> = {};
    const values = this.form?.current ?? {};
    for (const option of this.product?.options ?? []) {
      const value = values[option.id];
      if (value === undefined || value === null || value === '') continue;
      // The variation's own question ("Boja kutije"): the cart line already names the variation.
      if (fromAttributeOf(option)) continue;
      // A collage: how many photos print ("24 slike") — the filled ACTIVE
      // slots; spares and the fills of a hidden layout do not print.
      if (option.type === PHOTO_COLLAGE_OPTION_TYPE) {
        const text = this.form?.collageSummary(option.id);
        if (text) out[option.label] = text;
        continue;
      }
      // Uploads and ids mean nothing to a customer reading their order.
      if (option.type === 'image_upload' || option.type === 'vector_upload') {
        // A "Multiple Upload Image" answer is a list aligned to the placeholders
        // it feeds; how many pictures arrived is the part a customer can check.
        out[option.label] = Array.isArray(value)
          ? `Otpremljeno (${value.filter((one) => one !== null && one !== undefined && one !== '').length})`
          : 'Otpremljeno';
        continue;
      }
      // A design the shopper made themselves: the texts verbatim (they are what
      // gets printed) and the rest counted — the same sentence the order sheet,
      // the mail and the connector's panel show, from the same function.
      if (isDesignAreaAnswer(value)) {
        const described = describeDesign(value);
        if (described) out[option.label] = described;
        continue;
      }
      // A checkbox reads as the answer to its own question, never as "true".
      if (option.type === 'checkbox') {
        out[option.label] = value === true ? CHOICE_TEXTS.yes : CHOICE_TEXTS.no;
        continue;
      }
      // A picked colour reads as its hex, upper-cased the way a designer writes
      // it — there is no name to show, and "#4337af" is what the print carries.
      if (option.type === 'color' && typeof value === 'string') {
        out[option.label] = value.toUpperCase();
        continue;
      }
      // Sound is reported once, under the 'Zvuk' entry below — repeating it under
      // the option's own label would list the same file twice on the order.
      if (['audio_source', 'voice_record', 'video_source'].includes(option.type)) continue;
      // Pictures picked from a collection, and pictures painted from words:
      // their ids mean nothing on an order; how many, or what it is, does.
      if (option.type === 'clipart_multi') {
        const count = (Array.isArray(value) ? value : [value]).filter((one) => one !== null && one !== undefined && one !== '').length;
        out[option.label] = `${count} ${plural(count, 'sličica', 'sličice', 'sličica')}`;
        continue;
      }
      if (option.type === 'ai_image') {
        out[option.label] = 'AI slika';
        continue;
      }
      // Several picked choices read as a list of their names.
      if (Array.isArray(value)) {
        out[option.label] = value
          .map((one) => option.values.find((v) => v.value === String(one))?.label ?? String(one))
          .join(', ');
        continue;
      }
      // A picked place reads as its name, not as coordinates.
      if (value && typeof value === 'object') {
        const labeled = value as { label?: unknown };
        if (typeof labeled.label === 'string') out[option.label] = labeled.label;
        continue;
      }
      const choice = option.values.find((v) => v.value === String(value));
      out[option.label] = choice
        ? choice.label
        : option.type === 'date'
          ? formatDate(String(value), 'dd.MM.yyyy')
          : String(value);
    }
    // Only sounds that actually exist get reported — opening a card without
    // recording or pasting anything is not a choice.
    const audioLabels = this.visibleAudios()
      .filter(hasSound)
      .map((a) => {
        switch (a.kind) {
          case 'youtube': {
            const part =
              a.fromMs != null || a.toMs != null
                ? ` (${clock(a.fromMs ?? 0)}–${a.toMs != null ? clock(a.toMs) : 'kraj'})`
                : '';
            // The song, by name — never the link: "YouTube: Ne lomite mi
            // bagrenje — Đorđe Balašević (0:45–1:30)".
            return `${youtubeName(a)}${part}`;
          }
          case 'tiktok':
            return a.title ?? 'TikTok zvuk';
          case 'voice':
            return 'Glasovna poruka';
          case 'tts':
            return a.title ?? 'AI glasovna';
          case 'song':
            return a.title ?? 'AI pesma';
          default:
            return a.title ?? 'Otpremljen zvuk';
        }
      });
    if (audioLabels.length === 1) out['Zvuk'] = audioLabels[0]!;
    else for (const [i, label] of audioLabels.entries()) out[`Zvuk ${i + 1}`] = label;
    return out;
  }
}

/**
 * One answer, in words, for the studio's preview panel.
 *
 * The compound answers are the ones worth spelling out: a photo now carries the
 * shopper's framing, the look they picked and whether they kept their own
 * background, and a song carries its title and artist. Printing the raw object
 * would show the person testing a template a blob of JSON at exactly the moment
 * they are trying to check that their choices took effect.
 */
function describeAnswer(value: unknown): string {
  if (value === null || value === undefined) return '';
  // A ticked box, in words. `String(true)` put the literal "true" on the cart
  // line of every product with a checkbox question.
  if (typeof value === 'boolean') return value ? CHOICE_TEXTS.yes : CHOICE_TEXTS.no;
  // A colour, as a designer writes one.
  if (typeof value === 'string' && /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(value)) {
    return value.toUpperCase();
  }
  if (typeof value !== 'object') return String(value);
  // A design area, in the same words the cart line and the order sheet use.
  if (isDesignAreaAnswer(value)) return describeDesign(value);

  const record = value as Record<string, unknown>;
  if (typeof record.label === 'string') return record.label;

  // A picked song. Answers saved before the picker wrote its own label land
  // here, and they must read exactly as a fresh pick does — the cart line, the
  // summary and the field the shopper approved are all the same sentence.
  if (typeof record.uri === 'string' && record.uri.startsWith('spotify:')) {
    const artists = Array.isArray(record.artists)
      ? record.artists.join(', ')
      : typeof record.artists === 'string'
        ? record.artists
        : '';
    return songLabel(typeof record.name === 'string' ? record.name : '', artists) || String(record.uri);
  }

  // A place on a map. The picker always writes a label — the place the shopper
  // searched for, which is what the cart line and the order have to read — so
  // this covers only answers saved before it did.
  if (Number.isFinite(Number(record.lat)) && Number.isFinite(Number(record.lng))) {
    return `${Number(record.lat).toFixed(4)}, ${Number(record.lng).toFixed(4)}`;
  }

  // A photo, and what was done to it.
  if (typeof record.assetId === 'string') {
    const notes: string[] = ['Otpremljeno'];
    if (record.crop) notes.push('uokvireno');
    if (typeof record.filter === 'string' && record.filter !== 'none') {
      notes.push(`filter: ${record.filter}`);
    }
    if (record.keepOriginal) notes.push('bez uklanjanja pozadine');
    return notes.join(' · ');
  }

  return JSON.stringify(value);
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** localStorage, or nothing where the browser blocks it. */
function safeStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

/** A small JPEG data URL of a picture, for a list thumbnail. */
async function thumbnailOf(blob: Blob, size: number): Promise<string | undefined> {
  if (typeof createImageBitmap !== 'function') return undefined;
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, size / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const paper = canvas.getContext('2d');
  if (!paper) return undefined;
  paper.fillStyle = '#ffffff';
  paper.fillRect(0, 0, canvas.width, canvas.height);
  paper.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  const url = canvas.toDataURL('image/jpeg', 0.72);
  return url.length <= LIBRARY_MAX_THUMB ? url : undefined;
}

/**
 * The variation WooCommerce's own form already shows, read from its inputs —
 * `attribute_pa_boja=crvena` selects and the hidden `variation_id`.
 */
function readPageVariation(): { id: number | null; attributes: Record<string, string> } {
  const attributes: Record<string, string> = {};
  const form = document.querySelector<HTMLFormElement>('form.variations_form');
  if (!form) return { id: null, attributes };
  for (const select of form.querySelectorAll<HTMLSelectElement>('select[name^="attribute_"]')) {
    if (select.value) attributes[select.name.replace(/^attribute_/, '')] = select.value;
  }
  const idField = form.querySelector<HTMLInputElement>('input[name="variation_id"]');
  const id = idField && Number(idField.value) > 0 ? Number(idField.value) : null;
  return { id, attributes };
}

/**
 * How long a page is open before the print renderer is fetched anyway, in
 * the browser's idle time, for a shopper who reads before touching the form.
 */
const RENDERER_IDLE_DELAY_MS = 6000;

/** A phone reporting 2 GB of memory or less keeps the light 2D preview. */
function lowMemoryDevice(): boolean {
  const deviceMemory = (navigator as { deviceMemory?: number }).deviceMemory;
  return Boolean(deviceMemory && deviceMemory <= 2);
}

/**
 * Where the renderer script lives: next to this one, unless the page says otherwise.
 * Deriving it from our own URL means one script tag configures both.
 */
function rendererUrl(): string {
  const override = (window as unknown as { personalizyRendererUrl?: string })
    .personalizyRendererUrl;
  if (override) return override;
  const own =
    document.currentScript instanceof HTMLScriptElement
      ? document.currentScript.src
      : (document.querySelector<HTMLScriptElement>('script[src*="personalizy.js"]')?.src ?? '');
  // The page's cache-busting query rides along: without it a phone kept the
  // renderer it cached before a release, next to the new widget.
  return own ? own.replace(/personalizy\.js(\?[^#]*)?(#.*)?$/, (_m, query?: string) => `personalizy-render.js${query ?? ''}`) : '';
}

/**
 * Where the widget goes when the connector's own mount is not on the page.
 *
 * Blockified "Add to Cart with Options", quick-view popups and custom product
 * templates all drop the hook the connector renders into. Rather than showing
 * nothing at all — a product that silently cannot be personalized — the host is
 * created just above the add-to-cart button, inside the cart form so the hidden
 * inputs still travel with the POST.
 */
function fallbackMount(config: WidgetConfig): HTMLElement | null {
  const button = document.querySelector<HTMLElement>(config.cartButton);
  const form = button?.closest('form') ?? document.querySelector<HTMLFormElement>('form.cart');
  if (!form && !button) return null;
  const host = document.createElement('div');
  host.id = 'personalizy-options';
  host.className = 'pz-fallback-mount';
  if (button && button.parentElement) button.parentElement.insertBefore(host, button);
  else form!.insertBefore(host, form!.firstChild);
  // The connector normally renders these; without them the commit has nowhere
  // to write, and WooCommerce would receive a plain line.
  if (form) {
    for (const name of [
      'personalizy_personalization_id',
      'personalizy_price_delta',
      'personalizy_properties',
      'personalizy_extra_products',
    ]) {
      if (form.querySelector(`input[name="${name}"]`)) continue;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = '';
      form.appendChild(input);
    }
  }
  return host;
}

function boot(): void {
  const config = (window as unknown as { personalizyConfig?: WidgetConfig }).personalizyConfig;
  if (!config) return;
  // Once per page: the connector re-adds this script when the first copy
  // failed to arrive, and a slow first copy may still turn up afterwards.
  const flags = window as unknown as { personalizyBooted?: boolean };
  if (flags.personalizyBooted) return;
  const host = document.querySelector<HTMLElement>(config.mount) ?? fallbackMount(config);
  if (!host) return;
  flags.personalizyBooted = true;
  void new Widget(config, host).start();
}

// The connector's loader reads this to know the script arrived and ran.
(window as unknown as { personalizyLoaded?: boolean }).personalizyLoaded = true;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

export { Widget };

/**
 * Head placement's decisions, in the console — only when a shop owner (or
 * support) sets `localStorage.pzDebug = '1'`. Returns undefined so an early
 * return can log why it gave up.
 */
function headDebug(...args: unknown[]): undefined {
  try {
    if (window.localStorage.getItem('pzDebug') === '1') console.info('[personalizy head]', ...args);
  } catch {
    // Storage blocked: stay silent.
  }
  return undefined;
}

/** Distance between a head's face (or silhouette) centre and a picked face's centre. */
function dist(head: HeadMeasure, face: { x: number; y: number; w: number; h: number }): number {
  const box = head.face ?? head.box;
  return Math.hypot(box.x + box.w / 2 - (face.x + face.w / 2), box.y + box.h / 2 - (face.y + face.h / 2));
}
